import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  IconButton,
  Card,
  CardContent,
  Container,
  CssBaseline,
  Divider,
  Grid,
  Stack,
  Tab,
  Tabs,
  TextField,
  InputAdornment,
  ThemeProvider,
  Typography,
  useMediaQuery,
  createTheme,
} from '@mui/material';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardReturnRoundedIcon from '@mui/icons-material/KeyboardReturnRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import MicRoundedIcon from '@mui/icons-material/MicRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { Element, scroller } from 'react-scroll';
import './App.css';

const BACKGROUND_IMAGE =
  'https://www.theknot.com/tk-media/images/0b322954-be65-4692-9b00-b007aad49f61~rt_auto-cr_30.0.3014.3730-rs_2048.h?ordering=explicit&quality=90';

const NAV_ITEMS = [
  { label: 'Home', path: '/home' },
  { label: 'Story', path: '/story' },
  { label: 'Travel + Stay', path: '/travel' },
  { label: 'FAQ', path: '/faq' },
  { label: 'Gifts?', path: '/gifts-causes' },
  { label: 'Games!', path: '/game' },
  { label: 'RSVP', path: '/rsvp' },
];

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || 'https://o8akxcs2ga.execute-api.us-east-1.amazonaws.com/prod').replace(/\/$/, '');
const PHOTO_TRIVIA_PLAYER_KEY = 'ukUsPhotoTriviaPlayerName';
const PHOTO_TRIVIA_SWIPE_THRESHOLD = 90;
const PHOTO_TRIVIA_MAX_SCORE = 1000;
const USD_TO_GBP_RATE = 0.72;
const PHOTO_PUZZLE_GRID_SIZE = 3;
const PHOTO_PUZZLE_MAX_SCORE = 1000;
const PHOTO_PUZZLE_POINTS_PER_SECOND = 5;
const SITE_PASSWORD_HASH = 'a556ee67144668301607c5f7313bf0bb8c7906ccdd4f9e63884fd4089e8c9336';
const RESPONSES_ADMIN_PASSWORD = 'rachel1234';
const RESPONSES_ADMIN_ACCESS_KEY = 'responsesAdminAccess';
const PHOTO_PUZZLE_IMAGE_SRC = `${process.env.PUBLIC_URL}/puzzle/puzzle_reduced.jpg`;
const PHOTO_PUZZLE_SOLVED_REEL_IMAGES = [
  `${process.env.PUBLIC_URL}/puzzle/solved_puzzle_0_reduced.jpg`,
  `${process.env.PUBLIC_URL}/puzzle/solved_puzzle_3_reduced.jpg`,
  `${process.env.PUBLIC_URL}/puzzle/solved_puzzle_4_reduced.jpg`,
  `${process.env.PUBLIC_URL}/puzzle/solved_puzzle_2_reduced.jpg`,
  `${process.env.PUBLIC_URL}/puzzle/solved_puzzle_1_reduced.jpg`,
];
const RSVP_EVENT_KEYS = ['friday', 'saturday', 'sunday'];
const DEFAULT_EVENT_INVITES = Object.freeze({
  friday: true,
  saturday: true,
  sunday: true,
});
const RSVP_EVENTS = [
  { key: 'friday', label: 'Friday Welcome Drinks', meta: '(Aug 7 • 6:30 PM–10:00 PM)' },
  { key: 'saturday', label: 'Wedding Day', meta: '(Aug 8 • 2:00 PM–late)' },
  { key: 'sunday', label: 'Sunday Recovery Breakfast', meta: '(Aug 9 • 11:00 AM–1:00 PM)' },
];

async function sha256Hex(value) {
  if (!window.crypto?.subtle) {
    throw new Error('Web Crypto unavailable');
  }
  const data = new TextEncoder().encode(value);
  const digest = await window.crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

async function apiFetch(path, options = {}) {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    headers: {
      'content-type': 'application/json',
      ...(options.headers || {}),
    },
    ...options,
  });

  const bodyText = await response.text();
  const body = bodyText ? JSON.parse(bodyText) : {};
  if (!response.ok) {
    const error = new Error(body?.message || `API request failed (${response.status})`);
    error.status = response.status;
    error.body = body;
    throw error;
  }
  return body;
}

function normalizeGuestName(guest) {
  if (typeof guest === 'string') return guest.trim();
  if (guest && typeof guest === 'object') {
    return (guest.fullName || guest.name || '').trim();
  }
  return '';
}

function slugify(value) {
  return (value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function normalizeEventInvites(eventInvites) {
  const invites = { ...DEFAULT_EVENT_INVITES };
  if (!eventInvites || typeof eventInvites !== 'object') return invites;
  RSVP_EVENT_KEYS.forEach((key) => {
    if (typeof eventInvites[key] === 'boolean') {
      invites[key] = eventInvites[key];
    }
  });
  return invites;
}

function normalizeGuestGroup(group) {
  const baseGuests = (group?.guests || []).map(normalizeGuestName).filter(Boolean);
  const guestDetailByName = new Map();

  (group?.guests || []).forEach((guest) => {
    if (!guest || typeof guest !== 'object') return;
    const fullName = normalizeGuestName(guest);
    if (!fullName) return;
    guestDetailByName.set(fullName, {
      guestId: `${guest?.guestId || slugify(fullName)}`.trim() || slugify(fullName),
      fullName,
      isChild: guest?.isChild === true,
    });
  });

  (group?.guestDetails || []).forEach((guest) => {
    if (!guest || typeof guest !== 'object') return;
    const fullName = normalizeGuestName(guest);
    if (!fullName) return;
    const existing = guestDetailByName.get(fullName);
    guestDetailByName.set(fullName, {
      guestId: `${guest?.guestId || existing?.guestId || slugify(fullName)}`.trim() || slugify(fullName),
      fullName,
      isChild: guest?.isChild === true || existing?.isChild === true,
    });
  });

  const mergedGuestNames = [...baseGuests];
  guestDetailByName.forEach((_, fullName) => {
    if (!mergedGuestNames.includes(fullName)) {
      mergedGuestNames.push(fullName);
    }
  });

  const guestDetails = mergedGuestNames.map((fullName) => ({
    guestId: guestDetailByName.get(fullName)?.guestId || slugify(fullName),
    fullName,
    isChild: guestDetailByName.get(fullName)?.isChild === true,
  }));

  return {
    id: group?.id || group?.groupId || '',
    label: group?.label || mergedGuestNames.join(', '),
    guests: mergedGuestNames,
    guestDetails,
    eventInvites: normalizeEventInvites(group?.eventInvites),
  };
}

function mergeNormalizedGuestGroups(preferredGroup, fallbackGroup) {
  const preferred = normalizeGuestGroup(preferredGroup);
  const fallback = normalizeGuestGroup(fallbackGroup);
  const mergedGuestNames = [...preferred.guests];
  fallback.guests.forEach((guestName) => {
    if (!guestName) return;
    if (!mergedGuestNames.includes(guestName)) {
      mergedGuestNames.push(guestName);
    }
  });

  const detailByKey = new Map();
  const addGuestDetail = (detail) => {
    const fullName = normalizeGuestName(detail);
    const lookupKey = normalizeGuestLookupKey(fullName);
    if (!lookupKey) return;
    const existing = detailByKey.get(lookupKey);
    detailByKey.set(lookupKey, {
      guestId: `${detail?.guestId || existing?.guestId || slugify(fullName)}`.trim() || slugify(fullName),
      fullName: fullName || existing?.fullName || '',
      isChild: detail?.isChild === true || existing?.isChild === true,
    });
  };

  fallback.guestDetails.forEach(addGuestDetail);
  preferred.guestDetails.forEach(addGuestDetail);

  const guestDetails = mergedGuestNames.map((fullName) => {
    const detail = detailByKey.get(normalizeGuestLookupKey(fullName));
    return {
      guestId: `${detail?.guestId || slugify(fullName)}`.trim() || slugify(fullName),
      fullName,
      isChild: detail?.isChild === true,
    };
  });

  return {
    ...preferred,
    id: preferred.id || fallback.id,
    label: preferred.label || fallback.label,
    guests: mergedGuestNames,
    guestDetails,
    eventInvites: normalizeEventInvites(preferred.eventInvites || fallback.eventInvites),
  };
}

function normalizeScoreboardRows(rows) {
  return (rows || []).map((row) => ({
    name: (row?.playerName || 'Guest').trim() || 'Guest',
    game: row?.gameId || '',
    score: Number(row?.score ?? 0),
    total: Number(row?.total ?? 0),
    attempts: Number(row?.attempts ?? 1),
    scoreLabel: row?.scoreLabel || `${Number(row?.score ?? 0)}`,
    rankPoints: Number(row?.rankPoints ?? row?.score ?? 0),
    metadata: row?.metadata || {},
    updatedAt: Number(row?.updatedAt ?? Date.now()),
  }));
}

function normalizeCombinedRows(rows) {
  return (rows || [])
    .map((row) => {
      const photoPoints = Number(row?.photoPoints ?? 0);
      const strandsPoints = Number(row?.strandsPoints ?? 0);
      return {
        name: (row?.playerName || 'Guest').trim() || 'Guest',
        photoPoints,
        strandsPoints,
        rankPoints: photoPoints + strandsPoints,
        updatedAt: Number(row?.updatedAt ?? 0),
      };
    })
    .sort((a, b) => (b.rankPoints - a.rankPoints) || (b.updatedAt - a.updatedAt));
}

function normalizeSearchMatches(payload) {
  return (payload?.matches || []).map(normalizeGuestGroup).filter((group) => group.id && group.guests.length > 0);
}

function normalizeRsvpResponses(raw) {
  if (!raw || typeof raw !== 'object') return {};
  return raw;
}

function normalizeOptionalBool(value) {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['yes', 'true', '1'].includes(normalized)) return true;
    if (['no', 'false', '0'].includes(normalized)) return false;
  }
  return null;
}

function derivePartyShuttleFromResponses(responses) {
  const guestResponses = Object.values(normalizeRsvpResponses(responses)).filter(
    (guestResponse) => guestResponse && typeof guestResponse === 'object'
  );
  const boolResponses = guestResponses
    .map((guestResponse) => normalizeOptionalBool(guestResponse.needsShuttle))
    .filter((value) => value !== null);
  const firstLocation = guestResponses
    .map((guestResponse) => (guestResponse.shuttleLocation || '').trim())
    .find(Boolean) || '';

  if (boolResponses.includes(true)) {
    return { needsShuttle: true, shuttleLocation: firstLocation };
  }
  if (boolResponses.length > 0 && boolResponses.every((value) => value === false)) {
    return { needsShuttle: false, shuttleLocation: '' };
  }
  return { needsShuttle: null, shuttleLocation: firstLocation };
}

async function mapWithConcurrency(items, concurrencyLimit, mapper) {
  const source = Array.isArray(items) ? items : [];
  const workerCount = Math.max(1, Math.min(Number(concurrencyLimit) || 1, source.length || 1));
  const results = new Array(source.length);
  let cursor = 0;

  const workers = Array.from({ length: workerCount }, async () => {
    while (true) {
      const index = cursor;
      cursor += 1;
      if (index >= source.length) return;
      results[index] = await mapper(source[index], index);
    }
  });

  await Promise.all(workers);
  return results;
}

async function searchInviteGroups(query, limit = 12) {
  const params = new URLSearchParams({ query, limit: `${limit}` });
  const payload = await apiFetch(`/v1/invites/search?${params.toString()}`);
  return normalizeSearchMatches(payload);
}

async function fetchInviteGroup(groupId) {
  const payload = await apiFetch(`/v1/invites/${encodeURIComponent(groupId)}`);
  return normalizeGuestGroup(payload);
}

async function fetchAllGuestGroups(limit = 1000) {
  const params = new URLSearchParams({ limit: `${limit}` });
  const payload = await apiFetch(`/v1/admin/guest-groups?${params.toString()}`);
  return (payload?.groups || []).map(normalizeGuestGroup).filter((group) => group.id && group.guests.length > 0);
}

async function fetchRsvp(groupId) {
  return apiFetch(`/v1/rsvps/${encodeURIComponent(groupId)}`);
}

async function saveRsvp(groupId, payload) {
  return apiFetch(`/v1/rsvps/${encodeURIComponent(groupId)}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

async function fetchGameLeaderboard(gameId, limit = 24) {
  const params = new URLSearchParams({ limit: `${limit}` });
  const payload = await apiFetch(`/v1/games/${encodeURIComponent(gameId)}/leaderboard?${params.toString()}`);
  return normalizeScoreboardRows(payload?.rows || []);
}

async function fetchCombinedLeaderboard(limit = 24) {
  const params = new URLSearchParams({ limit: `${limit}` });
  const payload = await apiFetch(`/v1/leaderboard/combined?${params.toString()}`);
  return normalizeCombinedRows(payload?.rows || []);
}

async function submitGameScore(gameId, scorePayload) {
  return apiFetch(`/v1/games/${encodeURIComponent(gameId)}/scores`, {
    method: 'PUT',
    body: JSON.stringify(scorePayload),
  });
}

const PHOTO_TRIVIA_BASE_DECK = [
  {
    id: 'us-chickfila',
    country: 'US',
    photoSrc: `${process.env.PUBLIC_URL}/geo/chickfila_us_reduced.jpg`,
    alt: 'Chick-fil-A location in Virginia, United States',
    revealTitle: 'Jesus Chicken USA',
    revealDescription: 'This photo was taken in Virginia, United States.',
  },
  {
    id: 'uk-london-tube',
    country: 'UK',
    photoSrc: `${process.env.PUBLIC_URL}/geo/covid_uk_reduced.jpg`,
    alt: 'Photo on the London Underground in the United Kingdom',
    revealTitle: 'Mind the Gap and the mask',
    revealDescription: 'This photo was taken on the London Underground in the United Kingdom.',
  },
  {
    id: 'uk-cowboy-carter',
    country: 'UK',
    photoSrc: `${process.env.PUBLIC_URL}/geo/cowboy_carter_uk_reduced.jpg`,
    alt: 'Cowboy Carter event photo in London, United Kingdom',
    revealTitle: 'Beyonce Round 3, London',
    revealDescription: 'This photo was taken in London, United Kingdom.',
  },
  {
    id: 'us-crabs',
    country: 'US',
    photoSrc: `${process.env.PUBLIC_URL}/geo/crabs_us_reduced.jpg`,
    alt: 'Crab dish photo in North Carolina, United States',
    revealTitle: 'More like Old Bae',
    revealDescription: 'This photo was taken in North Carolina, United States.',
  },
  {
    id: 'us-cville',
    country: 'US',
    photoSrc: `${process.env.PUBLIC_URL}/geo/cville_us_reduced.jpg`,
    alt: 'Charlottesville scene in Virginia, United States',
    revealTitle: 'Visiting Joey\'s \'Uni\' of Virginia',
    revealDescription: 'This photo was taken in Charlottesville, Virginia, United States.',
  },
  {
    id: 'us-dc',
    country: 'US',
    photoSrc: `${process.env.PUBLIC_URL}/geo/dc_us_reduced.jpg`,
    alt: 'Washington, DC scene in the United States',
    revealTitle: 'Pencil!',
    revealDescription: 'This photo is from the United States.',
  },
  {
    id: 'uk-five-guys',
    country: 'UK',
    photoSrc: `${process.env.PUBLIC_URL}/geo/five_guys_uk_reduced.jpg`,
    alt: 'Five Guys location in London, United Kingdom',
    revealTitle: 'Fourth of July 5 Guys London',
    revealDescription: 'This photo was taken in London, United Kingdom.',
  },
  {
    id: 'us-gun',
    country: 'US',
    photoSrc: `${process.env.PUBLIC_URL}/geo/gun_us_reduced.jpg`,
    alt: 'Photo from Tennessee, United States',
    revealTitle: 'Only Ten I See',
    revealDescription: 'This photo was taken in Tennessee, United States.',
  },
  {
    id: 'uk-joey-bday',
    country: 'UK',
    photoSrc: `${process.env.PUBLIC_URL}/geo/joey_bday_uk_reduced.jpg`,
    alt: 'Birthday photo in London, United Kingdom',
    revealTitle: 'Joeys thirdy 30th Birthday?',
    revealDescription: 'This photo was taken in London, United Kingdom.',
  },
  {
    id: 'us-san-fran',
    country: 'US',
    photoSrc: `${process.env.PUBLIC_URL}/geo/san_fran_us_reduced.jpg`,
    alt: 'San Francisco scene in California, United States',
    revealTitle: 'San Fran!',
    revealDescription: 'This photo was taken in San Francisco, California, United States.',
  },
  {
    id: 'uk-snowden',
    country: 'UK',
    photoSrc: `${process.env.PUBLIC_URL}/geo/snowden_uk_reduced.jpg`,
    alt: 'Snowdon area in Wales, United Kingdom',
    revealTitle: 'Hiking Yr Wyddfa, Wales - such great views!',
    revealDescription: 'This photo is from the United Kingdom.',
  },
  {
    id: 'uk-tea',
    country: 'UK',
    photoSrc: `${process.env.PUBLIC_URL}/geo/tea_uk_reduced.jpg`,
    alt: 'Tea service photo in London, United Kingdom',
    revealTitle: 'Tea Time, London',
    revealDescription: 'This photo was taken in London, United Kingdom.',
  },
];

const PHOTO_TRIVIA_FIXED_ORDER_IDS = ['uk-london-tube', 'us-san-fran'];

function normalizeGeoPhotoSrc(src) {
  if (typeof src !== 'string' || !src) return src || '';
  return src.replace(
    /(\/geo\/)([^/?#]+?)(\.(?:png|jpe?g|webp))(?=($|[?#]))/i,
    (_, geoPrefix, fileStem) => `${geoPrefix}${fileStem.endsWith('_reduced') ? fileStem : `${fileStem}_reduced`}.jpg`
  );
}

function getPhotoTriviaDeck() {
  const fixedCards = PHOTO_TRIVIA_FIXED_ORDER_IDS
    .map((id) => PHOTO_TRIVIA_BASE_DECK.find((card) => card.id === id))
    .filter(Boolean);
  const fixedIds = new Set(fixedCards.map((card) => card.id));
  const remainingCards = PHOTO_TRIVIA_BASE_DECK.filter((card) => !fixedIds.has(card.id));
  return [...fixedCards, ...shuffleArray(remainingCards)];
}

const JEOPARDY_BOARD_TEMPLATE = [
  {
    category: 'Joey\nWeate',
    clues: [
      {
        id: 'joey-car',
        prompt: 'What was Joey’s first and only car?',
        answer: 'Red Ford Mustang',
        usdValue: 100,
        correctIndices: [0],
        options: ['Red Ford Mustang', 'Blue Honda Civic', 'Black Jeep Wrangler', 'Silver Toyota Corolla'],
        resultDescription: 'Joey had one car and one car only: a red Ford Mustang.',
      },
      {
        id: 'joey-lived',
        prompt: 'How many times has Joey lived?',
        answer: '10',
        usdValue: 200,
        correctIndices: [2],
        options: ['8', '9', '10', '12'],
        resultDescription: 'Joey has lived in 10 places.',
      },
      {
        id: 'joey-jobs',
        prompt: 'What jobs has Joey had? Select all that apply.',
        answer: 'Ref, Cashier, Army, Tech',
        usdValue: 300,
        questionType: 'multi',
        options: ['Ref', 'Cashier', 'Army', 'Tech', 'Teacher', 'Pilot'],
        correctIndices: [0, 1, 2, 3],
        resultDescription: 'Joey has worked as a ref, cashier, in the Army, and in tech.',
      },
    ],
  },
  {
    category: 'Jessie\nWebster',
    clues: [
      {
        id: 'jess-100',
        prompt: 'Jessie Webster question placeholder (100).',
        answer: 'Placeholder answer',
        usdValue: 100,
        correctIndices: [0],
        options: ['Placeholder answer', 'Choice B', 'Choice C', 'Choice D'],
        resultDescription: 'Add a short fun fact/description here.',
      },
      {
        id: 'jess-200',
        prompt: 'Jessie Webster question placeholder (200).',
        answer: 'Placeholder answer',
        usdValue: 200,
        correctIndices: [0],
        options: ['Placeholder answer', 'Choice B', 'Choice C', 'Choice D'],
        resultDescription: 'Add a short fun fact/description here.',
      },
      {
        id: 'jess-300',
        prompt: 'Jessie Webster question placeholder (300).',
        answer: 'Placeholder answer',
        usdValue: 300,
        correctIndices: [0],
        options: ['Placeholder answer', 'Choice B', 'Choice C', 'Choice D'],
        resultDescription: 'Add a short fun fact/description here.',
      },
    ],
  },
  {
    category: "The JW's",
    clues: [
      {
        id: 'jws-meet-app',
        prompt: 'What app did Joey and Jess meet on?',
        answer: 'Hinge',
        usdValue: 100,
        correctIndices: [0],
        options: ['Hinge', 'Bumble', 'Tinder', 'Instagram'],
        resultDescription: 'Joey and Jess met on Hinge.',
      },
      {
        id: 'jws-200',
        prompt: "The JW's question placeholder (200).",
        answer: 'Placeholder answer',
        usdValue: 200,
        correctIndices: [0],
        options: ['Placeholder answer', 'Choice B', 'Choice C', 'Choice D'],
        resultDescription: 'Add a short fun fact/description here.',
      },
      {
        id: 'jws-300',
        prompt: "The JW's question placeholder (300).",
        answer: 'Placeholder answer',
        usdValue: 300,
        correctIndices: [0],
        options: ['Placeholder answer', 'Choice B', 'Choice C', 'Choice D'],
        resultDescription: 'Add a short fun fact/description here.',
      },
    ],
  },
];

const FINAL_JEOPARDY_CLUE = {
  prompt: "Final Jeopardy placeholder question (replace this with your real final question).",
  answer: 'Placeholder answer',
  options: ['Placeholder answer', 'Choice B', 'Choice C', 'Choice D'],
  correctIndices: [0],
  resultDescription: 'Add a short description for the final Jeopardy answer.',
};

const FINAL_JEOPARDY_ENABLED = false;

function formatElapsedTime(totalSeconds) {
  const safeSeconds = Math.max(0, Math.floor(totalSeconds || 0));
  const minutes = Math.floor(safeSeconds / 60);
  const seconds = safeSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

function getPuzzleScoreFromSeconds(elapsedSeconds) {
  return Math.max(0, PHOTO_PUZZLE_MAX_SCORE - (Math.floor(elapsedSeconds) * PHOTO_PUZZLE_POINTS_PER_SECOND));
}

function shuffleArray(items) {
  const next = [...items];
  for (let i = next.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [next[i], next[j]] = [next[j], next[i]];
  }
  return next;
}

function upsertScore(scoreboard, playerName, result) {
  const normalizedName = (playerName || 'Guest').trim() || 'Guest';
  const nextEntry = {
    name: normalizedName,
    game: result.game,
    score: result.score ?? 0,
    total: result.total ?? 0,
    attempts: result.attempts ?? 1,
    scoreLabel: result.scoreLabel ?? `${result.score ?? 0}`,
    rankPoints: result.rankPoints ?? (result.score ?? 0),
    metadata: result.metadata ?? {},
    updatedAt: Date.now(),
  };
  const filtered = scoreboard.filter((entry) => entry.name.toLowerCase() !== normalizedName.toLowerCase());
  return [...filtered, nextEntry]
    .sort((a, b) => (b.rankPoints - a.rankPoints) || (b.score - a.score) || (b.total - a.total) || (b.updatedAt - a.updatedAt))
    .slice(0, 24);
}

function getPartyPlayerNames(selectedGuestGroup, siteVisitorName) {
  const partyNames = selectedGuestGroup?.guests?.length ? selectedGuestGroup.guests : [];
  if (partyNames.length > 0) return partyNames;
  return siteVisitorName ? [siteVisitorName] : ['Guest'];
}

function getStoredPreferredPlayer(partyNames, siteVisitorName) {
  try {
    const stored = window.localStorage.getItem(PHOTO_TRIVIA_PLAYER_KEY) || '';
    if (partyNames.includes(stored)) return stored;
  } catch {
    // ignore
  }
  if (siteVisitorName && partyNames.includes(siteVisitorName)) return siteVisitorName;
  return partyNames[0] || '';
}

function getScoreEntryForPlayer(scoreboard, playerName) {
  const normalized = (playerName || '').trim().toLowerCase();
  if (!normalized) return null;
  return scoreboard.find((entry) => (entry.name || '').trim().toLowerCase() === normalized) || null;
}

function getRankPositionForPlayer(rows, playerName) {
  const normalized = (playerName || '').trim().toLowerCase();
  if (!normalized) return null;
  const index = (rows || []).findIndex((row) => (row.name || '').trim().toLowerCase() === normalized);
  return index >= 0 ? index + 1 : null;
}

function upsertCombinedLeaderboardRow(rows, playerName, updates = {}) {
  const normalizedName = (playerName || 'Guest').trim() || 'Guest';
  const existing = (rows || []).find((row) => (row.name || '').trim().toLowerCase() === normalizedName.toLowerCase());
  const currentPhoto = Number(existing?.photoPoints ?? 0);
  const currentPuzzle = Number(existing?.strandsPoints ?? 0);

  const nextPhoto = Number(updates.photoPoints ?? currentPhoto);
  const nextPuzzle = Number(updates.strandsPoints ?? currentPuzzle);
  const nextRow = {
    name: normalizedName,
    photoPoints: nextPhoto,
    strandsPoints: nextPuzzle,
    rankPoints: nextPhoto + nextPuzzle,
    updatedAt: Date.now(),
  };

  const filtered = (rows || []).filter((row) => (row.name || '').trim().toLowerCase() !== normalizedName.toLowerCase());
  return [...filtered, nextRow]
    .sort((a, b) => (b.rankPoints - a.rankPoints) || ((b.updatedAt || 0) - (a.updatedAt || 0)))
    .slice(0, 24);
}

function getNameKey(value) {
  return (value || '').trim().toLowerCase();
}

function buildGameRankLookup(rows) {
  const lookup = new Map();
  (rows || []).forEach((row, index) => {
    const key = getNameKey(row?.name);
    if (!key) return;
    lookup.set(key, {
      rank: index + 1,
      score: Number(row?.rankPoints ?? row?.score ?? 0),
      metadata: row?.metadata || {},
      scoreLabel: row?.scoreLabel || '',
    });
  });
  return lookup;
}

function hasRecordedCombinedScore(row) {
  if (!row) return false;
  const updatedAt = Number(row.updatedAt ?? 0);
  const photoPoints = Number(row.photoPoints ?? 0);
  const puzzlePoints = Number(row.strandsPoints ?? 0);
  const rankPoints = Number(row.rankPoints ?? 0);
  return updatedAt > 0 || photoPoints !== 0 || puzzlePoints !== 0 || rankPoints !== 0;
}

function formatLeaderboardGameLine(gameId, gameEntry) {
  if (!gameEntry) return '--';
  const scoreValue = Math.trunc(Number(gameEntry.score) || 0);
  if (gameId === 'strands') {
    const elapsed = Number(gameEntry.metadata?.elapsedSeconds);
    const moves = Number(gameEntry.metadata?.moveCount);
    if (Number.isFinite(elapsed) && Number.isFinite(moves)) {
      return `${scoreValue}pts • ${formatElapsedTime(elapsed)} • ${moves} moves`;
    }
    return `${scoreValue}pts`;
  }
  if (gameId === 'photo') {
    const correct = Number(gameEntry.metadata?.correctCount);
    const answered = Number(gameEntry.metadata?.answeredCount);
    if (Number.isFinite(correct) && Number.isFinite(answered) && answered > 0) {
      return `${scoreValue}pts • ${correct}/${answered}`;
    }
    return `${scoreValue}pts`;
  }
  if (gameId === 'jeopardy') {
    return `${formatSignedCurrencyAmount('USD', scoreValue)}`;
  }
  return `${scoreValue}pts`;
}

function getLeaderboardRankMarker(rank) {
  if (rank === 1) return '🥇';
  if (rank === 2) return '🥈';
  if (rank === 3) return '🥉';
  return `#${rank}`;
}

function buildCombinedSummaryRows(rows, playerName) {
  const safeRows = Array.isArray(rows) ? rows : [];
  const topRows = safeRows.slice(0, 3).map((row, index) => ({ rank: index + 1, row }));
  const topRanks = new Set(topRows.map((item) => item.rank));
  const normalizedPlayer = getNameKey(playerName);
  const playerIndex = normalizedPlayer
    ? safeRows.findIndex((row) => getNameKey(row?.name) === normalizedPlayer)
    : -1;

  let contextRows = [];
  if (playerIndex >= 0) {
    if (playerIndex < 3) {
      if (safeRows.length > 3) {
        const lastRank = safeRows.length;
        contextRows = [{ rank: lastRank, row: safeRows[lastRank - 1] }];
      }
    } else {
      const candidateIndices = [playerIndex - 1, playerIndex, playerIndex + 1];
      const seenRanks = new Set();
      contextRows = candidateIndices
        .filter((idx) => idx >= 0 && idx < safeRows.length)
        .map((idx) => ({ rank: idx + 1, row: safeRows[idx] }))
        .filter((item) => !topRanks.has(item.rank))
        .filter((item) => {
          if (seenRanks.has(item.rank)) return false;
          seenRanks.add(item.rank);
          return true;
        });
    }
  }

  const hiddenRowCount = Math.max(0, safeRows.length - topRows.length - contextRows.length);
  const showDivider = safeRows.length > topRows.length && (contextRows.length > 0 || hiddenRowCount > 0);
  return { topRows, contextRows, showDivider };
}

function CombinedLeaderboardSummary({
  rows,
  playerName,
  title = 'Leaderboard Summary',
  emptyLabel = 'No combined scores yet.',
  gameRows = [],
  gameId = '',
  showViewButton = true,
  onViewFullLeaderboard = () => {},
}) {
  const normalizedPlayer = getNameKey(playerName);
  const isGameMode = !!gameId;
  const gameRankLookup = useMemo(() => buildGameRankLookup(gameRows), [gameRows]);
  const visibleRows = useMemo(() => {
    if (isGameMode) {
      return (gameRows || []).filter((row) => {
        const key = getNameKey(row?.name);
        return key && gameRankLookup.has(key);
      });
    }
    return (rows || []).filter(hasRecordedCombinedScore);
  }, [gameRankLookup, gameRows, isGameMode, rows]);
  const { topRows, contextRows, showDivider } = useMemo(
    () => buildCombinedSummaryRows(visibleRows, playerName),
    [playerName, visibleRows]
  );
  const formatPoints = (value) => `${Math.trunc(Number(value) || 0)} pts`;
  const isSelf = (name) => normalizedPlayer && getNameKey(name) === normalizedPlayer;

  const renderRow = (item, keyPrefix) => {
    const rowName = item.row?.name || 'Guest';
    const gameEntry = isGameMode ? gameRankLookup.get(getNameKey(rowName)) : null;
    const displayRank = isGameMode ? (gameEntry?.rank || item.rank) : item.rank;
    const scoreText = isGameMode
      ? formatLeaderboardGameLine(gameId, gameEntry)
      : formatPoints(item.row.rankPoints);
    return (
      <Box
        key={`${keyPrefix}-${item.rank}-${rowName}`}
        className={[
          'combined-final-row',
          isGameMode ? 'combined-final-row--game-mode' : '',
          `combined-final-row--rank-${displayRank}`,
          isSelf(rowName) ? 'combined-final-row--self' : '',
        ].join(' ').trim()}
      >
        <Typography className="combined-final-rank">{getLeaderboardRankMarker(displayRank)}</Typography>
        <Typography className="copy combined-final-name"><strong>{rowName}</strong></Typography>
        <Typography className="copy combined-final-score"><strong>{scoreText}</strong></Typography>
      </Box>
    );
  };

  return (
    <Box className="combined-final-card">
      <Typography className="photo-trivia-stat-label">{title}</Typography>
      {visibleRows.length === 0 ? (
        <Typography className="copy">{emptyLabel}</Typography>
      ) : (
        <Box className="combined-final-list">
          {topRows.map((item) => renderRow(item, 'combined-summary-top'))}
          {showDivider && (
            <Box className="combined-final-divider">
              <Box className="combined-final-divider-dots">...</Box>
              {showViewButton && (
                <Button
                  size="small"
                  className="combined-final-view-button"
                  onClick={onViewFullLeaderboard}
                >
                  View Full Leaderboard
                </Button>
              )}
              <Box className="combined-final-divider-dots">...</Box>
            </Box>
          )}
          {contextRows.map((item) => renderRow(item, 'combined-summary-context'))}
        </Box>
      )}
    </Box>
  );
}

function CombinedLeaderboardTable({
  rows,
  playerName,
  easyRankLookup,
  mediumRankLookup,
  onPlayGame = () => {},
}) {
  const isCompactLeaderboard = useMediaQuery('(max-width:767px)');
  const normalizedPlayer = getNameKey(playerName);
  const formatTotal = (value) => {
    const total = Math.trunc(Number(value) || 0);
    return isCompactLeaderboard ? `${total}` : `${total}pts`;
  };
  const formatGameCell = (entry) => {
    const rank = Number(entry?.rank ?? 0);
    const score = Math.trunc(Number(entry?.score ?? 0));
    return isCompactLeaderboard ? `#${rank}:${score}` : `#${rank}(${score}pts)`;
  };
  const visibleRows = useMemo(() => (rows || []).filter(hasRecordedCombinedScore), [rows]);

  const renderGameCell = (row, gameId, lookup) => {
    const playerKey = getNameKey(row?.name);
    const gameEntry = lookup?.get(playerKey);
    if (gameEntry) {
      return <Typography className="combined-table-cell">{formatGameCell(gameEntry)}</Typography>;
    }
    if (playerKey && playerKey === normalizedPlayer) {
      return (
        <Button
          size="small"
          className="combined-play-now-button"
          onClick={() => onPlayGame(gameId)}
        >
          Play now
        </Button>
      );
    }
    return <Typography className="combined-table-cell combined-table-cell--empty">-</Typography>;
  };

  return (
    <Box className="combined-table-card">
      <Typography className="photo-trivia-stat-label">Combined Leaderboard</Typography>
      {visibleRows.length === 0 ? (
        <Typography className="copy">No combined scores yet.</Typography>
      ) : (
        <Box className="combined-table-grid-wrap">
          <Box className="combined-table-grid combined-table-grid--header">
            <Typography className="combined-table-head">Rk</Typography>
            <Typography className="combined-table-head">Name</Typography>
            <Typography className="combined-table-head">Tot</Typography>
            <Typography className="combined-table-head">{isCompactLeaderboard ? 'Puz' : 'Puzzle'}</Typography>
            <Typography className="combined-table-head">{isCompactLeaderboard ? 'Geo' : 'GeoGuesser'}</Typography>
          </Box>
          {visibleRows.map((row, index) => {
            const isSelfRow = getNameKey(row?.name) === normalizedPlayer;
            return (
              <Box
                key={`combined-table-row-${row.name}-${index}`}
                className={[
                  'combined-table-grid',
                  'combined-table-grid--row',
                  isSelfRow ? 'combined-table-grid--self' : '',
                ].join(' ').trim()}
              >
                <Typography className="combined-table-rank">{getLeaderboardRankMarker(index + 1)}</Typography>
                <Typography className="combined-table-name">{row.name}</Typography>
                <Typography className="combined-table-total">{formatTotal(row.rankPoints)}</Typography>
                <Box className="combined-table-game">{renderGameCell(row, 'strands', easyRankLookup)}</Box>
                <Box className="combined-table-game">{renderGameCell(row, 'photo', mediumRankLookup)}</Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}

function formatCurrencyAmount(currency, amount) {
  const safeAmount = Number.isFinite(amount) ? amount : 0;
  const symbol = currency === 'GBP' ? '£' : '$';
  return `${symbol}${Math.abs(Math.trunc(safeAmount))}`;
}

function formatSignedCurrencyAmount(currency, amount) {
  const safeAmount = Number.isFinite(amount) ? amount : 0;
  const sign = safeAmount < 0 ? '-' : '';
  return `${sign}${formatCurrencyAmount(currency, safeAmount)}`;
}

function getJeopardyRankPoints(result) {
  return result.baseUsdScore || result.usdScore || 0;
}

function InfoCard({ title, children, className = '', contentClassName = '' }) {
  const cardClassName = ['glass-card', className].filter(Boolean).join(' ');
  return (
    <Card className={cardClassName} elevation={0}>
      <CardContent className={contentClassName} sx={{ p: { xs: 2.5, md: 3 } }}>
        {title ? (
          <>
            <Typography variant="h5" className="section-title" gutterBottom>
              {title}
            </Typography>
            <Divider sx={{ borderColor: 'rgba(71, 58, 46, 0.14)', mb: 2 }} />
          </>
        ) : null}
        {children}
      </CardContent>
    </Card>
  );
}

function WelcomeGateTitle() {
  return (
    <Box component="span" sx={{ fontWeight: 700 }}>
      Welcome to our wedding (website!)
    </Box>
  );
}

function prefixScore(query, namePart) {
  const q = query.trim().toLowerCase();
  const n = namePart.trim().toLowerCase();
  if (!q || q.length < 3) return 0;
  if (!n.startsWith(q)) return 0;
  const denominator = Math.min(Math.max(n.length, 1), 4);
  return q.length / denominator;
}

function getBestMatchingGuest(group, query) {
  let best = { name: '', score: 0 };
  group.guests.forEach((guest) => {
    guest.split(/\s+/).forEach((part) => {
      const score = prefixScore(query, part);
      if (score > best.score) {
        best = { name: guest, score };
      }
    });
  });
  return best;
}

function getDefaultGuestResponse() {
  return {
    friday: '',
    saturday: '',
    sunday: '',
    allergies: '',
    songRequest: '',
  };
}

function RsvpStatusPanel() {
  const navigate = useNavigate();
  const {
    selectedGuestGroup,
    siteVisitorName,
    rsvpResponses,
    setRsvpResponses,
    partySongRecommendation,
    setPartySongRecommendation,
    partyAdditionalNotes,
    setPartyAdditionalNotes,
    partyNeedsShuttle,
    setPartyNeedsShuttle,
    partyShuttleLocation,
    setPartyShuttleLocation,
    hasLoadedRsvp,
  } = useOutletContext();
  const visibleEvents = useMemo(
    () => RSVP_EVENTS.filter((event) => selectedGuestGroup?.eventInvites?.[event.key] !== false),
    [selectedGuestGroup]
  );
  const options = ['Attending', 'Not attending'];
  const [submitConfirmation, setSubmitConfirmation] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showGamesPrompt, setShowGamesPrompt] = useState(false);
  const [rsvpLeaderboardRows, setRsvpLeaderboardRows] = useState([]);
  const [rsvpLeaderboardError, setRsvpLeaderboardError] = useState('');
  const [submittedGuestName, setSubmittedGuestName] = useState('');
  const [autosaveStatus, setAutosaveStatus] = useState('');
  const autosaveRef = useRef({ lastSnapshot: '', timeoutId: null, requestId: 0 });
  const fallbackGuestName = selectedGuestGroup?.guests?.[0] || 'Guest';
  const hasAnyAttendanceSelection = !!selectedGuestGroup?.guests?.some((guest) =>
    visibleEvents.some((event) => !!(rsvpResponses?.[guest]?.[event.key]))
  );
  const rsvpLeaderboardPreview = useMemo(() => {
    const visibleRows = (rsvpLeaderboardRows || []).filter(hasRecordedCombinedScore);
    const activeGuestName = (submittedGuestName || siteVisitorName || fallbackGuestName || 'Guest').trim() || 'Guest';
    const activeGuestKey = getNameKey(activeGuestName);
    const playerIndex = activeGuestKey
      ? visibleRows.findIndex((row) => getNameKey(row?.name) === activeGuestKey)
      : -1;
    const topRows = visibleRows.slice(0, 5).map((row, index) => ({
      rank: index + 1,
      name: row.name,
      scoreLabel: `${Math.trunc(Number(row.rankPoints) || 0)} pts`,
      isSelf: activeGuestKey && getNameKey(row.name) === activeGuestKey,
      isPlaceholder: false,
    }));

    if (playerIndex >= 0) {
      const playerRank = playerIndex + 1;
      const playerRowData = visibleRows[playerIndex];
      const playerRow =
        playerRank > topRows.length
          ? {
              rank: playerRank,
              name: playerRowData.name,
              scoreLabel: `${Math.trunc(Number(playerRowData.rankPoints) || 0)} pts`,
              isSelf: true,
              isPlaceholder: false,
            }
          : null;
      return {
        topRows,
        playerRow,
        playerRank,
        playerHasScore: true,
      };
    }

    return {
      topRows,
      playerRow: {
        rank: visibleRows.length + 1,
        name: activeGuestName,
        scoreLabel: '—',
        isSelf: true,
        isPlaceholder: true,
      },
      playerRank: visibleRows.length + 1,
      playerHasScore: false,
    };
  }, [fallbackGuestName, rsvpLeaderboardRows, siteVisitorName, submittedGuestName]);

  useEffect(() => {
    setSubmitConfirmation('');
    setSubmitError('');
  }, [partyAdditionalNotes, partyNeedsShuttle, partyShuttleLocation, partySongRecommendation, rsvpResponses, selectedGuestGroup?.id]);

  useEffect(() => {
    if (autosaveRef.current.timeoutId) {
      window.clearTimeout(autosaveRef.current.timeoutId);
    }
    autosaveRef.current = { lastSnapshot: '', timeoutId: null, requestId: 0 };
    setAutosaveStatus('');
  }, [selectedGuestGroup?.id]);

  useEffect(() => () => {
    if (autosaveRef.current.timeoutId) {
      window.clearTimeout(autosaveRef.current.timeoutId);
    }
  }, []);

  const buildRsvpSavePayload = useCallback(() => {
    if (!selectedGuestGroup?.id) return null;
    const groupEventInvites = normalizeEventInvites(selectedGuestGroup?.eventInvites);
    const responsesToSave = {};
    (selectedGuestGroup?.guests || []).forEach((guest) => {
      const nextGuestResponse = { ...(rsvpResponses?.[guest] ?? getDefaultGuestResponse()) };
      RSVP_EVENT_KEYS.forEach((eventKey) => {
        if (groupEventInvites[eventKey] === false) {
          nextGuestResponse[eventKey] = 'Not attending';
          return;
        }
        if (nextGuestResponse[eventKey] !== 'Attending' && nextGuestResponse[eventKey] !== 'Not attending') {
          nextGuestResponse[eventKey] = '';
        }
      });
      responsesToSave[guest] = nextGuestResponse;
    });

    return {
      responses: responsesToSave,
      partySongRecommendation,
      partyAdditionalNotes,
      partyNeedsShuttle,
      partyShuttleLocation: partyNeedsShuttle === true ? partyShuttleLocation : '',
      submittedBy: siteVisitorName || '',
    };
  }, [
    partyAdditionalNotes,
    partyNeedsShuttle,
    partyShuttleLocation,
    partySongRecommendation,
    rsvpResponses,
    selectedGuestGroup,
    siteVisitorName,
  ]);

  useEffect(() => {
    if (!selectedGuestGroup?.id || !hasLoadedRsvp) return;
    const payload = buildRsvpSavePayload();
    if (!payload) return;
    const snapshot = JSON.stringify({ groupId: selectedGuestGroup.id, payload });

    if (!autosaveRef.current.lastSnapshot) {
      autosaveRef.current.lastSnapshot = snapshot;
      return;
    }
    if (snapshot === autosaveRef.current.lastSnapshot) return;

    if (autosaveRef.current.timeoutId) {
      window.clearTimeout(autosaveRef.current.timeoutId);
    }
    setAutosaveStatus('Saving changes...');

    autosaveRef.current.timeoutId = window.setTimeout(async () => {
      const requestId = autosaveRef.current.requestId + 1;
      autosaveRef.current.requestId = requestId;
      try {
        await saveRsvp(selectedGuestGroup.id, payload);
        if (autosaveRef.current.requestId === requestId) {
          autosaveRef.current.lastSnapshot = snapshot;
          setAutosaveStatus('Saved automatically');
        }
      } catch {
        if (autosaveRef.current.requestId === requestId) {
          setAutosaveStatus('Autosave failed. Use Submit RSVP to try again.');
        }
      }
    }, 650);
  }, [buildRsvpSavePayload, hasLoadedRsvp, selectedGuestGroup?.id]);

  const handleSubmitRsvp = async () => {
    if (!hasAnyAttendanceSelection || !selectedGuestGroup?.id) return;
    const submittingGuest = (siteVisitorName || fallbackGuestName || 'Guest').trim() || 'Guest';
    setIsSubmitting(true);
    setSubmitError('');
    setRsvpLeaderboardError('');
    setSubmittedGuestName(submittingGuest);
    try {
      const payload = buildRsvpSavePayload();
      if (!payload) return;
      await saveRsvp(selectedGuestGroup.id, payload);
      autosaveRef.current.lastSnapshot = JSON.stringify({ groupId: selectedGuestGroup.id, payload });
      setAutosaveStatus('Saved automatically');
      setSubmitConfirmation('RSVP received. Thank you!');
      try {
        const combinedRows = await fetchCombinedLeaderboard();
        setRsvpLeaderboardRows(combinedRows);
        setRsvpLeaderboardError('');
      } catch {
        setRsvpLeaderboardRows([]);
        setRsvpLeaderboardError('Leaderboard is temporarily unavailable. You can still head to Games! and play.');
      }
      setShowGamesPrompt(true);
    } catch {
      setSubmitError('We could not save your RSVP just now. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderRsvpLeaderboardRow = (row, key) => (
    <Box
      key={key}
      className={[
        'rsvp-games-leaderboard-row',
        row.isSelf ? 'rsvp-games-leaderboard-row--self' : '',
        row.isPlaceholder ? 'rsvp-games-leaderboard-row--placeholder' : '',
      ].join(' ').trim()}
    >
      <Typography className="rsvp-games-leaderboard-rank">
        {row.isPlaceholder ? `#${row.rank}` : getLeaderboardRankMarker(row.rank)}
      </Typography>
      <Typography className="rsvp-games-leaderboard-name">{row.name}</Typography>
      <Typography className="rsvp-games-leaderboard-score">{row.scoreLabel}</Typography>
    </Box>
  );

  return (
    <>
    <InfoCard title="RSVP">
        <Stack spacing={1.4}>
          {!selectedGuestGroup && (
            <Typography className="copy">Select a guest in the welcome flow to manage RSVPs.</Typography>
          )}
        <Typography className="copy">If you haven&apos;t already, please RSVP as soon as possible</Typography>
          {selectedGuestGroup?.guests.map((guest) => {
            const guestResponse = rsvpResponses?.[guest] ?? getDefaultGuestResponse();
            return (
              <Box key={guest} className="rsvp-guest-card">
                <Typography className="rsvp-guest-name">{guest}</Typography>
                <Stack spacing={1}>
                  {visibleEvents.map((event) => (
                    <Box key={event.key} className="rsvp-row">
                      <Typography className="rsvp-row-title">
                        {event.label} <span className="rsvp-row-meta">{event.meta}</span>
                      </Typography>
                      <Stack className="rsvp-row-actions" direction="row" spacing={0.7}>
                        {options.map((option) => (
                          <Button
                            key={option}
                            size="small"
                            variant={guestResponse[event.key] === option ? 'contained' : 'outlined'}
                            className={`rsvp-pill ${guestResponse[event.key] === option ? 'rsvp-pill--active' : ''}`}
                            onClick={() =>
                              setRsvpResponses((prev) => ({
                                ...prev,
                                [guest]: { ...(prev?.[guest] ?? getDefaultGuestResponse()), [event.key]: option },
                              }))
                            }
                          >
                            {option}
                          </Button>
                        ))}
                      </Stack>
                    </Box>
                  ))}
                  <TextField
                    fullWidth
                  label="Allergies/dietary needs e.g. vegetarian, pescetarian, vegan"
                    value={guestResponse.allergies}
                    onChange={(e) =>
                      setRsvpResponses((prev) => ({
                        ...prev,
                        [guest]: { ...(prev?.[guest] ?? getDefaultGuestResponse()), allergies: e.target.value },
                      }))
                    }
                    className="guest-search-input"
                  />
                </Stack>
              </Box>
            );
          })}
          <TextField
            fullWidth
            label="Song requests that will get you to the dance floor"
            value={partySongRecommendation}
            onChange={(e) => setPartySongRecommendation(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <MicRoundedIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            className="guest-search-input"
          />
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Anything else you want us to know or you want to ask!"
            value={partyAdditionalNotes}
            onChange={(e) => setPartyAdditionalNotes(e.target.value)}
            className="guest-search-input"
          />
          {selectedGuestGroup && (
            <Box className={`rsvp-shuttle-panel ${partyNeedsShuttle === true ? 'rsvp-shuttle-panel--double-decker' : ''}`}>
              <Typography className="rsvp-shuttle-title">Shuttle service</Typography>
              <Typography className="rsvp-shuttle-subtitle">
                We have two roaming shuttles available for drop-offs from 9pm. Plan accordingly for rush hours
              </Typography>
              <Box className="rsvp-shuttle-cab" aria-hidden="true">
                <Box className="rsvp-shuttle-driver" />
              </Box>
              <Stack spacing={1}>
                <Box className="rsvp-shuttle-guest-row">
                  <Box className="rsvp-shuttle-question-row">
                    <Typography className="rsvp-row-title">
                      Do you need the shuttle to take you back on Saturday Night?
                    </Typography>
                    <Stack className="rsvp-row-actions rsvp-shuttle-actions" direction="row" spacing={0.7}>
                      {[
                        { label: 'Yes', value: true },
                        { label: 'No', value: false },
                      ].map((option) => (
                        <Button
                          key={option.label}
                          size="small"
                          variant={partyNeedsShuttle === option.value ? 'contained' : 'outlined'}
                          className={`rsvp-pill ${partyNeedsShuttle === option.value ? 'rsvp-pill--active' : ''}`}
                          onClick={() => {
                            setPartyNeedsShuttle(option.value);
                            if (!option.value) {
                              setPartyShuttleLocation('');
                            }
                          }}
                        >
                          {option.label}
                        </Button>
                      ))}
                    </Stack>
                  </Box>
                  {partyNeedsShuttle === true && (
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      label="Where is your party staying?"
                      placeholder="Cirencester, Stratton House Hotel, Airbnb in Elkstone, etc."
                      value={partyShuttleLocation || ''}
                      onChange={(e) => setPartyShuttleLocation(e.target.value)}
                      className="guest-search-input rsvp-shuttle-location"
                    />
                  )}
                </Box>
              </Stack>
            </Box>
          )}
          <Box className="rsvp-submit-row">
            {!!autosaveStatus && (
              <Typography className="rsvp-autosave-status">
                {autosaveStatus}
              </Typography>
            )}
            <Button
              className="guest-continue-button rsvp-submit-button"
              disabled={!hasAnyAttendanceSelection || isSubmitting}
              onClick={handleSubmitRsvp}
            >
              {isSubmitting ? 'Saving RSVP...' : 'Submit RSVP 🥂🍾'}
            </Button>
          </Box>
          {submitConfirmation && (
            <Typography className="copy rsvp-submit-confirmation">{submitConfirmation}</Typography>
          )}
          {submitError && (
            <Typography className="copy rsvp-submit-confirmation">{submitError}</Typography>
          )}
        </Stack>
      </InfoCard>

      {showGamesPrompt && typeof document !== 'undefined' && createPortal(
        <Box
          className="rsvp-games-modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label="RSVP complete leaderboard prompt"
          onClick={() => setShowGamesPrompt(false)}
        >
          <Box className="rsvp-games-modal" onClick={(event) => event.stopPropagation()}>
            <IconButton
              className="rsvp-games-modal-close-icon"
              onClick={() => setShowGamesPrompt(false)}
              aria-label="Close RSVP leaderboard prompt"
            >
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
            <Typography className="copy">
              Thanks for your RSVP. Now time to see what guest knows us best! Winner gets an open bar tab!
            </Typography>
            <Typography className="copy rsvp-games-modal-rank">
              {rsvpLeaderboardPreview.playerHasScore
                ? `You are currently ${getLeaderboardRankMarker(rsvpLeaderboardPreview.playerRank)} on the leaderboard.`
                : `You are currently #${rsvpLeaderboardPreview.playerRank}. Your score spot is waiting for your first game.`}
            </Typography>
            {rsvpLeaderboardError && (
              <Typography className="copy rsvp-submit-confirmation">{rsvpLeaderboardError}</Typography>
            )}
            {!rsvpLeaderboardError && (
              <Box className="rsvp-games-leaderboard">
                <Box className="rsvp-games-leaderboard-row rsvp-games-leaderboard-row--header">
                  <Typography className="rsvp-games-leaderboard-head">Rank</Typography>
                  <Typography className="rsvp-games-leaderboard-head">Guest</Typography>
                  <Typography className="rsvp-games-leaderboard-head">Score</Typography>
                </Box>
                {rsvpLeaderboardPreview.topRows.map((row) =>
                  renderRsvpLeaderboardRow(row, `rsvp-leaderboard-top-${row.rank}-${row.name}`)
                )}
                {rsvpLeaderboardPreview.playerRow && (
                  <>
                    {rsvpLeaderboardPreview.topRows.length > 0 && (
                      <Box className="rsvp-games-leaderboard-divider">...</Box>
                    )}
                    {renderRsvpLeaderboardRow(
                      rsvpLeaderboardPreview.playerRow,
                      `rsvp-leaderboard-player-${rsvpLeaderboardPreview.playerRow.name}-${rsvpLeaderboardPreview.playerRow.rank}`
                    )}
                  </>
                )}
              </Box>
            )}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} className="rsvp-games-modal-actions">
              <Button
                className="guest-continue-button"
                onClick={() => {
                  setShowGamesPrompt(false);
                  navigate('/game');
                }}
              >
                Play Games!
              </Button>
              <Button
                variant="outlined"
                className="rsvp-games-modal-close-button"
                onClick={() => setShowGamesPrompt(false)}
              >
                Close
              </Button>
            </Stack>
          </Box>
        </Box>,
        document.body
      )}
    </>
  );
}

function HomePage() {
  const navigate = useNavigate();
  const { selectedGuestGroup } = useOutletContext();
  const weddingDate = new Date('2026-08-08T00:00:00');
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const msPerDay = 1000 * 60 * 60 * 24;
  const daysUntilWedding = Math.ceil((weddingDate - startOfToday) / msPerDay);
  const hideRsvpCta = daysUntilWedding <= 7;
  const itineraryEvents = [
    {
      key: 'friday',
      title: 'Friday Welcome Drinks',
      date: 'August 7, 2026',
      time: '6:30 PM–10:00 PM',
      location: 'The Green Dragon Inn · Cowley, Cheltenham GL53 9NW',
      photoCaption: 'Green Dragon Inn · Cowley, Cheltenham GL53 9NW',
      attire: 'Come as you are',
      details: [
        'Come for a pint at a local pub for a hello and welcome to the Cotswolds before the big day.',
      ],
      mapLinks: [
        { label: 'Google Maps', href: 'https://maps.app.goo.gl/KtKPJ6k1uLZ88H9C7' },
        { label: 'Apple Maps', href: 'https://maps.apple/p/JPph5p_Y-whMK-' },
      ],
      venueLabel: 'The Green Dragon Inn',
      showVenueArt: true,
      photoSrc: `${process.env.PUBLIC_URL}/home/green%20dragon%20pint_reduced.jpg`,
      rsvpEmoji: '🍺',
    },
    {
      key: 'saturday',
      title: 'Wedding Day',
      date: 'August 8, 2026',
      time: '2pm -> late',
      location: 'Coombe End Manor · Elkstone, England, GL53 9PT',
      photoCaption: 'Coombe End Manor · Elkstone, England, GL53 9PT',
      attire: 'Funky Formal',
      details: [
        "Please arrive at 2:00pm for a 2:30pm ceremony start. Following the ceremony we'll have cocktails, dinner, and dancing into the early hours.",
      ],
      mapLinks: [
        { label: 'Google Maps', href: 'https://maps.app.goo.gl/Bd1TdDUrfgfzy9Bv8' },
        { label: 'Apple Maps', href: 'https://maps.apple/p/-0LYNe4LAhF6-k' },
      ],
      venueLabel: 'Coombe End Manor',
      showVenueArt: true,
      photoSrc: `${process.env.PUBLIC_URL}/home/Coombe%20end%20pic_reduced.jpg`,
      rsvpEmoji: '💍',
    },
    {
      key: 'sunday',
      title: 'Sunday Recovery Breakfast',
      date: 'August 9, 2026',
      time: '11:00 AM–1:00 PM',
      location: 'Coombe End Manor · Elkstone, England, GL53 9PT',
      photoCaption: 'Coombe End Manor · Elkstone, England, GL53 9PT',
      attire: 'Come as you are',
      details: ["If you don't need to rush off, swing by for a coffee and pastry and any lost items you left behind."],
      mapLinks: [
        { label: 'Google Maps', href: 'https://maps.app.goo.gl/Bd1TdDUrfgfzy9Bv8' },
        { label: 'Apple Maps', href: 'https://maps.apple/p/-0LYNe4LAhF6-k' },
      ],
      venueLabel: 'Coombe End Manor',
      showVenueArt: true,
      photoSrc: `${process.env.PUBLIC_URL}/home/sunday_reduced.jpg`,
      rsvpEmoji: '☀️',
    },
  ];
  const visibleItineraryEvents = itineraryEvents.filter(
    (event) => selectedGuestGroup?.eventInvites?.[event.key] !== false
  );

  return (
    <Grid container spacing={2.5}>
      <Grid item xs={12}>
        <InfoCard title="What's on">
          <Stack spacing={2.4} className="itinerary-stack">
            {visibleItineraryEvents.map((event) => (
              <Box key={event.key} className="itinerary-item">
                <Box className="itinerary-body-layout">
                  <Box className="itinerary-main-row">
                    <Box className="itinerary-text-col">
                      <Typography className="itinerary-title">{event.title}</Typography>
                      <Typography className="copy itinerary-date-time">
                        <strong>{event.date}</strong> • <strong>{event.time}</strong>
                      </Typography>
                      <Stack spacing={0.35} mt={0.6} className="itinerary-description">
                        {event.details.map((line) => (
                          <Typography key={line} className="copy">{line}</Typography>
                        ))}
                      </Stack>
                      <Typography className="copy itinerary-location">
                        <strong>{event.location}</strong>
                      </Typography>
                    </Box>
                    {event.showVenueArt && (
                      <Box className="itinerary-side-panel">
                        <Stack spacing={0.7}>
                          <Box
                            component="img"
                            className="home-venue-art-card"
                            src={event.photoSrc}
                            alt={event.photoCaption || event.venueLabel || event.title}
                            loading="lazy"
                          />
                          {!hideRsvpCta && (
                            <Button
                              className="guest-continue-button itinerary-rsvp-button"
                              onClick={() => {
                                window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
                                navigate('/rsvp');
                              }}
                            >
                              RSVP NOW {event.rsvpEmoji}
                            </Button>
                          )}
                          <Box className="itinerary-map-links itinerary-map-links--mobile">
                            {event.mapLinks.map((link) => (
                              <a
                                key={link.label}
                                className={`itinerary-map-link ${link.label.toLowerCase().includes('apple') ? 'itinerary-map-link--right' : ''}`}
                                href={link.href}
                                target="_blank"
                                rel="noreferrer"
                              >
                                {link.label}
                              </a>
                            ))}
                          </Box>
                        </Stack>
                      </Box>
                    )}
                  </Box>
                  {event.attire && (
                    <Box className="itinerary-attire-row">
                      <Typography className="copy itinerary-attire">
                        <strong>Attire:</strong> {event.attire}
                      </Typography>
                      <Box className="itinerary-map-links itinerary-map-links--desktop">
                        {event.mapLinks.map((link) => (
                          <a
                            key={link.label}
                            className={`itinerary-map-link ${link.label.toLowerCase().includes('apple') ? 'itinerary-map-link--right' : ''}`}
                            href={link.href}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {link.label}
                          </a>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            ))}
          </Stack>
        </InfoCard>
      </Grid>
    </Grid>
  );
}

function StoryPage() {
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prevHtmlSnap = html.style.scrollSnapType;
    const prevBodySnap = body.style.scrollSnapType;
    html.style.scrollSnapType = 'y mandatory';
    body.style.scrollSnapType = 'y mandatory';
    return () => {
      html.style.scrollSnapType = prevHtmlSnap;
      body.style.scrollSnapType = prevBodySnap;
    };
  }, []);

  const storyMoments = [
    {
      year: '2019',
      date: 'January 2019',
      blurb: 'In snowy January 2019, Jess went to DC for an internship and hopped on Hinge for an American tour guide...',
      photos: [`${process.env.PUBLIC_URL}/story-pictures/image38_reduced.jpg`],
    },
    {
      year: '2019',
      date: '24 January',
      blurb: 'On 24 January, we went on our first date...',
      photos: [`${process.env.PUBLIC_URL}/story-pictures/image33_reduced.jpg`],
    },
    {
      year: '2019',
      date: 'Spring 2019',
      blurb: 'Spring 2019 was spent dating (and meeting each other’s mom/mum bizarrely early on).',
      photos: [
        `${process.env.PUBLIC_URL}/story-pictures/image5_reduced.jpg`,
        `${process.env.PUBLIC_URL}/story-pictures/image11_reduced.jpg`,
      ],
    },
    {
      year: '2019',
      date: 'Summer 2019',
      blurb: 'Jess returned home and in summer 2019, Joey came to London to teach Jess’s mates some frat drinking skills.',
      photos: [`${process.env.PUBLIC_URL}/story-pictures/image19_reduced.jpg`],
    },
    {
      year: '2019',
      date: 'Late 2019',
      blurb: 'In late 2019, our Facetime era was in full swing... slightly earlier than the rest of the world’s.',
      photos: [`${process.env.PUBLIC_URL}/story-pictures/image32_reduced.jpg`],
    },
    {
      year: '2020',
      date: '14 March 2020',
      blurb: 'Jess had planned to visit DC for Joey’s birthday on 14 March 2020. Covid had other plans...',
      photos: [`${process.env.PUBLIC_URL}/story-pictures/image18_reduced.jpg`],
    },
    {
      year: '2020',
      date: 'November 2020',
      blurb: 'In November 2020, Jess finally made it to visit Joey in the US...',
      photos: [`${process.env.PUBLIC_URL}/story-pictures/image14_reduced.jpg`],
    },
    {
      year: '2020',
      date: 'December 2020',
      blurb: 'And then in December 2020, Joey took the plunge and moved to London.',
      photos: [`${process.env.PUBLIC_URL}/story-pictures/image16_reduced.jpg`],
    },
    {
      year: '2021',
      date: '2021',
      blurb: '2021 was not quite living it up in the Big Smoke but living locked down with 4 girls.',
      photos: [`${process.env.PUBLIC_URL}/story-pictures/image2_reduced.jpg`],
    },
    {
      year: '2021',
      date: 'Spring 2021',
      blurb: 'But we made it on some spring adventures.',
      photos: [`${process.env.PUBLIC_URL}/story-pictures/country_side_reduced.jpg`],
    },
    {
      year: '2021',
      date: '2021',
      blurb: 'Survived Jess’s DIY bleached hair.',
      photos: [`${process.env.PUBLIC_URL}/story-pictures/image30_reduced.jpg`],
    },
    {
      year: '2021',
      date: 'Christmas 2021',
      blurb: 'And topped the year off with Joey’s parents visiting Covid-riddled Websters for Christmas.',
      photos: [
        `${process.env.PUBLIC_URL}/story-pictures/image35_reduced.jpg`,
        `${process.env.PUBLIC_URL}/story-pictures/image4_reduced.jpg`,
      ],
    },
    {
      year: '2022',
      date: 'Summer 2022',
      blurb: 'Summer 2022 saw more London visitors.',
      photos: [
        `${process.env.PUBLIC_URL}/story-pictures/image15_reduced.jpg`,
        `${process.env.PUBLIC_URL}/story-pictures/image9_reduced.jpg`,
      ],
    },
    {
      year: '2022',
      date: '2022',
      blurb: 'And Jess finally made it on the annual US family holiday in the Outer Banks.',
      photos: [
        `${process.env.PUBLIC_URL}/story-pictures/image21_reduced.jpg`,
        `${process.env.PUBLIC_URL}/story-pictures/image17_reduced.jpg`,
      ],
    },
    {
      year: '2023',
      date: '2023',
      blurb: 'In 2023 we moved into our Hackney flat.',
      photos: [`${process.env.PUBLIC_URL}/story-pictures/image20_reduced.jpg`],
    },
    {
      year: '2023',
      date: '2023',
      blurb: 'And spent most of the year at the pub.',
      photos: [`${process.env.PUBLIC_URL}/story-pictures/image3_reduced.jpg`],
    },
    {
      year: '2024',
      date: '2024',
      blurb: 'In 2024 we had some big adventures.',
      photos: [
        `${process.env.PUBLIC_URL}/story-pictures/image34_reduced.jpg`,
        `${process.env.PUBLIC_URL}/story-pictures/image27_reduced.jpg`,
      ],
    },
    {
      year: '2024',
      date: '2024',
      blurb: 'Protested in rain and shine',
      photos: [
        `${process.env.PUBLIC_URL}/story-pictures/image28_reduced.jpg`,
        `${process.env.PUBLIC_URL}/story-pictures/image25_reduced.jpg`,
      ],
    },
    {
      year: '2024',
      date: 'November 2024',
      blurb: 'And in November, Joey asked Jess to get married in a pretty bloody brilliant day of throwbacks.',
      photos: [`${process.env.PUBLIC_URL}/story-pictures/image23_reduced.jpg`],
    },
    {
      year: '2025',
      date: 'Summer 2025',
      blurb: 'Summer 2025 brought the the Websters on the Outer Banks holiday.',
      photos: [
        `${process.env.PUBLIC_URL}/story-pictures/image37_reduced.jpg`,
        `${process.env.PUBLIC_URL}/story-pictures/image22_reduced.jpg`,
      ],
    },
    {
      year: '2025',
      date: '2025',
      blurb: 'And after our US engagement celebrations...',
      photos: [`${process.env.PUBLIC_URL}/story-pictures/image13_reduced.jpg`],
    },
    {
      year: '2025',
      date: '2025',
      blurb: 'We set off on a Brits abroad roadtrip from DC to Nashville.',
      photos: [
        `${process.env.PUBLIC_URL}/story-pictures/image12_reduced.jpg`,
        `${process.env.PUBLIC_URL}/story-pictures/image1_reduced.jpg`,
      ],
    },
    {
      year: '2025',
      date: '2025',
      blurb: 'We headed back home and tried to do some wedding prep...',
      photos: [`${process.env.PUBLIC_URL}/story-pictures/image26_reduced.jpg`],
    },
    {
      year: '2025',
      date: 'Late 2025',
      blurb: 'But instead found ourselves in Australia to end the year.',
      photos: [
        `${process.env.PUBLIC_URL}/story-pictures/image8_reduced.jpg`,
        `${process.env.PUBLIC_URL}/story-pictures/image6_reduced.jpg`,
        `${process.env.PUBLIC_URL}/story-pictures/image7_reduced.jpg`,
        `${process.env.PUBLIC_URL}/story-pictures/image29_reduced.jpg`,
      ],
    },
    {
      year: '2026',
      date: '2026',
      blurb: 'Back home to the Hackney riviera for a soggy start to 2026.',
      photos: [
        `${process.env.PUBLIC_URL}/story-pictures/image24_reduced.jpg`,
        `${process.env.PUBLIC_URL}/story-pictures/image36_reduced.jpg`,
      ],
    },
    {
      year: '2026',
      date: '2026',
      blurb: 'But all set for what\'s next',
      photos: [`${process.env.PUBLIC_URL}/story-pictures/image10_reduced.jpg`],
    },
  ];

  return (
    <Grid container spacing={2.5} className="story-page-grid">
      <Grid item xs={12}>
        <InfoCard title={null} className="glass-card--story">
          <Typography variant="h5" className="section-title" gutterBottom>
            How we got here
          </Typography>
          <Divider sx={{ borderColor: 'rgba(71, 58, 46, 0.14)', mb: 2 }} />
          <Box className="story-timeline">
            {storyMoments.map((moment, index) => (
              <Box
                key={`${moment.year}-${moment.date}-${index}`}
                className={`story-timeline-item ${index % 2 === 1 ? 'story-timeline-item--reverse' : ''}`}
              >
                <Box className="story-moment-layout">
                  {(() => {
                    const photos = moment.photos || [];
                    const isMulti = photos.length > 1;
                    const isDouble = photos.length === 2;
                    return (
                      <>
                        <Box className="story-moment-copy">
                          <Typography className="story-date">{moment.date}</Typography>
                          <Typography className="copy story-blurb">
                            {moment.blurb}
                          </Typography>
                        </Box>
                        <Box className="story-mobile-photo-separator" aria-hidden="true" />
                        <Box className="story-timeline-track" aria-hidden="true">
                          <Box className="story-timeline-dot" />
                        </Box>
                        <Box className={`story-moment-photos ${isMulti ? 'story-moment-photos--multi' : 'story-moment-photos--single'} ${isDouble ? 'story-moment-photos--double' : ''}`}>
                          {photos.flatMap((photoSrc, photoIndex) => {
                            const items = [
                              <Box key={`${moment.year}-${moment.date}-photo-${photoIndex}`} className="story-photo">
                                <img
                                  className="story-photo-image"
                                  src={photoSrc}
                                  alt={`${moment.year} ${moment.date} ${photoIndex + 1}`}
                                  loading="lazy"
                                />
                              </Box>,
                            ];

                            if (isDouble && photoIndex === 0) {
                              items.push(
                                <Box key={`${moment.year}-${moment.date}-connector`} className="story-double-connector" aria-hidden="true" />
                              );
                            }

                            return items;
                          })}
                        </Box>
                        <Box className="story-mobile-top-separator" aria-hidden="true">
                          <Box className="story-mobile-heart" />
                        </Box>
                        <Box className="story-mobile-bottom-separator" aria-hidden="true" />
                      </>
                    );
                  })()}
                </Box>
              </Box>
            ))}
          </Box>
        </InfoCard>
      </Grid>
    </Grid>
  );
}

function TravelPageContent() {
  return (
    <Stack spacing={2} className="travel-stay-breakdown">
      <Typography className="copy">
        If you are travelling from the US (or internationally), we recommend flying into <strong>London Heathrow Airport (LHR)</strong>.
      </Typography>
      <Stack spacing={1.2} className="travel-options-stack">
        <Box className="travel-stay-subsection">
          <Typography className="travel-option-title">🚌 Option 1: Coach / Bus</Typography>
          <Typography className="copy">(Easiest from the airport)</Typography>
          <Typography className="copy">
            You can take the <strong>National Express Bus</strong> directly from Heathrow to <strong>Cirencester</strong>. This takes approximately <strong>1 hour 40 minutes</strong> and drops off in <strong>Cirencester town center</strong>. From here you can walk or get a cab to where you&apos;re staying.
          </Typography>
          <Typography className="copy">
            Book:{' '}
            <a href="https://www.nationalexpress.com/en" target="_blank" rel="noreferrer">
              nationalexpress.com
            </a>
          </Typography>
        </Box>
        <Box className="travel-stay-subsection">
          <Typography className="travel-option-title">🚆 Option 2: Train</Typography>
          <Typography className="copy">(Best if coming from London)</Typography>
          <Typography className="copy">
            You can catch a train from <strong>London Paddington</strong> to <strong>Kemble Station</strong> (Great Western Railway). This takes approximately <strong>1 hour 10 minutes</strong> and will drop you off in <strong>Kemble</strong>, a quick cab ride away from the venue (the nearest station to the venue)
          </Typography>
          <Typography className="copy">
            Book Tickets:{' '}
            <a href="https://www.thetrainline.com/en-us" target="_blank" rel="noreferrer">
              thetrainline.com
            </a>
          </Typography>
        </Box>
      </Stack>

      <Box className="travel-stay-subsection">
        <Typography className="what-to-do-subsection-title">🕰️ A Note on Timing</Typography>
        <Typography className="copy">
          Trains and coaches can book up quickly, especially in summer. Delays and cancellation do occasionally happen (weather, strikes, engineering works... even swans on the tracks!).
        </Typography>
        <Stack spacing={0.5}>
          <Typography className="copy">• Book travel as early as possible</Typography>
          <Typography className="copy">• Allow extra time in your schedule</Typography>
          <Typography className="copy">• Arrive the day before the wedding if you can</Typography>
        </Stack>
      </Box>

      <Box className="travel-stay-subsection">
        <Typography className="what-to-do-subsection-title">🗺️ Getting Around the Cotswolds</Typography>
        <Typography className="copy">
          The Cotswolds is a rural area, and public transport between villages can be limited.
        </Typography>
        <Stack spacing={0.5}>
          <Typography className="copy">• Taxis (best to pre-book)</Typography>
          <Typography className="copy">• Rental cars (recommended if you'd like to explore)</Typography>
        </Stack>
        <Typography className="copy">If you're staying in Cirencester or nearby, taxis will be the easiest option for getting to and from the wedding.</Typography>
      </Box>
    </Stack>
  );
}

function AccommodationPageContent() {
  const recommendedStays = [
    {
      id: 3,
      name: 'Stratton House Hotel & Spa',
      area: 'Stratton / Cirencester',
      note: 'Hotel + spa close to Cirencester and an easy weekend base.',
      href: 'https://strattonhousehotel.com/',
    },
    {
      id: 4,
      name: 'The Fleece at Cirencester',
      area: 'Cirencester Market Place',
      note: 'Town-centre inn with restaurants and pubs nearby.',
      href: 'https://www.thefleececirencester.co.uk/',
    },
    {
      id: 5,
      name: 'The Kings Head',
      area: 'Cirencester Market Place',
      note: 'Central Cirencester hotel and a great base for the weekend.',
      href: 'https://kingsheadcotswold.co.uk/stay/',
    },
    {
      id: 6,
      name: 'Ingleside House',
      area: 'Cirencester (near Barn Theatre)',
      note: 'Boutique stay with a bold Art Deco-inspired style and an on-site restaurant/bar.',
      href: 'https://www.booking.com/hotel/gb/ingleside-cirencester.en.html?aid=1606903&no_rooms=1&group_adults=2',
    },
    {
      id: 6,
      name: 'Airbnb',
      href: 'https://www.airbnb.co.uk/s/Colesbourne--Cotswold/homes?refinement_paths%5B%5D=%2Fhomes&acp_id=e99eab47-8a61-4f38-9b21-6b6321c3c832&date_picker_type=calendar&checkin=2026-08-07&checkout=2026-08-09&adults=2&search_type=user_map_move&flexible_trip_lengths%5B%5D=one_week&monthly_start_date=2026-04-01&monthly_length=3&monthly_end_date=2026-07-01&price_filter_input_type=2&price_filter_num_nights=2&channel=EXPLORE&zoom_level=12.375976701153592&place_id=ChIJc1WArUEXcUgRstb3Pn6rnYc&query=Colesbourne%2C%20Cotswold&search_mode=regular_search&ne_lat=51.85152076854455&ne_lng=-1.9557853139756673&sw_lat=51.72518486414381&sw_lng=-2.0452062111802434&zoom=12.375976701153592&search_by_map=true',
    },
  ];

  return (
    <Stack spacing={2} className="travel-stay-breakdown">
      <Box className="travel-stay-subsection">
        <Typography className="copy">
          Most accommodation options are within about a 15-minute drive of the venue. We recommend staying in Cirencester or nearby villages like Cowley, Elkstone, Colesbourne, and Stratton.
        </Typography>
        <Typography className="copy" sx={{ mt: 1 }}>
          <strong>Some recommendations to get you started:</strong>
        </Typography>
        <Stack spacing={0.35} sx={{ alignItems: 'stretch', mt: 0.25 }}>
          {recommendedStays.map((stay) => (
            <Typography key={stay.name} className="copy" sx={{ mb: '0 !important' }}>
              • {stay.name} - <a href={stay.href} target="_blank" rel="noreferrer">Website</a>
            </Typography>
          ))}
        </Stack>
      </Box>
    </Stack>
  );
}

function TravelAndStayPage() {
  return (
    <Grid container spacing={2.5}>
      <Grid item xs={12}>
        <InfoCard>
          <Stack spacing={1.6} className="travel-tab-sections">
            <Typography className="page-main-title">Travel</Typography>
            <TravelPageContent />
            <Typography className="page-main-title">Stay...</Typography>
            <AccommodationPageContent />
            <Typography className="page-main-title">... & Play</Typography>
            <AndPlayPageContent />
          </Stack>
        </InfoCard>
      </Grid>
    </Grid>
  );
}

function AndPlayPageContent() {
  const allWhatToDoPhotos = [
    {
      src: `${process.env.PUBLIC_URL}/travelandstay/cotswolds_1_reduced.jpg`,
      caption: 'Explore with a walk',
    },
    {
      src: `${process.env.PUBLIC_URL}/travelandstay/cotswolds_4_reduced.jpg`,
      caption: 'or a run',
    },
    {
      src: `${process.env.PUBLIC_URL}/travelandstay/london_2_reduced.jpg`,
      caption: 'or a bike',
    },
    {
      src: `${process.env.PUBLIC_URL}/travelandstay/london_uber_boat_reduced.jpg`,
      caption: 'or even a boat!',
    },
    {
      src: `${process.env.PUBLIC_URL}/travelandstay/london_3_reduced.jpg`,
      caption: 'Have a cup of tea',
    },
    {
      src: `${process.env.PUBLIC_URL}/travelandstay/london_tea_two_reduced.jpg`,
      caption: 'or champagne',
    },
    {
      src: `${process.env.PUBLIC_URL}/travelandstay/london_thedove_reduced.jpg`,
      caption: 'or beer',
    },
    {
      src: `${process.env.PUBLIC_URL}/travelandstay/london_wine_reduced.jpg`,
      caption: 'or a wine (& roast)!',
    },
    {
      src: `${process.env.PUBLIC_URL}/travelandstay/london_karl_joey_reduced.jpg`,
      caption: 'Visit famous graves',
    },
    {
      src: `${process.env.PUBLIC_URL}/travelandstay/london_big_ben_reduced.jpg`,
      caption: 'or big ben',
    },
    {
      src: `${process.env.PUBLIC_URL}/travelandstay/london_globe_reduced.jpg`,
      caption: 'or the globe',
      fitMode: 'contain',
    },
    {
      src: `${process.env.PUBLIC_URL}/travelandstay/london_7_reduced.jpg`,
      caption: "just take a toursity pic!",
    },
  ];

  return (
    <Stack spacing={2}>
      <Typography className="copy">
        If you&apos;re making a trip out of the wedding and coming to London, please do let us know. We have so many recommendations and suggestions that we&apos;d love to share with you.
      </Typography>
      <Typography className="copy">Thank you again for coming all the way!</Typography>
      <Typography className="copy">For the Cotswolds...</Typography>
      <Typography className="copy">
        There are lovely little towns and walks to explore. Please let us know if you want any recommendations (although google may be more helpful than us on this one!)
      </Typography>
      <Box className="what-to-do-subsection">
        <Box className="what-to-do-ideas-grid">
          {allWhatToDoPhotos.map((photo, index) => (
            <Box className="what-to-do-ideas-grid-item" key={`what-to-do-photo-${index + 1}`}>
              <Box className="what-to-do-polaroid-wrap">
                <Box
                  className="what-to-do-gallery-card what-to-do-polaroid"
                  sx={{ '--what-to-do-polaroid-tilt': `${[-2.2, 1.8, -1.1, 2.3, -1.7, 0.9][index % 6]}deg` }}
                >
                  <Box
                    component="img"
                    className={[
                      'what-to-do-gallery-card-image',
                      'what-to-do-polaroid-image',
                      photo.fitMode === 'contain' ? 'what-to-do-polaroid-image--contain' : '',
                    ].join(' ').trim()}
                    src={photo.src}
                    alt={`What to do photo ${index + 1}`}
                    loading="lazy"
                  />
                  <Typography className="what-to-do-polaroid-caption">{photo.caption}</Typography>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Stack>
  );
}

function WhatToDoPage() {
  return <Navigate to="/travel" replace />;
}

function FaqPage() {
  const faqs = [
    ['How can I get to the venue?', 'The easiest base is Cirencester (about 15 minutes away) but Cowley is also close. Taxis are the best option on the day, and we recommend pre-booking. We will organise some roaming cabs/shuttles leaving the venue throughout the evening, but if you know you need to be away at a certain time, we suggest you pre-book.'],
    ['What is Funky Formal?', 'Think wedding-ready, but fun. Suits, dresses, jumpsuits, color, personality, a little flair. Feel free to go bold. Comfortable shoes are strongly encouraged for dancing (and for not breaking ankles on the grass).'],
    ['What time should I arrive on Saturday?', 'Please arrive at 2:00pm for a 2:30pm ceremony start.  The road up to the venue is small and windy and lots of you will be coming at once, so we recommend leaving more time than Maps says you\'ll need!'],
    ['What should I wear on Friday?', 'Friday is a casual local pub evening. Wear whatever feels comfortable and come say hello.'],
    ['What time does Saturday go until?', 'Saturday festivities will run late into the evening (and into the early hours). You are very welcome to stay for the full party or head out whenever you need. We will have roaming cabs and shuttles to help get guests home safely.'],
    ['Should I bring extra shoes?', 'Yes, if you want to dance comfortably. Funky Formal is the vibe, but a shoe swap later in the evening is always a good idea.'],
  ];
  return (
    <Grid container spacing={2.5}>
      <Grid item xs={12}>
        <InfoCard title="FAQ">
          <Stack spacing={1.1}>
            {faqs.map(([q, a]) => (
              <Accordion key={q} className="faq-accordion" disableGutters elevation={0}>
                <AccordionSummary expandIcon={<ExpandMoreRoundedIcon className="faq-expand" />}>
                  <Typography className="faq-question">{q}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography className="copy">{a}</Typography>
                </AccordionDetails>
              </Accordion>
            ))}
            <Box className="faq-contact-box">
              <Typography className="copy">Any other questions, please contact us!</Typography>
            </Box>
          </Stack>
        </InfoCard>
      </Grid>
    </Grid>
  );
}

function GiftsAndCausesPage() {
  return (
    <Grid container spacing={2.5}>
      <Grid item xs={12}>
        <InfoCard title="Gifts?">
          <Stack spacing={2} className="gifts-main-sections">
            <Box className="what-to-do-main-section">
              <Box className="what-to-do-subsection">
                <Stack spacing={1.05}>
                  <Typography className="copy">
                    We are so fortunate to be in a position to celebrate our wedding with all of our loved ones in safety and security.
                  </Typography>
                  <Typography className="copy">
                    In our own countries and all over the world, many people are unable to celebrate in this way, whether because of war and oppression, poverty and insecurity, or because of a rejection of who they are and who they love.
                  </Typography>
                  <Typography className="copy">
                    In lieu of gifts, we would like to make a donation to some of the causes we support that promote equality, justice and human rights for all.
                  </Typography>
                  <Typography className="copy">
                    If you are able to contribute, please make a transfer to Jess&apos;s Monzo or Joey&apos;s Venmo below. We will divide your donations between the organisations we support that we have detailed below. Or, if you&rsquo;d prefer your donation to go to just one of our chosen charities, let us know and we will make sure your money is directed there.
                  </Typography>
                  <Typography className="copy">
                    Thank you so much for your generosity.
                  </Typography>
                  <Box className="gifts-payment-grid">
                    <Box className="gifts-payment-card gifts-payment-card--monzo">
                      <Box className="gifts-payment-header">
                        <Box className="gifts-payment-symbol gifts-payment-symbol--monzo" aria-hidden="true">
                          <svg
                            className="gifts-payment-symbol-svg"
                            viewBox="0 0 56 40"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <polygon points="5,14 5,30 17,40 17,24" fill="#2A8191" />
                            <polygon points="5,14 14,5 28,19 28,29 17,24" fill="#79B595" />
                            <polygon points="28,19 42,5 51,14 28,36" fill="#DBBB7A" />
                            <polygon points="51,14 51,30 39,40 39,24" fill="#E24B60" />
                          </svg>
                        </Box>
                        <Typography className="gifts-payment-title">MONZO</Typography>
                      </Box>
                      <Typography className="copy gifts-payment-line">Jessica Webster</Typography>
                      <Typography className="copy gifts-payment-line"><strong>Sort Code:</strong> 04-00-04</Typography>
                      <Typography className="copy gifts-payment-line"><strong>Acct No:</strong> 40294226</Typography>
                    </Box>

                    <Box className="gifts-payment-card gifts-payment-card--venmo">
                      <Box className="gifts-payment-header">
                        <Box className="gifts-payment-symbol gifts-payment-symbol--venmo" aria-hidden="true">
                          <svg
                            className="gifts-payment-symbol-svg"
                            viewBox="0 0 40 40"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <rect x="1" y="1" width="38" height="38" rx="6" fill="#3D95CE" />
                            <path
                              d="M9.5 11.5H17.8L19.6 24.6L23.9 11.5H31L23.8 29H14.6L9.5 11.5Z"
                              fill="#FFFFFF"
                            />
                          </svg>
                        </Box>
                        <Typography className="gifts-payment-title">VENMO</Typography>
                      </Box>
                      <Typography className="copy gifts-payment-line">
                        <a href="https://venmo.com/u/Joey-Weate" target="_blank" rel="noreferrer">@joey-weate</a>
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              </Box>
            </Box>

            <Box className="what-to-do-main-section">
              <Typography className="what-to-do-subsection-title gifts-cause-title gifts-cause-title--1">Access and Empowerment for People with Learning Disabilities</Typography>
              <Box className="what-to-do-subsection">
                <Typography className="copy">
                  Joey&apos;s mom is a special education teacher in Virginia and works tirelessly to educate and advocate for her students.
                </Typography>
                <Typography className="copy">
                  Jess&apos;s mum Celia co-founded WAVE (&apos;We&apos;re All Valued Equally&apos;) in 2009 and founded the inclusive play group The Challenge Group to support families with children with learning disabilities.
                </Typography>
                <Stack spacing={0.45} sx={{ mt: 0.4 }}>
                  <Typography className="copy">
                    <strong>WAVE and Challenge Group:</strong>{' '}
                    <a href="https://www.challengegroup.co.uk/" target="_blank" rel="noreferrer">challengegroup.co.uk</a>{' '}
                    /{' '}
                    <a href="https://www.wave-for-change.org.uk/" target="_blank" rel="noreferrer">wave-for-change.org.uk</a>
                  </Typography>
                  <Typography className="copy">
                    For the best baked treats in London, check out CJ&apos;s bakery that Jess&apos;s sister Maia is part of.
                  </Typography>
                  <Typography className="copy">
                    <strong>CJ&apos;s bakery:</strong>{' '}
                    <a href="https://cjsbakery.org.uk/" target="_blank" rel="noreferrer">cjsbakery.org.uk</a>
                  </Typography>
                  <Typography className="copy">
                    We are going to have some amazing art at our wedding from some of our favorite artists with learning disabilities from the UK and US. We will share some of their art with you on the day, but would encourage you to check out{' '}
                    <a href="https://www.artboxlondon.org/" target="_blank" rel="noreferrer">artboxlondon.org</a>.
                  </Typography>
                </Stack>
              </Box>
            </Box>

            <Box className="what-to-do-main-section">
              <Typography className="what-to-do-subsection-title gifts-cause-title gifts-cause-title--2">Refugees and Migrant Rights</Typography>
              <Box className="what-to-do-subsection">
                <Typography className="copy">
                  In both the UK and US we have seen increasing scapegoating of and hatred towards migrants and refugees in recent years. We would not be able to be together if it was not for freedom of movement and we strongly support the rights of refugees and migrants to live and thrive in peace and safety.
                </Typography>
                <Stack spacing={0.45} sx={{ mt: 0.4 }}>
                  <Typography className="copy"><strong>Refugee Action:</strong> <a href="https://www.refugee-action.org.uk/" target="_blank" rel="noreferrer">refugee-action.org.uk</a></Typography>
                  <Typography className="copy"><strong>Care for Calais:</strong> <a href="https://care4calais.org/" target="_blank" rel="noreferrer">care4calais.org</a></Typography>
                  <Typography className="copy"><strong>Immigrant Defenders Law Center (ImmDef) US:</strong> <a href="https://www.immdef.org/" target="_blank" rel="noreferrer">immdef.org</a></Typography>
                  <Typography className="copy">
                    Jess&apos;s mum also works at the incredible <strong>Baobab Centre for Young Survivors in Exile</strong> (
                    <a href="https://www.baobabsurvivors.org/" target="_blank" rel="noreferrer">baobabsurvivors.org</a>
                    ) which supports young survivors of human rights abuses seeking asylum in the UK.
                  </Typography>
                </Stack>
              </Box>
            </Box>

            <Box className="what-to-do-main-section">
              <Typography className="what-to-do-subsection-title gifts-cause-title gifts-cause-title--3">Victims of Genocide and Oppression</Typography>
              <Box className="what-to-do-subsection">
                <Typography className="copy">
                  Unfortunately, our countries continue to be directly and indirectly complicit in occupations, genocides and oppression throughout the world, including in Gaza, Sudan and many many other places.
                </Typography>
                <Typography className="copy">Here are some of the big and small organisations we think do amazing work fighting for justice for the oppressed:</Typography>
                <Stack spacing={0.45} sx={{ mt: 0.4 }}>
                  <Typography className="copy"><strong>Doctors Without Borders:</strong> <a href="https://msf.org.uk/" target="_blank" rel="noreferrer">msf.org.uk</a></Typography>
                  <Typography className="copy"><strong>War Child - Sudan:</strong> <a href="http://warchild.org.uk/our-work/where-we-work/sudan" target="_blank" rel="noreferrer">warchild.org.uk/our-work/where-we-work/sudan</a></Typography>
                  <Typography className="copy"><strong>Medical Aid for Palestinians:</strong> <a href="https://www.map.org.uk/" target="_blank" rel="noreferrer">map.org.uk</a></Typography>
                  <Typography className="copy"><strong>The Hind Rajab Foundation:</strong> <a href="https://www.hindrajabfoundation.org/" target="_blank" rel="noreferrer">hindrajabfoundation.org</a></Typography>
                  <Typography className="copy"><strong>Gaza Sunbirds:</strong> <a href="https://gazasunbirds.org/" target="_blank" rel="noreferrer">gazasunbirds.org</a></Typography>
                  <Typography className="copy"><strong>B&apos;tselem:</strong> <a href="https://www.btselem.org/" target="_blank" rel="noreferrer">btselem.org</a></Typography>
                </Stack>
              </Box>
            </Box>

            <Box className="what-to-do-main-section">
              <Typography className="what-to-do-subsection-title gifts-cause-title gifts-cause-title--4">Human Rights and Upholding International Law</Typography>
              <Box className="what-to-do-subsection">
                <Typography className="copy">
                  Jess works as a human rights lawyer, and so sees each day the protective nature of human rights laws but also their fragility.
                </Typography>
                <Typography className="copy">
                  Two organizations that fight to uphold human rights and keep international law at the forefront of their work are:
                </Typography>
                <Stack spacing={0.45} sx={{ mt: 0.4 }}>
                  <Typography className="copy"><strong>Amnesty International:</strong> <a href="https://www.amnesty.org/en/" target="_blank" rel="noreferrer">amnesty.org/en</a></Typography>
                  <Typography className="copy"><strong>Human Rights Watch:</strong> <a href="https://www.hrw.org/" target="_blank" rel="noreferrer">hrw.org</a></Typography>
                </Stack>
              </Box>
            </Box>

            <Box className="what-to-do-main-section">
              <Typography className="what-to-do-subsection-title gifts-cause-title gifts-cause-title--5">Equality</Typography>
              <Box className="what-to-do-subsection">
                <Typography className="copy">
                  As discrimination and hatred has continued to be fueled on the basis of race, religion, sexuality, and gender, we love the following organizations:
                </Typography>
                <Stack spacing={0.45} sx={{ mt: 0.4 }}>
                  <Typography className="copy">
                    <strong>Smyal:</strong> supports LGBTQ+ young people in DC, affirming and empowering them to thrive:{' '}
                    <a href="https://smyal.org/" target="_blank" rel="noreferrer">smyal.org</a>
                  </Typography>
                  <Typography className="copy">
                    <strong>The Equal Justice Initiative:</strong> works tirelessly to teach about the US&apos;s history and struggles for racial justice:{' '}
                    <a href="https://eji.org/" target="_blank" rel="noreferrer">eji.org</a>
                  </Typography>
                  <Typography className="copy">
                    <strong>Stand Up To Racism:</strong> a hub for coordinating protests and supporting the fight back in the UK:{' '}
                    <a href="https://standuptoracism.org.uk/" target="_blank" rel="noreferrer">standuptoracism.org.uk</a>
                  </Typography>
                </Stack>
              </Box>
            </Box>

            <Box className="what-to-do-main-section">
              <Typography className="what-to-do-subsection-title gifts-cause-title gifts-cause-title--6">Homelessness and Local Outreach</Typography>
              <Box className="what-to-do-subsection">
                <Typography className="copy">
                  These are some of our favorite organizations that support individuals living in poverty and without secure housing:
                </Typography>
                <Stack spacing={0.45} sx={{ mt: 0.4 }}>
                  <Typography className="copy">
                    <strong>Playtime Project</strong> is an amazing DC charity that provides children living in poverty and housing insecurity with an opportunity to be children:{' '}
                    <a href="https://playtimeproject.org/" target="_blank" rel="noreferrer">playtimeproject.org</a>
                  </Typography>
                  <Typography className="copy">
                    <strong>Centrepoint</strong> assists young people around the UK without secure housing:{' '}
                    <a href="https://centrepoint.org.uk/" target="_blank" rel="noreferrer">centrepoint.org.uk</a>
                  </Typography>
                  <Typography className="copy">
                    <strong>Thrive DC</strong> provides holistic support for vulnerable individuals in financial distress to rebuild their lives:{' '}
                    <a href="https://www.thrivedc.org/" target="_blank" rel="noreferrer">thrivedc.org</a>
                  </Typography>
                  <Typography className="copy">
                    <strong>Streetskitchen's </strong> &apos;mantra is solidarity not charity&apos; and provides meals at various hubs for people who are street homeless:{' '}
                    <a href="https://www.streetskitchen.org/" target="_blank" rel="noreferrer">streetskitchen.org</a>
                  </Typography>
                </Stack>
              </Box>
            </Box>
          </Stack>
        </InfoCard>
      </Grid>
    </Grid>
  );
}

function PhotoTriviaGame({
  playerName,
  scoreboard,
  combinedScoreboard = [],
  onSaveScore,
  onViewLeaderboard = () => {},
  showSummaryViewButton = true,
}) {
  const [deck, setDeck] = useState(() => getPhotoTriviaDeck());
  const [currentIndex, setCurrentIndex] = useState(0);
  const [scorePoints, setScorePoints] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [answeredCount, setAnsweredCount] = useState(0);
  const [answerHistory, setAnswerHistory] = useState([]);
  const [isResolving, setIsResolving] = useState(false);
  const [isCardPopping, setIsCardPopping] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isSnappingBack, setIsSnappingBack] = useState(false);
  const [resolveDirection, setResolveDirection] = useState(0);
  const [dragX, setDragX] = useState(0);
  const [hasSavedRun, setHasSavedRun] = useState(false);
  const [isPracticeReplay, setIsPracticeReplay] = useState(false);
  const dragStateRef = useRef({ active: false, startX: 0, pointerId: null });
  const popTimerRef = useRef(null);
  const advanceTimerRef = useRef(null);
  const snapTimerRef = useRef(null);
  const pendingScrollRestoreRef = useRef(null);

  const savedEntry = getScoreEntryForPlayer(scoreboard, playerName);
  const activeCard = deck[currentIndex] ?? null;
  const nextCardOne = deck[currentIndex + 1] ?? null;
  const nextCardTwo = deck[currentIndex + 2] ?? null;
  const isComplete = currentIndex >= deck.length;
  const isScoreLocked = !!savedEntry;
  const canPlayCurrentRun = !isScoreLocked || isPracticeReplay;
  const isLockedView = isScoreLocked && !isPracticeReplay;
  const showCompletionView = isComplete || isLockedView;
  const photoTriviaPointsPerCorrect = useMemo(
    () => Math.ceil(PHOTO_TRIVIA_MAX_SCORE / Math.max(1, deck.length || PHOTO_TRIVIA_BASE_DECK.length)),
    [deck.length]
  );
  const displayScorePoints = isLockedView ? Number(savedEntry?.score ?? 0) : scorePoints;
  const displayCorrectCount = isLockedView
    ? Number(savedEntry?.metadata?.correctCount ?? correctCount)
    : correctCount;
  const answerHistoryForSave = useMemo(
    () => answerHistory.map((result) => ({
      id: result.id,
      guess: result.guess,
      correct: result.correct,
      isCorrect: !!result.isCorrect,
      photoSrc: normalizeGeoPhotoSrc(result.photoSrc),
      alt: result.alt,
      revealTitle: result.revealTitle || '',
      revealDescription: result.revealDescription || '',
    })),
    [answerHistory]
  );
  const displayAnswerHistory = useMemo(() => {
    if (!isLockedView) return answerHistory;
    if (answerHistory.length > 0) return answerHistory;
    const savedAnswerHistory = Array.isArray(savedEntry?.metadata?.answerHistory)
      ? savedEntry.metadata.answerHistory
      : [];
    if (savedAnswerHistory.length > 0) {
      const deckLookup = new Map(deck.map((card) => [card.id, card]));
      return savedAnswerHistory.map((entry, index) => {
        const card = deckLookup.get(entry?.id) || deck[index] || {};
        return {
          id: entry?.id || card.id || `saved-photo-${index}`,
          guess: entry?.guess ?? null,
          correct: entry?.correct ?? card.country ?? null,
          isCorrect: !!entry?.isCorrect,
          photoSrc: normalizeGeoPhotoSrc(entry?.photoSrc || card.photoSrc || ''),
          alt: entry?.alt || card.alt || '',
          revealTitle: entry?.revealTitle || card.revealTitle || '',
          revealDescription: entry?.revealDescription || card.revealDescription || '',
        };
      });
    }
    return deck.map((card) => ({
      id: `${card.id}-saved`,
      guess: null,
      correct: card.country,
      isCorrect: true,
      photoSrc: card.photoSrc,
      alt: card.alt,
      revealTitle: card.revealTitle || '',
      revealDescription: card.revealDescription || '',
    }));
  }, [answerHistory, deck, isLockedView, savedEntry?.metadata?.answerHistory]);

  useLayoutEffect(() => {
    if (pendingScrollRestoreRef.current == null) return;
    const targetY = pendingScrollRestoreRef.current;
    pendingScrollRestoreRef.current = null;
    const currentY = window.scrollY || window.pageYOffset || 0;
    if (Math.abs(currentY - targetY) > 1) {
      window.scrollTo(0, targetY);
    }
  }, [answeredCount, currentIndex, showCompletionView]);

  const resetRound = (options = {}) => {
    if (popTimerRef.current) window.clearTimeout(popTimerRef.current);
    if (advanceTimerRef.current) window.clearTimeout(advanceTimerRef.current);
    if (snapTimerRef.current) window.clearTimeout(snapTimerRef.current);
    setDeck(getPhotoTriviaDeck());
    setCurrentIndex(0);
    setScorePoints(0);
    setCorrectCount(0);
    setAnsweredCount(0);
    setAnswerHistory([]);
    setIsResolving(false);
    setIsCardPopping(false);
    setIsDragging(false);
    setIsSnappingBack(false);
    setResolveDirection(0);
    setDragX(0);
    setHasSavedRun(false);
    setIsPracticeReplay(!!options.practiceReplay);
    dragStateRef.current = { active: false, startX: 0, pointerId: null };
  };

  useEffect(() => {
    resetRound({ practiceReplay: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerName]);

  useEffect(
    () => () => {
      if (popTimerRef.current) window.clearTimeout(popTimerRef.current);
      if (advanceTimerRef.current) window.clearTimeout(advanceTimerRef.current);
      if (snapTimerRef.current) window.clearTimeout(snapTimerRef.current);
    },
    []
  );

  useEffect(() => {
    if (!isComplete || !answeredCount || hasSavedRun || isScoreLocked || isPracticeReplay) return;
    onSaveScore({
      game: 'photo',
      playerName,
      score: scorePoints,
      total: answeredCount,
      rankPoints: scorePoints,
      scoreLabel: `${scorePoints} pts`,
      metadata: {
        correctCount,
        answeredCount,
        pointsPerCorrect: photoTriviaPointsPerCorrect,
        answerHistory: answerHistoryForSave,
      },
    });
    setHasSavedRun(true);
  }, [
    answerHistoryForSave,
    answeredCount,
    correctCount,
    hasSavedRun,
    isComplete,
    isPracticeReplay,
    isScoreLocked,
    onSaveScore,
    playerName,
    photoTriviaPointsPerCorrect,
    scorePoints,
  ]);

  const submitAnswer = (guess) => {
    if (!activeCard || isResolving || !canPlayCurrentRun) return;
    pendingScrollRestoreRef.current = window.scrollY || window.pageYOffset || 0;
    const isCorrect = activeCard.country === guess;
    const swipeDirection = guess === 'UK' ? 1 : -1;
    setIsResolving(true);
    setIsDragging(false);
    setIsSnappingBack(false);
    setResolveDirection(swipeDirection);
    // Let resolving styles apply first, then animate off-screen.
    window.requestAnimationFrame(() => {
      setDragX(swipeDirection * 760);
    });
    const result = {
      id: activeCard.id,
      guess,
      correct: activeCard.country,
      isCorrect,
      photoSrc: activeCard.photoSrc,
      alt: activeCard.alt,
      revealTitle: activeCard.revealTitle || '',
      revealDescription: activeCard.revealDescription || '',
    };
    setAnswerHistory((prev) => [...prev, result]);
    setAnsweredCount((prev) => prev + 1);
    if (isCorrect) {
      setCorrectCount((prev) => prev + 1);
      setScorePoints((prev) => Math.min(PHOTO_TRIVIA_MAX_SCORE, prev + photoTriviaPointsPerCorrect));
    }
    advanceTimerRef.current = window.setTimeout(() => {
      setCurrentIndex((prev) => prev + 1);
      setResolveDirection(0);
      setDragX(0);
      setIsResolving(false);
      setIsCardPopping(true);
      popTimerRef.current = window.setTimeout(() => setIsCardPopping(false), 260);
    }, 260);
  };

  const onCardPointerDown = (event) => {
    if (isResolving || isComplete || !canPlayCurrentRun) return;
    if (event.cancelable) event.preventDefault();
    if (snapTimerRef.current) window.clearTimeout(snapTimerRef.current);
    setIsSnappingBack(false);
    setIsDragging(true);
    dragStateRef.current = { active: true, startX: event.clientX, pointerId: event.pointerId };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onCardPointerMove = (event) => {
    if (!dragStateRef.current.active || dragStateRef.current.pointerId !== event.pointerId) return;
    if (event.cancelable) event.preventDefault();
    setDragX(event.clientX - dragStateRef.current.startX);
  };

  const endDrag = (event) => {
    if (!dragStateRef.current.active || dragStateRef.current.pointerId !== event.pointerId) return;
    if (event.cancelable) event.preventDefault();
    setIsDragging(false);
    dragStateRef.current = { active: false, startX: 0, pointerId: null };
    if (dragX <= -PHOTO_TRIVIA_SWIPE_THRESHOLD) {
      submitAnswer('US');
      return;
    }
    if (dragX >= PHOTO_TRIVIA_SWIPE_THRESHOLD) {
      submitAnswer('UK');
      return;
    }
    setIsSnappingBack(true);
    setDragX(0);
    snapTimerRef.current = window.setTimeout(() => setIsSnappingBack(false), 190);
  };

  const cardRotation = Math.max(-14, Math.min(14, dragX / 12));
  const usOverlayOpacity = Math.max(0, Math.min(1, -dragX / 140));
  const ukOverlayOpacity = Math.max(0, Math.min(1, dragX / 140));
  const formatCountry = (countryCode) => (countryCode === 'UK' ? 'United Kingdom' : 'United States');
  const projectedPhotoScoreboard = useMemo(() => {
    if (!isComplete || answeredCount === 0) return scoreboard;
    return upsertScore(scoreboard, playerName, {
      game: 'photo',
      score: scorePoints,
      total: answeredCount,
      rankPoints: scorePoints,
      scoreLabel: `${scorePoints} pts`,
      metadata: {
        correctCount,
        answeredCount,
        pointsPerCorrect: photoTriviaPointsPerCorrect,
        answerHistory: answerHistoryForSave,
      },
    });
  }, [answerHistoryForSave, answeredCount, correctCount, isComplete, photoTriviaPointsPerCorrect, playerName, scorePoints, scoreboard]);
  const projectedCombinedScoreboard = useMemo(() => {
    if (!isComplete || answeredCount === 0) return combinedScoreboard;
    return upsertCombinedLeaderboardRow(combinedScoreboard, playerName, {
      photoPoints: scorePoints,
    });
  }, [answeredCount, combinedScoreboard, isComplete, playerName, scorePoints]);
  const photoPlace = useMemo(
    () => getRankPositionForPlayer(projectedPhotoScoreboard, playerName),
    [playerName, projectedPhotoScoreboard]
  );
  const overallPlace = useMemo(
    () => getRankPositionForPlayer(projectedCombinedScoreboard, playerName),
    [playerName, projectedCombinedScoreboard]
  );

  return (
    <Stack spacing={1.2}>
      <Box className="game-intro">
        <Typography className="travel-heading">GeoGuesser</Typography>
        <Typography className="copy">
          Swipe left if this photo was taken in the <strong>United States</strong> and right if it was in the <strong>United Kingdom</strong>.
        </Typography>
      </Box>

      {isScoreLocked && !isPracticeReplay && !showCompletionView && (
        <Box className="game-lock-banner">
          <Box className="game-panel-header-row">
            <Typography className="travel-heading game-panel-header-title">GeoGuesser leaderboard</Typography>
            <Button
              size="small"
              className="guest-continue-button game-replay-top-button"
              onClick={() => resetRound({ practiceReplay: true })}
            >
              Play Again
            </Button>
          </Box>
          <Typography className="copy">
            You scored <strong>{savedEntry?.scoreLabel || `${savedEntry?.score ?? 0} pts`}</strong> in GeoGuesser.
          </Typography>
          {(photoPlace || overallPlace) && (
            <Typography className="copy">
              You&apos;re currently
              {photoPlace ? <> <strong>#{photoPlace}</strong> in GeoGuesser</> : ''}
              {photoPlace && overallPlace ? ' and' : ''}
              {overallPlace ? <> <strong>#{overallPlace}</strong> overall</> : ''}.
            </Typography>
          )}
          <Box sx={{ mt: 1 }}>
            <CombinedLeaderboardSummary
              rows={projectedCombinedScoreboard}
              playerName={playerName}
              title="GeoGuesser Leaderboard"
              gameRows={projectedPhotoScoreboard}
              gameId="photo"
              showViewButton={showSummaryViewButton}
              onViewFullLeaderboard={onViewLeaderboard}
            />
          </Box>
          {showSummaryViewButton && (
            <Box sx={{ mt: 1 }}>
              <Stack direction="row" spacing={1} className="game-post-actions">
                <Button className="guest-continue-button game-post-actions-view" onClick={onViewLeaderboard}>
                  View Full Leaderboard
                </Button>
              </Stack>
            </Box>
          )}
        </Box>
      )}

      {(canPlayCurrentRun || !!savedEntry) && (
        <Box className="photo-trivia-module-shell">
          <Box className="photo-trivia-playfield">
            {!showCompletionView && activeCard && canPlayCurrentRun && (
              <Box className="photo-trivia-stack">
                {nextCardTwo ? (
                  <Box className="photo-trivia-card-back photo-trivia-card-back--two">
                    <Box
                      component="img"
                      className="photo-trivia-card-back-image"
                      src={nextCardTwo.photoSrc}
                      alt={nextCardTwo.alt || 'Upcoming photo'}
                      draggable={false}
                    />
                  </Box>
                ) : (
                  <Box className="photo-trivia-card-back photo-trivia-card-back--two" />
                )}
                {nextCardOne ? (
                  <Box className="photo-trivia-card-back photo-trivia-card-back--one">
                    <Box
                      component="img"
                      className="photo-trivia-card-back-image"
                      src={nextCardOne.photoSrc}
                      alt={nextCardOne.alt || 'Upcoming photo'}
                      draggable={false}
                    />
                  </Box>
                ) : (
                  <Box className="photo-trivia-card-back photo-trivia-card-back--one" />
                )}
                <Box className={`photo-trivia-card-front ${isCardPopping ? 'photo-trivia-card-front--pop' : ''}`}>
                  <Box
                    key={`photo-trivia-card-${activeCard.id}`}
                    className={[
                      'photo-trivia-card',
                      isResolving ? 'photo-trivia-card--resolving' : '',
                      isDragging ? 'photo-trivia-card--dragging' : '',
                      isSnappingBack ? 'photo-trivia-card--snapping' : '',
                    ].join(' ').trim()}
                    onPointerDown={onCardPointerDown}
                    onPointerMove={onCardPointerMove}
                    onPointerUp={endDrag}
                    onPointerCancel={endDrag}
                    style={{
                      transform: `translate3d(${dragX}px, 0, 0) rotate(${isResolving ? resolveDirection * 16 : cardRotation}deg)`,
                    }}
                  >
                    <Box
                      component="img"
                      className="photo-trivia-card-image"
                      src={activeCard.photoSrc}
                      alt={activeCard.alt}
                      draggable={false}
                    />
                    <Box className="photo-trivia-overlay photo-trivia-overlay--us" sx={{ opacity: usOverlayOpacity }}>
                      US
                    </Box>
                    <Box className="photo-trivia-overlay photo-trivia-overlay--uk" sx={{ opacity: ukOverlayOpacity }}>
                      UK
                    </Box>
                    <Box className="photo-trivia-card-footer">
                      <Typography className="photo-trivia-card-count">Photo {currentIndex + 1} of {deck.length}</Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}

            {showCompletionView && (
              <Box className="photo-trivia-finish-card">
                <Box className="game-panel-header-row">
                  <Typography className="travel-heading game-panel-header-title">Photo trivia complete</Typography>
                  <Button
                    size="small"
                    className="guest-continue-button game-replay-top-button"
                    onClick={() => resetRound({ practiceReplay: !!savedEntry })}
                  >
                    Play Again
                  </Button>
                </Box>
                <Typography className="copy">
                  You scored <strong>{displayScorePoints} points</strong> ({displayCorrectCount} correct).
                </Typography>
                {(photoPlace || overallPlace) && (
                  <Typography className="copy">
                    You&apos;re currently
                    {photoPlace ? <> <strong>#{photoPlace}</strong> in GeoGuesser</> : ''}
                    {photoPlace && overallPlace ? ' and' : ''}
                    {overallPlace ? <> <strong>#{overallPlace}</strong> overall</> : ''}.
                  </Typography>
                )}
                {!(isPracticeReplay || isScoreLocked) && (
                  <Typography className="copy">
                    Score saved to the leaderboard.
                  </Typography>
                )}
                <Box className="photo-trivia-results-grid">
                  {displayAnswerHistory.map((result) => (
                    <Box
                      key={`photo-result-${result.id}`}
                      className={[
                        'photo-trivia-polaroid',
                        result.isCorrect ? 'photo-trivia-polaroid--correct' : 'photo-trivia-polaroid--wrong',
                      ].join(' ').trim()}
                    >
                      <Box
                        component="img"
                        className="photo-trivia-polaroid-image"
                        src={result.photoSrc}
                        alt={result.alt || 'Answer reveal photo'}
                        loading="lazy"
                        draggable={false}
                      />
                      <Typography className="photo-trivia-polaroid-caption">
                        {result.revealTitle || result.alt || formatCountry(result.correct)}
                      </Typography>
                      <Typography
                        className={[
                          'photo-trivia-polaroid-outcome',
                          result.isCorrect ? 'photo-trivia-polaroid-outcome--correct' : 'photo-trivia-polaroid-outcome--wrong',
                        ].join(' ').trim()}
                      >
                        {result.isCorrect ? 'Correct' : 'Missed'}
                      </Typography>
                      {!!result.revealDescription && (
                        <Typography className="copy photo-trivia-polaroid-description">{result.revealDescription}</Typography>
                      )}
                      {!result.revealDescription && (
                        <Typography className="copy photo-trivia-polaroid-description">No description provided.</Typography>
                      )}
                    </Box>
                  ))}
                </Box>
                <CombinedLeaderboardSummary
                  rows={projectedCombinedScoreboard}
                  playerName={playerName}
                  title="GeoGuesser Leaderboard"
                  gameRows={projectedPhotoScoreboard}
                  gameId="photo"
                  showViewButton={showSummaryViewButton}
                  onViewFullLeaderboard={onViewLeaderboard}
                />
                {showSummaryViewButton && (
                  <Stack direction="row" spacing={1} className="game-post-actions">
                    <Button className="guest-continue-button game-post-actions-view" onClick={onViewLeaderboard}>
                      View Full Leaderboard
                    </Button>
                  </Stack>
                )}
              </Box>
            )}
          </Box>

          {!showCompletionView && (
            <Stack direction="row" spacing={1} className="photo-trivia-controls">
              <Button
                variant="outlined"
                className="photo-trivia-choice photo-trivia-choice--us"
                disabled={!activeCard || isResolving || !canPlayCurrentRun}
                onClick={() => submitAnswer('US')}
              >
                🇺🇸 USA
              </Button>
              <Button
                variant="outlined"
                className="photo-trivia-choice photo-trivia-choice--uk"
                disabled={!activeCard || isResolving || !canPlayCurrentRun}
                onClick={() => submitAnswer('UK')}
              >
                🇬🇧 UK
              </Button>
            </Stack>
          )}

        </Box>
      )}
    </Stack>
  );
}

// eslint-disable-next-line no-unused-vars
function JeopardyGame({
  playerName,
  scoreboard,
  combinedScoreboard = [],
  onSaveScore,
  onViewLeaderboard = () => {},
  showSummaryViewButton = true,
}) {
  const [usedClues, setUsedClues] = useState([]);
  const [activeClue, setActiveClue] = useState(null);
  const [screenMode, setScreenMode] = useState('board');
  const [currencyMode, setCurrencyMode] = useState('USD');
  const [baseUsdScore, setBaseUsdScore] = useState(0);
  const [hasSavedRun, setHasSavedRun] = useState(false);
  const [isPracticeReplay, setIsPracticeReplay] = useState(false);
  const [cluePhotoVisible, setCluePhotoVisible] = useState(true);
  const [finalBetAmount, setFinalBetAmount] = useState('0');
  const [finalSelectedChoiceIndex, setFinalSelectedChoiceIndex] = useState(null);
  const [finalResolved, setFinalResolved] = useState(false);
  const [finalWasCorrect, setFinalWasCorrect] = useState(false);
  const [finalPhotoVisible, setFinalPhotoVisible] = useState(true);

  const savedEntry = getScoreEntryForPlayer(scoreboard, playerName);
  const isScoreLocked = !!savedEntry;
  const canPlayCurrentRun = !isScoreLocked || isPracticeReplay;
  const totalClueCount = JEOPARDY_BOARD_TEMPLATE.reduce((sum, column) => sum + column.clues.length, 0);
  const allUsed = usedClues.length === totalClueCount;
  const shouldSkipFinalJeopardy = FINAL_JEOPARDY_ENABLED && allUsed && baseUsdScore < 0;
  const isGameComplete = allUsed && (!FINAL_JEOPARDY_ENABLED || finalResolved || shouldSkipFinalJeopardy);

  const toDisplayAmount = (usdAmount) => (
    currencyMode === 'GBP' ? Math.round(usdAmount * USD_TO_GBP_RATE) : usdAmount
  );
  const formatDisplayCurrency = (usdAmount) => formatSignedCurrencyAmount(currencyMode, toDisplayAmount(usdAmount));
  const formatValueLabel = (usdAmount) => formatCurrencyAmount(currencyMode, toDisplayAmount(usdAmount));
  const maxFinalBetDisplay = Math.max(0, toDisplayAmount(baseUsdScore));
  const finalBetDisplayValue = Math.max(0, Math.trunc(Number(finalBetAmount) || 0));

  const getProjectedUsdDeltaFromDisplayBet = (displayBet, wasCorrect) => {
    const signedDisplayDelta = wasCorrect ? displayBet : -displayBet;
    return currencyMode === 'GBP'
      ? Math.round(signedDisplayDelta / USD_TO_GBP_RATE)
      : signedDisplayDelta;
  };

  const getProjectedJeopardyRank = (projectedBaseUsdScore) => {
    const normalized = (playerName || '').trim().toLowerCase();
    const rows = scoreboard
      .filter((entry) => (entry.name || '').trim().toLowerCase() !== normalized)
      .map((entry) => ({
        name: entry.name,
        rankPoints: entry.rankPoints ?? entry.score ?? 0,
        updatedAt: entry.updatedAt ?? 0,
      }));
    rows.push({
      name: playerName,
      rankPoints: projectedBaseUsdScore,
      updatedAt: Date.now(),
    });
    rows.sort((a, b) => (b.rankPoints - a.rankPoints) || (b.updatedAt - a.updatedAt));
    return rows.findIndex((row) => row.name === playerName && row.rankPoints === projectedBaseUsdScore) + 1;
  };

  const projectedFinalCorrectUsd = baseUsdScore + getProjectedUsdDeltaFromDisplayBet(Math.min(finalBetDisplayValue, maxFinalBetDisplay), true);
  const projectedFinalWrongUsd = baseUsdScore + getProjectedUsdDeltaFromDisplayBet(Math.min(finalBetDisplayValue, maxFinalBetDisplay), false);
  const projectedJeopardyScoreboard = useMemo(() => {
    if (!isGameComplete) return scoreboard;
    return upsertScore(scoreboard, playerName, {
      game: 'jeopardy',
      score: baseUsdScore,
      total: totalClueCount + (FINAL_JEOPARDY_ENABLED ? 1 : 0),
      rankPoints: getJeopardyRankPoints({ baseUsdScore }),
      scoreLabel: `${formatSignedCurrencyAmount('USD', baseUsdScore)} (${formatSignedCurrencyAmount('GBP', Math.round(baseUsdScore * USD_TO_GBP_RATE))})`,
      metadata: {
        baseUsdScore,
        displayCurrency: currencyMode,
        displayScore: currencyMode === 'GBP' ? Math.round(baseUsdScore * USD_TO_GBP_RATE) : baseUsdScore,
        conversionRate: USD_TO_GBP_RATE,
      },
    });
  }, [baseUsdScore, currencyMode, isGameComplete, playerName, scoreboard, totalClueCount]);
  const projectedCombinedScoreboard = useMemo(() => {
    if (!isGameComplete) return combinedScoreboard;
    return upsertCombinedLeaderboardRow(combinedScoreboard, playerName, {
      jeopardyBaseUsd: baseUsdScore,
    });
  }, [baseUsdScore, combinedScoreboard, isGameComplete, playerName]);
  const jeopardyPlace = useMemo(
    () => getRankPositionForPlayer(projectedJeopardyScoreboard, playerName),
    [playerName, projectedJeopardyScoreboard]
  );
  const overallPlace = useMemo(
    () => getRankPositionForPlayer(projectedCombinedScoreboard, playerName),
    [playerName, projectedCombinedScoreboard]
  );
  const displaySavedJeopardyScore = savedEntry?.scoreLabel || `${formatSignedCurrencyAmount('USD', savedEntry?.score ?? 0)}`;
  const isLockedView = isScoreLocked && !isPracticeReplay;
  const allClueIds = useMemo(
    () => JEOPARDY_BOARD_TEMPLATE.flatMap((column) => column.clues.map((clue) => clue.id)),
    []
  );
  const renderedUsedClues = isLockedView ? allClueIds : usedClues;
  const renderedAllUsed = renderedUsedClues.length === totalClueCount;
  const displayedBaseUsdScore = isLockedView ? Number(savedEntry?.score ?? 0) : baseUsdScore;
  const displayedIsGameComplete = isGameComplete || isLockedView;
  const displayedJeopardyScoreboard = isLockedView ? scoreboard : projectedJeopardyScoreboard;
  const displayedCombinedScoreboard = isLockedView ? combinedScoreboard : projectedCombinedScoreboard;

  const resetJeopardyRun = (options = {}) => {
    setUsedClues([]);
    setActiveClue(null);
    setScreenMode('board');
    setBaseUsdScore(0);
    setHasSavedRun(false);
    setIsPracticeReplay(!!options.practiceReplay);
    setCluePhotoVisible(true);
    setFinalBetAmount('0');
    setFinalSelectedChoiceIndex(null);
    setFinalResolved(false);
    setFinalWasCorrect(false);
    setFinalPhotoVisible(true);
  };

  useEffect(() => {
    resetJeopardyRun({ practiceReplay: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerName]);

  useEffect(() => {
    if (!isGameComplete || hasSavedRun || isScoreLocked || isPracticeReplay) return;
    onSaveScore({
      game: 'jeopardy',
      playerName,
      score: baseUsdScore,
      total: totalClueCount + (FINAL_JEOPARDY_ENABLED ? 1 : 0),
      rankPoints: getJeopardyRankPoints({ baseUsdScore }),
      scoreLabel: `${formatSignedCurrencyAmount('USD', baseUsdScore)} (${formatSignedCurrencyAmount('GBP', Math.round(baseUsdScore * USD_TO_GBP_RATE))})`,
      metadata: {
        baseUsdScore,
        displayCurrency: currencyMode,
        displayScore: currencyMode === 'GBP' ? Math.round(baseUsdScore * USD_TO_GBP_RATE) : baseUsdScore,
        conversionRate: USD_TO_GBP_RATE,
      },
    });
    setHasSavedRun(true);
  }, [baseUsdScore, currencyMode, hasSavedRun, isGameComplete, isPracticeReplay, isScoreLocked, onSaveScore, playerName, totalClueCount]);

  useEffect(() => {
    if (!FINAL_JEOPARDY_ENABLED) return;
    if (allUsed && !finalResolved && !activeClue && screenMode === 'board') {
      setScreenMode('final');
    }
  }, [activeClue, allUsed, finalResolved, screenMode]);

  useEffect(() => {
    if (!shouldSkipFinalJeopardy) return;
    setFinalResolved(false);
  }, [shouldSkipFinalJeopardy]);

  const openClue = (categoryIndex, rowIndex) => {
    if (!canPlayCurrentRun || renderedAllUsed) return;
    const clue = JEOPARDY_BOARD_TEMPLATE[categoryIndex]?.clues?.[rowIndex];
    if (!clue || renderedUsedClues.includes(clue.id)) return;
    setActiveClue({
      ...clue,
      selectedChoiceIndex: null,
      selectedChoiceIndices: [],
      resolved: false,
      wasCorrect: false,
    });
    setCluePhotoVisible(true);
    setScreenMode('clue');
  };

  const toggleMultiSelectChoice = (choiceIndex) => {
    setActiveClue((prev) => {
      if (!prev || prev.resolved || prev.questionType !== 'multi') return prev;
      const exists = (prev.selectedChoiceIndices || []).includes(choiceIndex);
      return {
        ...prev,
        selectedChoiceIndices: exists
          ? prev.selectedChoiceIndices.filter((idx) => idx !== choiceIndex)
          : [...prev.selectedChoiceIndices, choiceIndex].sort((a, b) => a - b),
      };
    });
  };

  const submitActiveClueAnswer = (choiceIndex) => {
    if (!activeClue || activeClue.resolved || !canPlayCurrentRun) return;

    let wasCorrect = false;
    const nextSelectedChoiceIndex = choiceIndex ?? null;
    const nextSelectedChoiceIndices = activeClue.selectedChoiceIndices || [];

    if (activeClue.questionType === 'multi') {
      const picked = [...nextSelectedChoiceIndices].sort((a, b) => a - b);
      const expected = [...(activeClue.correctIndices || [])].sort((a, b) => a - b);
      wasCorrect = picked.length === expected.length && picked.every((value, idx) => value === expected[idx]);
    } else {
      const correctIndex = activeClue.correctIndices?.[0] ?? 0;
      wasCorrect = choiceIndex === correctIndex;
    }

    const deltaUsd = wasCorrect ? activeClue.usdValue : -activeClue.usdValue;
    setBaseUsdScore((prev) => prev + deltaUsd);
    setUsedClues((prev) => [...prev, activeClue.id]);
    setActiveClue((prev) => (prev ? {
      ...prev,
      selectedChoiceIndex: nextSelectedChoiceIndex,
      selectedChoiceIndices: nextSelectedChoiceIndices,
      resolved: true,
      wasCorrect,
    } : prev));
  };

  const closeClueScreen = () => {
    setActiveClue(null);
    setScreenMode('board');
  };

  const skipActiveClue = () => {
    if (!activeClue || activeClue.resolved) {
      closeClueScreen();
      return;
    }
    setUsedClues((prev) => (prev.includes(activeClue.id) ? prev : [...prev, activeClue.id]));
    setActiveClue(null);
    setScreenMode('board');
  };

  const submitFinalJeopardy = () => {
    if (!canPlayCurrentRun || finalResolved || finalSelectedChoiceIndex == null) return;
    const cappedBet = Math.min(finalBetDisplayValue, maxFinalBetDisplay);
    const wasCorrect = finalSelectedChoiceIndex === (FINAL_JEOPARDY_CLUE.correctIndices?.[0] ?? 0);
    const deltaUsd = getProjectedUsdDeltaFromDisplayBet(cappedBet, wasCorrect);

    setBaseUsdScore((prev) => prev + deltaUsd);
    setFinalBetAmount(String(cappedBet));
    setFinalResolved(true);
    setFinalWasCorrect(wasCorrect);
  };

  return (
    <Stack spacing={1.2}>
      <Box className="jeopardy-intro">
        <Typography className="travel-heading">Let&apos;s play Jeopardy!</Typography>
        <Typography className="copy">
          Pick a dollar tile, answer the clue, and build the highest total score.
        </Typography>
      </Box>

      {isScoreLocked && !isPracticeReplay && !displayedIsGameComplete && (
        <Box className="game-lock-banner">
          <Box className="game-panel-header-row">
            <Typography className="travel-heading game-panel-header-title">Jeopardy leaderboard</Typography>
            <Button
              size="small"
              className="guest-continue-button game-replay-top-button"
              onClick={() => resetJeopardyRun({ practiceReplay: true })}
            >
              Play Again
            </Button>
          </Box>
          <Typography className="copy">
            You scored <strong>{displaySavedJeopardyScore}</strong> in Hard.
          </Typography>
          {(jeopardyPlace || overallPlace) && (
            <Typography className="copy">
              You&apos;re currently
              {jeopardyPlace ? <> <strong>#{jeopardyPlace}</strong> in Hard</> : ''}
              {jeopardyPlace && overallPlace ? ' and' : ''}
              {overallPlace ? <> <strong>#{overallPlace}</strong> overall</> : ''}.
            </Typography>
          )}
          <Box sx={{ mt: 1 }}>
            <CombinedLeaderboardSummary
              rows={displayedCombinedScoreboard}
              playerName={playerName}
              title="Jeopardy Leaderboard"
              gameRows={displayedJeopardyScoreboard}
              gameId="jeopardy"
              showViewButton={showSummaryViewButton}
              onViewFullLeaderboard={onViewLeaderboard}
            />
          </Box>
          {showSummaryViewButton && (
            <Box sx={{ mt: 1 }}>
              <Stack direction="row" spacing={1} className="game-post-actions">
                <Button className="guest-continue-button game-post-actions-view" onClick={onViewLeaderboard}>
                  View Full Leaderboard
                </Button>
              </Stack>
            </Box>
          )}
        </Box>
      )}

      {(canPlayCurrentRun || !!savedEntry) && (
        <>
          <Box className="jeopardy-tv-shell">
            <Box className="jeopardy-tv-bezel">
              <Box className="jeopardy-tv-screen">
                <Box className="jeopardy-tv-hud">
                  <Box className="jeopardy-tv-hud-score">
                    <Box className="jeopardy-tv-hud-total">
                      <Typography className="photo-trivia-stat-label">Total</Typography>
                      <Typography className="jeopardy-tv-hud-score-value">{formatDisplayCurrency(displayedBaseUsdScore)}</Typography>
                    </Box>
                    <Box className="jeopardy-currency-float">
                      <Button
                        size="small"
                        variant={currencyMode === 'USD' ? 'contained' : 'outlined'}
                        className={`jeopardy-currency-float-button ${currencyMode === 'USD' ? 'jeopardy-currency-float-button--active' : ''}`}
                        onClick={() => setCurrencyMode('USD')}
                      >
                        USD
                      </Button>
                      <Button
                        size="small"
                        variant={currencyMode === 'GBP' ? 'contained' : 'outlined'}
                        className={`jeopardy-currency-float-button ${currencyMode === 'GBP' ? 'jeopardy-currency-float-button--active' : ''}`}
                        onClick={() => setCurrencyMode('GBP')}
                      >
                        GBP
                      </Button>
                    </Box>
                  </Box>
                </Box>

                {screenMode === 'board' && (
                  <Box className="jeopardy-tv-content">
                    <Box className="jeopardy-board">
                      {JEOPARDY_BOARD_TEMPLATE.map((column, categoryIndex) => (
                        <Box key={column.category} className="jeopardy-column">
                          <Box className="jeopardy-category-cell">
                            <Typography className="jeopardy-category-text">{column.category}</Typography>
                          </Box>
                          {column.clues.map((clue, rowIndex) => {
                            const used = renderedUsedClues.includes(clue.id);
                            return (
                              <Button
                                key={clue.id}
                                className={`jeopardy-value-cell ${used ? 'jeopardy-value-cell--used' : ''}`}
                                onClick={() => openClue(categoryIndex, rowIndex)}
                                disabled={used || !canPlayCurrentRun || renderedAllUsed}
                              >
                                {used ? 'DONE' : formatValueLabel(clue.usdValue)}
                              </Button>
                            );
                          })}
                        </Box>
                      ))}
                    </Box>

                    {FINAL_JEOPARDY_ENABLED && renderedAllUsed && !finalResolved && !shouldSkipFinalJeopardy && (
                      <Box className="photo-trivia-feedback photo-trivia-feedback--correct">
                        <Typography className="copy">All main board clues are complete. Final Jeopardy is next.</Typography>
                        <Box sx={{ mt: 1 }}>
                          <Button className="guest-continue-button" onClick={() => setScreenMode('final')}>
                            Go to Final Jeopardy
                          </Button>
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}

                {screenMode === 'clue' && activeClue && (
                  <Box className="jeopardy-tv-content jeopardy-tv-content--clue">
                    {!activeClue.resolved && (
                      <>
                        <Typography className="jeopardy-question-value-label">
                          Question for: {formatValueLabel(activeClue.usdValue)}
                        </Typography>
                        <Typography className="jeopardy-tv-question">{activeClue.prompt}</Typography>

                        <Stack spacing={1} className="jeopardy-choice-list">
                          {activeClue.options.map((option, index) => {
                            const isMulti = activeClue.questionType === 'multi';
                            const selectedSingle = activeClue.selectedChoiceIndex === index;
                            const selectedMulti = (activeClue.selectedChoiceIndices || []).includes(index);
                            const isSelected = isMulti ? selectedMulti : selectedSingle;
                            return (
                              <Button
                                key={`${activeClue.id}-${option}`}
                                className={[
                                  'jeopardy-choice-button',
                                  isSelected ? 'jeopardy-choice-button--selected' : '',
                                ].join(' ').trim()}
                                disabled={!canPlayCurrentRun || activeClue.resolved}
                                onClick={() => (isMulti ? toggleMultiSelectChoice(index) : submitActiveClueAnswer(index))}
                              >
                                {isMulti ? (isSelected ? '[x] ' : '[ ] ') : ''}{String.fromCharCode(65 + index)}. {option}
                              </Button>
                            );
                          })}
                        </Stack>

                        {activeClue.questionType === 'multi' && (
                          <Stack direction="row" spacing={1} className="jeopardy-clue-actions">
                            <Button
                              className="guest-continue-button"
                              onClick={() => submitActiveClueAnswer(null)}
                              disabled={!canPlayCurrentRun || (activeClue.selectedChoiceIndices || []).length === 0}
                            >
                              Submit Selections
                            </Button>
                          </Stack>
                        )}

                        <Stack direction="row" spacing={1} className="jeopardy-clue-actions">
                          <Button variant="outlined" className="jeopardy-choice-button jeopardy-skip-button" onClick={skipActiveClue}>
                            Skip (No Score)
                          </Button>
                        </Stack>
                      </>
                    )}

                    {activeClue.resolved && (
                      <Box className="jeopardy-answer-reveal">
                        <Typography className="jeopardy-tv-title">Correct Answer</Typography>
                        <Typography className="jeopardy-answer-title">{activeClue.answer}</Typography>

                        <Box className="jeopardy-result-photo-wrap">
                          {activeClue.resultPhotoSrc && cluePhotoVisible ? (
                            <Box
                              component="img"
                              src={activeClue.resultPhotoSrc}
                              alt={activeClue.resultPhotoAlt || activeClue.answer}
                              className="jeopardy-result-photo"
                              onError={() => setCluePhotoVisible(false)}
                            />
                          ) : (
                            <Box className="jeopardy-result-photo-placeholder">
                              <Typography className="game-leaderboard-subcopy">Add a reveal photo for this clue</Typography>
                            </Box>
                          )}
                        </Box>

                        <Typography className="jeopardy-answer-description">
                          {activeClue.resultDescription || 'Add a short description for this answer.'}
                        </Typography>

                        <Button className="guest-continue-button jeopardy-continue-button" onClick={closeClueScreen}>
                          Continue
                        </Button>
                      </Box>
                    )}
                  </Box>
                )}

                {FINAL_JEOPARDY_ENABLED && screenMode === 'final' && (
                  <Box className="jeopardy-tv-content jeopardy-tv-content--clue">
                    {shouldSkipFinalJeopardy && (
                      <Box className="jeopardy-answer-reveal">
                        <Typography className="jeopardy-tv-title">Final Jeopardy Skipped</Typography>
                        <Typography className="jeopardy-answer-title">Score is below $0</Typography>
                        <Box className="jeopardy-result-photo-wrap">
                          <Box className="jeopardy-result-photo-placeholder">
                            <Typography className="game-leaderboard-subcopy">
                              Final Jeopardy is only available with a non-negative score.
                            </Typography>
                          </Box>
                        </Box>
                        <Typography className="jeopardy-answer-description">
                          You can still finish the game and save your Jeopardy score.
                        </Typography>
                        <Button className="guest-continue-button jeopardy-continue-button" onClick={() => setScreenMode('board')}>
                          Continue
                        </Button>
                      </Box>
                    )}

                    {!finalResolved && !shouldSkipFinalJeopardy && (
                      <>
                        <Typography className="jeopardy-tv-title">Final Jeopardy</Typography>
                        <Typography className="jeopardy-tv-question">{FINAL_JEOPARDY_CLUE.prompt}</Typography>

                        <TextField
                          type="number"
                          label={`Bet (${currencyMode === 'GBP' ? '£' : '$'})`}
                          value={finalBetAmount}
                          onChange={(event) => setFinalBetAmount(event.target.value)}
                          inputProps={{ min: 0, max: maxFinalBetDisplay, step: 1 }}
                          className="guest-search-input jeopardy-final-bet-input"
                        />
                        <Typography className="game-leaderboard-subcopy jeopardy-final-bet-help">
                          Max bet: {formatCurrencyAmount(currencyMode, maxFinalBetDisplay)}
                        </Typography>
                        <Typography className="game-leaderboard-subcopy jeopardy-final-bet-help">
                          If correct: rank #{getProjectedJeopardyRank(projectedFinalCorrectUsd)} · If incorrect: rank #{getProjectedJeopardyRank(projectedFinalWrongUsd)}
                        </Typography>

                        <Stack spacing={1} className="jeopardy-choice-list">
                          {FINAL_JEOPARDY_CLUE.options.map((option, index) => (
                            <Button
                              key={`final-${option}`}
                              className={[
                                'jeopardy-choice-button',
                                finalSelectedChoiceIndex === index ? 'jeopardy-choice-button--selected' : '',
                              ].join(' ').trim()}
                              disabled={!canPlayCurrentRun || finalResolved}
                              onClick={() => setFinalSelectedChoiceIndex(index)}
                            >
                              {String.fromCharCode(65 + index)}. {option}
                            </Button>
                          ))}
                        </Stack>

                        <Stack direction="row" spacing={1} className="jeopardy-clue-actions">
                          <Button
                            className="guest-continue-button"
                            onClick={submitFinalJeopardy}
                            disabled={
                              finalSelectedChoiceIndex == null ||
                              finalBetDisplayValue > maxFinalBetDisplay
                            }
                          >
                            Submit Final Jeopardy
                          </Button>
                        </Stack>
                      </>
                    )}

                    {finalResolved && !shouldSkipFinalJeopardy && (
                      <Box className="jeopardy-answer-reveal">
                        <Typography className="jeopardy-tv-title">Correct Answer</Typography>
                        <Typography className="jeopardy-answer-title">{FINAL_JEOPARDY_CLUE.answer}</Typography>

                        <Box className="jeopardy-result-photo-wrap">
                          {FINAL_JEOPARDY_CLUE.resultPhotoSrc && finalPhotoVisible ? (
                            <Box
                              component="img"
                              src={FINAL_JEOPARDY_CLUE.resultPhotoSrc}
                              alt={FINAL_JEOPARDY_CLUE.resultPhotoAlt || FINAL_JEOPARDY_CLUE.answer}
                              className="jeopardy-result-photo"
                              onError={() => setFinalPhotoVisible(false)}
                            />
                          ) : (
                            <Box className="jeopardy-result-photo-placeholder">
                              <Typography className="game-leaderboard-subcopy">Add a reveal photo for final Jeopardy</Typography>
                            </Box>
                          )}
                        </Box>

                        <Typography className="jeopardy-answer-description">
                          {FINAL_JEOPARDY_CLUE.resultDescription || 'Add a short description for this answer.'}
                        </Typography>
                        <Typography className="game-leaderboard-subcopy jeopardy-final-bet-help">
                          {finalWasCorrect ? 'Correct' : 'Incorrect'} · Bet: {formatCurrencyAmount(currencyMode, finalBetDisplayValue)}
                        </Typography>

                        <Button className="guest-continue-button jeopardy-continue-button" onClick={() => setScreenMode('board')}>
                          Continue
                        </Button>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          </Box>

          {displayedIsGameComplete && (
            <Box className="photo-trivia-feedback photo-trivia-feedback--correct">
              <Box className="game-panel-header-row">
                <Typography className="travel-heading game-panel-header-title">Jeopardy complete</Typography>
                <Button
                  size="small"
                  className="guest-continue-button game-replay-top-button"
                  onClick={() => resetJeopardyRun({ practiceReplay: !!savedEntry })}
                >
                  Play Again
                </Button>
              </Box>
              <Typography className="copy">
                You scored <strong>{formatSignedCurrencyAmount('USD', displayedBaseUsdScore)}</strong> in Hard.
              </Typography>
              {(jeopardyPlace || overallPlace) && (
                <Typography className="copy">
                  You&apos;re currently
                  {jeopardyPlace ? <> <strong>#{jeopardyPlace}</strong> in Hard</> : ''}
                  {jeopardyPlace && overallPlace ? ' and' : ''}
                  {overallPlace ? <> <strong>#{overallPlace}</strong> overall</> : ''}.
                </Typography>
              )}
              {!(isPracticeReplay || isScoreLocked) && (
                <Typography className="copy">
                  Score saved to the leaderboard.
                </Typography>
              )}
              <Box sx={{ mt: 1 }}>
                <CombinedLeaderboardSummary
                  rows={displayedCombinedScoreboard}
                  playerName={playerName}
                  title="Jeopardy Leaderboard"
                  gameRows={displayedJeopardyScoreboard}
                  gameId="jeopardy"
                  showViewButton={showSummaryViewButton}
                  onViewFullLeaderboard={onViewLeaderboard}
                />
              </Box>
              {showSummaryViewButton && (
                <Box sx={{ mt: 1 }}>
                  <Stack direction="row" spacing={1} className="game-post-actions">
                    <Button className="guest-continue-button game-post-actions-view" onClick={onViewLeaderboard}>
                      View Full Leaderboard
                    </Button>
                  </Stack>
                </Box>
              )}
            </Box>
          )}
        </>
      )}
    </Stack>
  );
}

function PhotoPuzzleGame({
  playerName,
  scoreboard,
  combinedScoreboard = [],
  onSaveScore,
  onViewLeaderboard = () => {},
  showSummaryViewButton = true,
}) {
  const totalPieces = PHOTO_PUZZLE_GRID_SIZE * PHOTO_PUZZLE_GRID_SIZE;
  const solvedOrder = useMemo(() => Array.from({ length: totalPieces }, (_, index) => index), [totalPieces]);
  const [pieceOrder, setPieceOrder] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [dragState, setDragState] = useState({
    active: false,
    index: null,
    pointerId: null,
    startX: 0,
    startY: 0,
    currentX: 0,
    currentY: 0,
  });
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [timerRunning, setTimerRunning] = useState(false);
  const [moveCount, setMoveCount] = useState(0);
  const [hasSavedRun, setHasSavedRun] = useState(false);
  const [isPracticeReplay, setIsPracticeReplay] = useState(false);
  const [puzzleAspectRatio, setPuzzleAspectRatio] = useState(1);
  const [reelLightboxIndex, setReelLightboxIndex] = useState(-1);
  const pointerDragRef = useRef({
    pointerId: null,
    sourceIndex: null,
    startX: 0,
    startY: 0,
    hasDragged: false,
  });

  const savedEntry = getScoreEntryForPlayer(scoreboard, playerName);
  const isScoreLocked = !!savedEntry;
  const canPlayCurrentRun = !isScoreLocked || isPracticeReplay;
  const isLockedView = isScoreLocked && !isPracticeReplay;
  const isComplete = pieceOrder.length > 0 && pieceOrder.every((pieceId, index) => pieceId === solvedOrder[index]);
  const liveScore = getPuzzleScoreFromSeconds(elapsedSeconds);
  const displayElapsedSeconds = isLockedView
    ? Number(savedEntry?.metadata?.elapsedSeconds ?? elapsedSeconds)
    : elapsedSeconds;
  const displayMoveCount = isLockedView
    ? Number(savedEntry?.metadata?.moveCount ?? moveCount)
    : moveCount;
  const displayPuzzleScore = isLockedView ? Number(savedEntry?.score ?? 0) : liveScore;
  const showCompletionView = isComplete || isLockedView;
  const projectedPuzzleScoreboard = useMemo(() => {
    if (!isComplete) return scoreboard;
    return upsertScore(scoreboard, playerName, {
      game: 'strands',
      score: liveScore,
      total: totalPieces,
      rankPoints: liveScore,
      scoreLabel: `${liveScore} pts in ${formatElapsedTime(elapsedSeconds)}`,
      metadata: {
        elapsedSeconds,
        moveCount,
        pieces: totalPieces,
      },
    });
  }, [elapsedSeconds, isComplete, liveScore, moveCount, playerName, scoreboard, totalPieces]);
  const projectedCombinedScoreboard = useMemo(() => {
    if (!isComplete) return combinedScoreboard;
    return upsertCombinedLeaderboardRow(combinedScoreboard, playerName, {
      strandsPoints: liveScore,
    });
  }, [combinedScoreboard, isComplete, liveScore, playerName]);
  const displayedPuzzleScoreboard = isLockedView ? scoreboard : projectedPuzzleScoreboard;
  const displayedCombinedScoreboard = isLockedView ? combinedScoreboard : projectedCombinedScoreboard;
  const puzzlePlace = useMemo(
    () => getRankPositionForPlayer(displayedPuzzleScoreboard, playerName),
    [displayedPuzzleScoreboard, playerName]
  );
  const overallPlace = useMemo(
    () => getRankPositionForPlayer(displayedCombinedScoreboard, playerName),
    [displayedCombinedScoreboard, playerName]
  );

  const createShuffledOrder = useCallback(() => {
    let shuffled = shuffleArray(solvedOrder);
    while (shuffled.every((pieceId, index) => pieceId === solvedOrder[index])) {
      shuffled = shuffleArray(solvedOrder);
    }
    return shuffled;
  }, [solvedOrder]);

  const resetRound = useCallback((options = {}) => {
    setPieceOrder(createShuffledOrder());
    setSelectedIndex(null);
    setDragOverIndex(null);
    setDragState({
      active: false,
      index: null,
      pointerId: null,
      startX: 0,
      startY: 0,
      currentX: 0,
      currentY: 0,
    });
    setElapsedSeconds(0);
    setTimerRunning(false);
    setMoveCount(0);
    setHasSavedRun(false);
    setIsPracticeReplay(!!options.practiceReplay);
    setReelLightboxIndex(-1);
  }, [createShuffledOrder]);

  useEffect(() => {
    resetRound({ practiceReplay: false });
  }, [playerName, resetRound]);

  useEffect(() => {
    if (!isLockedView) return;
    setPieceOrder([...solvedOrder]);
    setSelectedIndex(null);
    setDragOverIndex(null);
    setDragState({
      active: false,
      index: null,
      pointerId: null,
      startX: 0,
      startY: 0,
      currentX: 0,
      currentY: 0,
    });
    setTimerRunning(false);
    setElapsedSeconds(Number(savedEntry?.metadata?.elapsedSeconds ?? 0));
    setMoveCount(Number(savedEntry?.metadata?.moveCount ?? 0));
    setReelLightboxIndex(-1);
  }, [isLockedView, savedEntry, solvedOrder]);

  useEffect(() => {
    if (!timerRunning || isComplete) return undefined;
    const intervalId = window.setInterval(() => {
      setElapsedSeconds((prev) => prev + 1);
    }, 1000);
    return () => window.clearInterval(intervalId);
  }, [isComplete, timerRunning]);

  useEffect(() => {
    const image = new Image();
    image.onload = () => {
      if (image.naturalWidth > 0 && image.naturalHeight > 0) {
        setPuzzleAspectRatio(image.naturalWidth / image.naturalHeight);
      }
    };
    image.src = PHOTO_PUZZLE_IMAGE_SRC;
  }, []);

  useEffect(() => {
    if (!isComplete) return;
    setTimerRunning(false);
  }, [isComplete]);

  const closeReelLightbox = useCallback(() => setReelLightboxIndex(-1), []);
  const showNextReelPhoto = useCallback(() => {
    setReelLightboxIndex((prev) => {
      if (prev < 0) return prev;
      return (prev + 1) % PHOTO_PUZZLE_SOLVED_REEL_IMAGES.length;
    });
  }, []);
  const showPrevReelPhoto = useCallback(() => {
    setReelLightboxIndex((prev) => {
      if (prev < 0) return prev;
      return (prev - 1 + PHOTO_PUZZLE_SOLVED_REEL_IMAGES.length) % PHOTO_PUZZLE_SOLVED_REEL_IMAGES.length;
    });
  }, []);

  useEffect(() => {
    if (reelLightboxIndex < 0) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeReelLightbox();
      } else if (event.key === 'ArrowRight') {
        showNextReelPhoto();
      } else if (event.key === 'ArrowLeft') {
        showPrevReelPhoto();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [closeReelLightbox, reelLightboxIndex, showNextReelPhoto, showPrevReelPhoto]);

  useEffect(() => {
    if (!isComplete || hasSavedRun || isScoreLocked || isPracticeReplay) return;
    const finalScore = getPuzzleScoreFromSeconds(elapsedSeconds);
    onSaveScore({
      game: 'strands',
      playerName,
      score: finalScore,
      total: totalPieces,
      rankPoints: finalScore,
      scoreLabel: `${finalScore} pts in ${formatElapsedTime(elapsedSeconds)}`,
      metadata: {
        elapsedSeconds,
        moveCount,
        pieces: totalPieces,
      },
    });
    setHasSavedRun(true);
  }, [elapsedSeconds, hasSavedRun, isComplete, isPracticeReplay, isScoreLocked, moveCount, onSaveScore, playerName, totalPieces]);

  const swapPieces = useCallback((sourceIndex, targetIndex) => {
    if (sourceIndex == null || targetIndex == null || sourceIndex === targetIndex) return;
    setPieceOrder((prev) => {
      const next = [...prev];
      [next[sourceIndex], next[targetIndex]] = [next[targetIndex], next[sourceIndex]];
      return next;
    });
    setMoveCount((prev) => prev + 1);
  }, []);

  const handlePieceTap = (index) => {
    if (!canPlayCurrentRun || isComplete) return;
    if (!timerRunning) setTimerRunning(true);
    if (selectedIndex == null) {
      setSelectedIndex(index);
      return;
    }
    if (selectedIndex === index) {
      setSelectedIndex(null);
      return;
    }
    swapPieces(selectedIndex, index);
    setSelectedIndex(null);
  };

  const handlePiecePointerDown = (event, index) => {
    if (!canPlayCurrentRun || isComplete) return;
    if (!timerRunning) setTimerRunning(true);
    pointerDragRef.current = {
      pointerId: event.pointerId,
      sourceIndex: index,
      startX: event.clientX,
      startY: event.clientY,
      hasDragged: false,
    };

    const handlePointerMove = (moveEvent) => {
      const dragSession = pointerDragRef.current;
      if (moveEvent.pointerId !== dragSession.pointerId) return;

      const movedDistance = Math.hypot(moveEvent.clientX - dragSession.startX, moveEvent.clientY - dragSession.startY);
      if (movedDistance > 6 && !dragSession.hasDragged) {
        pointerDragRef.current = { ...dragSession, hasDragged: true };
        setSelectedIndex(null);
        setDragState({
          active: true,
          index: dragSession.sourceIndex,
          pointerId: dragSession.pointerId,
          startX: dragSession.startX,
          startY: dragSession.startY,
          currentX: moveEvent.clientX,
          currentY: moveEvent.clientY,
        });
      }

      if (!pointerDragRef.current.hasDragged) return;

      setDragState((prev) => ({
        ...prev,
        currentX: moveEvent.clientX,
        currentY: moveEvent.clientY,
      }));

      const targetElement = document.elementFromPoint(moveEvent.clientX, moveEvent.clientY)?.closest?.('[data-puzzle-slot]');
      const targetIndex = targetElement ? Number(targetElement.getAttribute('data-puzzle-slot')) : null;
      setDragOverIndex(Number.isInteger(targetIndex) ? targetIndex : null);
    };

    const finishPointer = (upEvent) => {
      const dragSession = pointerDragRef.current;
      if (upEvent.pointerId !== dragSession.pointerId) return;

      if (dragSession.hasDragged) {
        const targetElement = document.elementFromPoint(upEvent.clientX, upEvent.clientY)?.closest?.('[data-puzzle-slot]');
        const targetIndex = targetElement ? Number(targetElement.getAttribute('data-puzzle-slot')) : null;
        if (Number.isInteger(dragSession.sourceIndex) && Number.isInteger(targetIndex) && dragSession.sourceIndex !== targetIndex) {
          swapPieces(dragSession.sourceIndex, targetIndex);
        }
      } else {
        handlePieceTap(dragSession.sourceIndex);
      }

      pointerDragRef.current = {
        pointerId: null,
        sourceIndex: null,
        startX: 0,
        startY: 0,
        hasDragged: false,
      };
      setDragState({
        active: false,
        index: null,
        pointerId: null,
        startX: 0,
        startY: 0,
        currentX: 0,
        currentY: 0,
      });
      setDragOverIndex(null);

      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', finishPointer);
      window.removeEventListener('pointercancel', finishPointer);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', finishPointer);
    window.addEventListener('pointercancel', finishPointer);
  };

  return (
    <Stack spacing={1.2}>
      <Box className="game-intro">
        <Typography className="travel-heading">Puzzle time</Typography>
        <Typography className="copy">
          For Jess&apos;s Covid birthday, Joey sent the very-bored girls a puzzle...claim your score below 
        </Typography>
      </Box>

      {isScoreLocked && !isPracticeReplay && !showCompletionView && (
        <Box className="game-lock-banner">
          <Box className="game-panel-header-row">
            <Typography className="travel-heading game-panel-header-title">Puzzle leaderboard</Typography>
            <Button
              size="small"
              className="guest-continue-button game-replay-top-button"
              onClick={() => resetRound({ practiceReplay: true })}
            >
              Play Again
            </Button>
          </Box>
          <Typography className="copy">
            You scored <strong>{savedEntry?.score ?? 0} pts</strong> in Puzzle.
          </Typography>
          {(puzzlePlace || overallPlace) && (
            <Typography className="copy">
              You&apos;re currently
              {puzzlePlace ? <> <strong>#{puzzlePlace}</strong> in Puzzle</> : ''}
              {puzzlePlace && overallPlace ? ' and' : ''}
              {overallPlace ? <> <strong>#{overallPlace}</strong> overall</> : ''}.
            </Typography>
          )}
          <Box sx={{ mt: 1 }}>
            <CombinedLeaderboardSummary
              rows={displayedCombinedScoreboard}
              playerName={playerName}
              title="Puzzle Leaderboard"
              gameRows={displayedPuzzleScoreboard}
              gameId="strands"
              showViewButton={showSummaryViewButton}
              onViewFullLeaderboard={onViewLeaderboard}
            />
          </Box>
          {showSummaryViewButton && (
            <Box sx={{ mt: 1 }}>
              <Stack direction="row" spacing={1} className="game-post-actions">
                <Button className="guest-continue-button game-post-actions-view" onClick={onViewLeaderboard}>
                  View Full Leaderboard
                </Button>
              </Stack>
            </Box>
          )}
        </Box>
      )}

      {(canPlayCurrentRun || !!savedEntry) && (
        <Box className="puzzle-module-shell">
          {!showCompletionView && (
            <Box className="puzzle-score-row">
              <Typography className="game-leaderboard-subcopy">
                Time: {formatElapsedTime(displayElapsedSeconds)} · Moves: {displayMoveCount} · Score: {displayPuzzleScore} pts
              </Typography>
            </Box>
          )}

          <Box
            className="puzzle-grid-shell"
            role="grid"
            aria-label="Photo puzzle grid"
            sx={{ aspectRatio: `${puzzleAspectRatio}` }}
          >
            <Box
              className="puzzle-grid"
              sx={{
                gridTemplateColumns: `repeat(${PHOTO_PUZZLE_GRID_SIZE}, minmax(0, 1fr))`,
                gridTemplateRows: `repeat(${PHOTO_PUZZLE_GRID_SIZE}, minmax(0, 1fr))`,
              }}
            >
              {pieceOrder.map((pieceId, index) => {
                const pieceRow = Math.floor(pieceId / PHOTO_PUZZLE_GRID_SIZE);
                const pieceCol = pieceId % PHOTO_PUZZLE_GRID_SIZE;
                const isDragging = dragState.active && dragState.index === index;
                const dragTranslateX = isDragging ? dragState.currentX - dragState.startX : 0;
                const dragTranslateY = isDragging ? dragState.currentY - dragState.startY : 0;
                return (
                  <Box
                    key={`puzzle-piece-${index}`}
                    component="button"
                    type="button"
                    data-puzzle-slot={index}
                    className={[
                      'puzzle-piece',
                      selectedIndex === index ? 'puzzle-piece--selected' : '',
                      isDragging ? 'puzzle-piece--dragging' : '',
                      dragOverIndex === index ? 'puzzle-piece--drag-over' : '',
                    ].join(' ').trim()}
                    onPointerDown={(event) => handlePiecePointerDown(event, index)}
                    disabled={!canPlayCurrentRun || isComplete}
                    sx={{
                      backgroundImage: `url("${PHOTO_PUZZLE_IMAGE_SRC}")`,
                      backgroundSize: `${PHOTO_PUZZLE_GRID_SIZE * 100}% ${PHOTO_PUZZLE_GRID_SIZE * 100}%`,
                      backgroundPosition: `${(pieceCol / (PHOTO_PUZZLE_GRID_SIZE - 1)) * 100}% ${(pieceRow / (PHOTO_PUZZLE_GRID_SIZE - 1)) * 100}%`,
                      transform: isDragging ? `translate(${dragTranslateX}px, ${dragTranslateY}px)` : undefined,
                      zIndex: isDragging ? 5 : 1,
                      pointerEvents: isDragging ? 'none' : 'auto',
                    }}
                  />
                );
              })}
            </Box>
            {isComplete && (
              <Box className="puzzle-complete-overlay" aria-hidden="true">
                <Box className="puzzle-fireworks">
                  <Box className="puzzle-firework-burst puzzle-firework-burst--a" />
                  <Box className="puzzle-firework-burst puzzle-firework-burst--b" />
                  <Box className="puzzle-firework-burst puzzle-firework-burst--c" />
                  <Box className="puzzle-firework-burst puzzle-firework-burst--d" />
                  <Box className="puzzle-firework-burst puzzle-firework-burst--e" />
                </Box>
                <Box className="puzzle-complete-overlay-copy">
                  <Typography className="puzzle-complete-overlay-text">Puzzle Complete</Typography>
                  <Typography className="puzzle-complete-overlay-score">{displayPuzzleScore} pts</Typography>
                </Box>
              </Box>
            )}
          </Box>

          {showCompletionView && (
            <Box className="puzzle-complete-banner">
              <Box className="game-panel-header-row">
                <Typography className="travel-heading game-panel-header-title">Puzzle complete</Typography>
                <Button
                  size="small"
                  className="guest-continue-button game-replay-top-button"
                  onClick={() => resetRound({ practiceReplay: !!savedEntry })}
                >
                  Play Again
                </Button>
              </Box>
              <Typography className="copy">
                You scored <strong>{displayPuzzleScore} pts</strong> in Puzzle.
              </Typography>
              {(puzzlePlace || overallPlace) && (
                <Typography className="copy">
                  You&apos;re currently
                  {puzzlePlace ? <> <strong>#{puzzlePlace}</strong> in Puzzle</> : ''}
                  {puzzlePlace && overallPlace ? ' and' : ''}
                  {overallPlace ? <> <strong>#{overallPlace}</strong> overall</> : ''}.
                </Typography>
              )}
              <Typography className="copy">
                Moves {displayMoveCount}
              </Typography>
              <Box className="puzzle-solved-reel-shell">
                <Typography className="copy puzzle-solved-reel-title">Throwback Thursday</Typography>
                <Box className="puzzle-solved-reel" role="list" aria-label="Throwback Thursday reel">
                  {PHOTO_PUZZLE_SOLVED_REEL_IMAGES.map((photoSrc, index) => (
                    <Box
                      key={`puzzle-solved-reel-${index}`}
                      component="button"
                      type="button"
                      className="puzzle-solved-polaroid"
                      sx={{ '--puzzle-polaroid-rotation': `${[-2.2, 1.8, -1.2, 2.4, -1.7][index % 5]}deg` }}
                      onClick={() => setReelLightboxIndex(index)}
                      aria-label={`Open throwback photo ${index + 1}`}
                    >
                      <Box
                        component="img"
                        className="puzzle-solved-polaroid-image"
                        src={photoSrc}
                        alt={`Throwback photo ${index + 1}`}
                        loading="lazy"
                      />
                    </Box>
                  ))}
                </Box>
              </Box>
              <CombinedLeaderboardSummary
                rows={displayedCombinedScoreboard}
                playerName={playerName}
                title="Puzzle Leaderboard"
                gameRows={displayedPuzzleScoreboard}
                gameId="strands"
                showViewButton={showSummaryViewButton}
                onViewFullLeaderboard={onViewLeaderboard}
              />
              {showSummaryViewButton && (
                <Stack direction="row" spacing={1} className="game-post-actions">
                  <Button className="guest-continue-button game-post-actions-view" onClick={onViewLeaderboard}>
                    View Full Leaderboard
                  </Button>
                </Stack>
              )}
            </Box>
          )}
        </Box>
      )}
      {reelLightboxIndex >= 0 && typeof document !== 'undefined' && createPortal(
        <Box
          className="puzzle-reel-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label="Throwback Thursday photos"
          onClick={closeReelLightbox}
        >
          <Box className="puzzle-reel-lightbox-content" onClick={(event) => event.stopPropagation()}>
            <Box className="puzzle-reel-lightbox-polaroid">
              <IconButton
                className="puzzle-reel-lightbox-close"
                onClick={closeReelLightbox}
                aria-label="Close throwback photos"
              >
                <CloseRoundedIcon fontSize="small" />
              </IconButton>
              <Box
                component="img"
                className="puzzle-reel-lightbox-image"
                src={PHOTO_PUZZLE_SOLVED_REEL_IMAGES[reelLightboxIndex]}
                alt={`Throwback photo ${reelLightboxIndex + 1}`}
              />
            </Box>
            <Stack direction="row" spacing={1} className="puzzle-reel-lightbox-actions">
              <Button className="puzzle-reel-lightbox-nav" onClick={showPrevReelPhoto}>Previous</Button>
              <Box className="puzzle-reel-lightbox-dots" aria-label="Throwback photo progress">
                {PHOTO_PUZZLE_SOLVED_REEL_IMAGES.map((_, index) => (
                  <Box
                    key={`puzzle-reel-dot-${index}`}
                    component="button"
                    type="button"
                    className={[
                      'puzzle-reel-lightbox-dot',
                      index === reelLightboxIndex ? 'puzzle-reel-lightbox-dot--active' : '',
                    ].join(' ').trim()}
                    onClick={() => setReelLightboxIndex(index)}
                    aria-label={`View throwback photo ${index + 1}`}
                    aria-current={index === reelLightboxIndex ? 'true' : undefined}
                  />
                ))}
              </Box>
              <Button className="puzzle-reel-lightbox-nav" onClick={showNextReelPhoto}>Next</Button>
            </Stack>
          </Box>
        </Box>,
        document.body
      )}
    </Stack>
  );
}

function GameLeaderboardSidebar({
  combinedLeaderboard,
  highlightedPlayerName,
  easyRankLookup,
  mediumRankLookup,
  onPlayGame = () => {},
  onBackToGame = () => {},
}) {
  return (
    <Box className="game-leaderboard-sidebar">
      <Button
        variant="outlined"
        className="game-mobile-leaderboard-toggle"
        onClick={onBackToGame}
      >
        Back to Game
      </Button>
      <Typography className="travel-heading" sx={{ mb: 0.7 }}>Full Leaderboard</Typography>
      <CombinedLeaderboardTable
        rows={combinedLeaderboard}
        playerName={highlightedPlayerName}
        easyRankLookup={easyRankLookup}
        mediumRankLookup={mediumRankLookup}
        onPlayGame={onPlayGame}
      />
    </Box>
  );
}

function GamePage() {
  const { siteVisitorName, selectedGuestGroup } = useOutletContext();
  const partyPlayerNames = useMemo(
    () => getPartyPlayerNames(selectedGuestGroup, siteVisitorName),
    [selectedGuestGroup, siteVisitorName]
  );
  const [selectedPlayerName, setSelectedPlayerName] = useState(() => getStoredPreferredPlayer(partyPlayerNames, siteVisitorName));
  const [activeGame, setActiveGame] = useState('strands');
  const [mobileGamesView, setMobileGamesView] = useState('game');
  const [photoScoreboard, setPhotoScoreboard] = useState([]);
  const [strandsScoreboard, setStrandsScoreboard] = useState([]);
  const [combinedLeaderboard, setCombinedLeaderboard] = useState([]);
  const [leaderboardError, setLeaderboardError] = useState('');
  const showSummaryViewButton = useMediaQuery('(max-width:899px)');

  const loadLeaderboards = useCallback(async () => {
    try {
      const [photoRows, strandsRows, combinedRows] = await Promise.all([
        fetchGameLeaderboard('photo'),
        fetchGameLeaderboard('strands'),
        fetchCombinedLeaderboard(),
      ]);
      setPhotoScoreboard(photoRows);
      setStrandsScoreboard(strandsRows);
      setCombinedLeaderboard(combinedRows);
      setLeaderboardError('');
    } catch {
      setLeaderboardError('Leaderboard is temporarily unavailable.');
    }
  }, []);

  useEffect(() => {
    if (partyPlayerNames.includes(selectedPlayerName)) return;
    setSelectedPlayerName(getStoredPreferredPlayer(partyPlayerNames, siteVisitorName));
  }, [partyPlayerNames, selectedPlayerName, siteVisitorName]);

  useEffect(() => {
    try {
      window.localStorage.setItem(PHOTO_TRIVIA_PLAYER_KEY, selectedPlayerName || '');
    } catch {
      // ignore
    }
  }, [selectedPlayerName]);

  useEffect(() => {
    loadLeaderboards();
  }, [loadLeaderboards]);

  const saveScore = useCallback(async (gameId, result) => {
    try {
      await submitGameScore(gameId, {
        playerName: result.playerName,
        playerId: slugify(result.playerName || 'Guest'),
        groupId: selectedGuestGroup?.id || '',
        score: result.score ?? 0,
        total: result.total ?? 0,
        attempts: result.attempts ?? 1,
        scoreLabel: result.scoreLabel ?? `${result.score ?? 0}`,
        rankPoints: result.rankPoints ?? (result.score ?? 0),
        metadata: result.metadata ?? {},
      });
    } catch (error) {
      if (error?.status !== 409) {
        // keep local score state and let user continue even if save is transiently unavailable
      }
    }
    await loadLeaderboards();
  }, [loadLeaderboards, selectedGuestGroup?.id]);

  const handleSavePhotoScore = (result) => {
    setPhotoScoreboard((prev) => upsertScore(prev, result.playerName, result));
    void saveScore('photo', result);
  };

  const handleSaveStrandsScore = (result) => {
    setStrandsScoreboard((prev) => upsertScore(prev, result.playerName, result));
    void saveScore('strands', result);
  };

  const easyRankLookup = useMemo(() => buildGameRankLookup(strandsScoreboard), [strandsScoreboard]);
  const mediumRankLookup = useMemo(() => buildGameRankLookup(photoScoreboard), [photoScoreboard]);

  const handleViewFullLeaderboard = useCallback(() => {
    setMobileGamesView('leaderboard');
  }, []);

  const handlePlayNow = useCallback((gameId) => {
    if (!gameId) return;
    setActiveGame(gameId);
    setMobileGamesView('game');
  }, []);

  return (
    <Grid container spacing={2.5}>
      <Grid item xs={12}>
        <InfoCard title="Games">
          {leaderboardError && (
            <Typography className="copy" sx={{ mb: 1.2 }}>{leaderboardError}</Typography>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12} md={8} className={`game-main-grid ${mobileGamesView === 'game' ? 'game-main-grid--open' : ''}`}>
              <Stack spacing={1.2}>
                <Box className="game-player-selector">
                  <Typography className="photo-trivia-stat-label">Who is playing?</Typography>
                  <Stack direction="row" spacing={0.8} className="game-player-chip-row">
                    {partyPlayerNames.map((name) => (
                      <Button
                        key={name}
                        size="small"
                        variant={selectedPlayerName === name ? 'contained' : 'outlined'}
                        className={`game-player-chip ${selectedPlayerName === name ? 'game-player-chip--active' : ''}`}
                        onClick={() => setSelectedPlayerName(name)}
                      >
                        {name}
                      </Button>
                    ))}
                  </Stack>
                </Box>

                <Tabs
                  value={activeGame}
                  onChange={(_, nextValue) => setActiveGame(nextValue)}
                  className="game-inner-tabs"
                  variant="scrollable"
                  scrollButtons="auto"
                >
                  <Tab label="Puzzle" value="strands" />
                  <Tab label="GeoGuesser" value="photo" />
                </Tabs>

                <Box className="game-panel-card">
                  {activeGame === 'photo' ? (
                    <PhotoTriviaGame
                      playerName={selectedPlayerName}
                      scoreboard={photoScoreboard}
                      combinedScoreboard={combinedLeaderboard}
                      onSaveScore={handleSavePhotoScore}
                      onViewLeaderboard={handleViewFullLeaderboard}
                      showSummaryViewButton={showSummaryViewButton}
                    />
                  ) : (
                    <PhotoPuzzleGame
                      playerName={selectedPlayerName}
                      scoreboard={strandsScoreboard}
                      combinedScoreboard={combinedLeaderboard}
                      onSaveScore={handleSaveStrandsScore}
                      onViewLeaderboard={handleViewFullLeaderboard}
                      showSummaryViewButton={showSummaryViewButton}
                    />
                  )}
                </Box>
              </Stack>
            </Grid>

            <Grid item xs={12} md={4} className={`game-leaderboard-grid ${mobileGamesView === 'leaderboard' ? 'game-leaderboard-grid--open' : ''}`}>
              <GameLeaderboardSidebar
                combinedLeaderboard={combinedLeaderboard}
                highlightedPlayerName={selectedPlayerName}
                easyRankLookup={easyRankLookup}
                mediumRankLookup={mediumRankLookup}
                onPlayGame={handlePlayNow}
                onBackToGame={() => setMobileGamesView('game')}
              />
            </Grid>
          </Grid>
        </InfoCard>
      </Grid>
    </Grid>
  );
}

function RsvpPage() {
  return (
    <Grid container spacing={2.5}>
      <Grid item xs={12}>
        <RsvpStatusPanel />
      </Grid>
    </Grid>
  );
}

function formatIsoDateTime(value) {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
}

function truncateDisplayText(value, maxLength = 10) {
  const text = `${value || ''}`;
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength)}...`;
}

function normalizeGuestLookupKey(value) {
  return `${value || ''}`.trim().toLowerCase();
}

function buildGuestDetailLookup(group) {
  const lookup = new Map();
  (group?.guestDetails || []).forEach((guestDetail) => {
    const name = normalizeGuestName(guestDetail);
    const key = normalizeGuestLookupKey(name);
    if (!key) return;
    lookup.set(key, {
      isChild: guestDetail?.isChild === true,
      guestId: `${guestDetail?.guestId || slugify(name)}`.trim() || slugify(name),
      fullName: name,
    });
  });
  return lookup;
}

const RESPONSES_FILTER_OPTIONS = [
  { value: 'all', label: 'All rows' },
  { value: 'responded', label: 'Responded' },
  { value: 'waiting', label: 'Still waiting' },
  { value: 'sunday-not-invited', label: 'Not Invited (Sunday)' },
  { value: 'music', label: 'Music' },
  { value: 'allergies', label: 'Allergies' },
  { value: 'shuttle', label: 'Shuttle' },
];

function ResponsesAdminPage() {
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isAuthorized, setIsAuthorized] = useState(() => {
    try {
      return window.sessionStorage.getItem(RESPONSES_ADMIN_ACCESS_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [groupRows, setGroupRows] = useState([]);
  const [lastLoadedAtIso, setLastLoadedAtIso] = useState('');
  const [responseFilter, setResponseFilter] = useState('all');

  const loadResponses = useCallback(async () => {
    setIsLoading(true);
    setLoadError('');
    try {
      const groups = await fetchAllGuestGroups(1000);
      const groupResponses = await mapWithConcurrency(groups, 12, async (group) => {
          let hydratedGroup = group;
          if (group?.id) {
            try {
              const fetchedGroup = await fetchInviteGroup(group.id);
              hydratedGroup = mergeNormalizedGuestGroups(fetchedGroup, group);
            } catch {
              hydratedGroup = group;
            }
          }
          try {
            const payload = await fetchRsvp(group.id);
            return { group: hydratedGroup, payload };
          } catch {
            return { group: hydratedGroup, payload: null };
          }
        });

      const nextGroupRows = groupResponses.map(({ group, payload }) => {
        const groupResponsesMap = normalizeRsvpResponses(payload?.responses);
        const eventInvites = normalizeEventInvites(group.eventInvites);
        const groupSong = (payload?.partySongRecommendation || '').trim() || '—';
        const groupNotes = (payload?.partyAdditionalNotes || '').trim() || '—';
        const submittedBy = (payload?.submittedBy || '').trim() || '—';
        const updatedAtIso = payload?.updatedAtIso || '';
        const guestDetailLookup = buildGuestDetailLookup(group);
        const legacyPartyShuttle = derivePartyShuttleFromResponses(groupResponsesMap);
        const storedPartyNeedsShuttle = normalizeOptionalBool(payload?.partyNeedsShuttle);
        const partyNeedsShuttle = storedPartyNeedsShuttle ?? legacyPartyShuttle.needsShuttle;
        const partyShuttleNeedLabel = partyNeedsShuttle === true ? 'Yes' : partyNeedsShuttle === false ? 'No' : 'No response';
        const partyShuttleLocation = partyNeedsShuttle === true
          ? ((payload?.partyShuttleLocation || legacyPartyShuttle.shuttleLocation || '').trim() || '—')
          : '—';

        const guests = group.guests.map((guest) => {
          const guestResponse = groupResponsesMap?.[guest] ?? getDefaultGuestResponse();
          const friday = eventInvites.friday === false ? 'Not invited' : (guestResponse.friday || 'No response');
          const saturday = eventInvites.saturday === false ? 'Not invited' : (guestResponse.saturday || 'No response');
          const sunday = eventInvites.sunday === false ? 'Not invited' : (guestResponse.sunday || 'No response');
          const detail = guestDetailLookup.get(normalizeGuestLookupKey(guest));
          const invitedEventCount = RSVP_EVENT_KEYS.reduce(
            (count, eventKey) => count + (eventInvites[eventKey] === false ? 0 : 1),
            0
          );
          const respondedEventCount = RSVP_EVENT_KEYS.reduce((count, eventKey) => {
            if (eventInvites[eventKey] === false) return count;
            const answer = guestResponse[eventKey];
            if (answer === 'Attending' || answer === 'Not attending') return count + 1;
            return count;
          }, 0);
          return {
            name: guest,
            friday,
            saturday,
            sunday,
            allergies: (guestResponse.allergies || '').trim() || '—',
            shuttleNeed: partyShuttleNeedLabel,
            shuttleLocation: partyShuttleLocation,
            invitedEventCount,
            respondedEventCount,
            isChild: detail?.isChild === true,
          };
        });

        return {
          groupId: group.id,
          guestCount: guests.length,
          guests,
          songRequest: groupSong,
          additionalNotes: groupNotes,
          submittedBy,
          updatedAtIso,
        };
      });

      nextGroupRows.sort((a, b) => (a.groupId || '').localeCompare(b.groupId || ''));
      setGroupRows(nextGroupRows);
      setLastLoadedAtIso(new Date().toISOString());
    } catch {
      setGroupRows([]);
      setLoadError('Unable to load responses right now. Please refresh and try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthorized) return;
    void loadResponses();
  }, [isAuthorized, loadResponses]);

  const personRows = useMemo(() => {
    const flattened = [];
    groupRows.forEach((group) => {
      group.guests.forEach((guest, guestIndex) => {
        let statusKey = 'waiting';
        let statusLabel = 'Still waiting';
        if (guest.invitedEventCount === 0) {
          statusKey = 'not-invited';
          statusLabel = 'Not invited';
        } else if (guest.respondedEventCount >= guest.invitedEventCount) {
          statusKey = 'responded';
          statusLabel = 'Responded';
        } else if (guest.respondedEventCount > 0) {
          statusKey = 'partial';
          statusLabel = 'Partial';
        }

        flattened.push({
          rowId: `${group.groupId}-${slugify(guest.name)}-${guestIndex}`,
          groupId: group.groupId,
          guestName: guest.name,
          friday: guest.friday,
          saturday: guest.saturday,
          sunday: guest.sunday,
          allergies: guest.allergies,
          shuttleNeed: guest.shuttleNeed,
          shuttleLocation: guest.shuttleLocation,
          statusKey,
          statusLabel,
          songRequest: group.songRequest,
          additionalNotes: group.additionalNotes,
          submittedBy: group.submittedBy,
          updatedAtIso: group.updatedAtIso,
          isKid: guest.isChild === true,
        });
      });
    });
    return flattened;
  }, [groupRows]);

  const summary = useMemo(() => {
    const totalGroups = groupRows.length;
    let invitedPeople = 0;
    let respondedPeople = 0;
    let partialPeople = 0;
    let waitingPeople = 0;
    let notInvitedPeople = 0;
    let kidPeople = 0;

    groupRows.forEach((group) => {
      group.guests.forEach((guest) => {
        if (guest.isChild === true) {
          kidPeople += 1;
        }
        if (guest.invitedEventCount === 0) {
          notInvitedPeople += 1;
          return;
        }
        invitedPeople += 1;
        if (guest.respondedEventCount >= guest.invitedEventCount) {
          respondedPeople += 1;
          return;
        }
        if (guest.respondedEventCount > 0) {
          partialPeople += 1;
          return;
        }
        waitingPeople += 1;
      });
    });

    return {
      totalGroups,
      invitedPeople,
      respondedPeople,
      partialPeople,
      waitingPeople,
      notInvitedPeople,
      kidPeople,
    };
  }, [groupRows]);

  const eventStats = useMemo(() => RSVP_EVENTS.map((event) => {
    let invited = 0;
    let responded = 0;
    let attending = 0;
    let attendingAdults = 0;
    let attendingKids = 0;
    let notAttending = 0;
    let waiting = 0;
    let maxOutstanding = 0;
    let maxOutstandingAdults = 0;
    let maxOutstandingKids = 0;

    groupRows.forEach((group) => {
      group.guests.forEach((guest) => {
        const value = guest[event.key];
        if (value === 'Not invited') return;
        const isKid = guest.isChild === true;
        invited += 1;
        if (value === 'Attending') {
          responded += 1;
          attending += 1;
          maxOutstanding += 1;
          if (isKid) {
            attendingKids += 1;
            maxOutstandingKids += 1;
          } else {
            attendingAdults += 1;
            maxOutstandingAdults += 1;
          }
          return;
        }
        if (value === 'Not attending') {
          responded += 1;
          notAttending += 1;
          return;
        }
        waiting += 1;
        maxOutstanding += 1;
        if (isKid) {
          maxOutstandingKids += 1;
        } else {
          maxOutstandingAdults += 1;
        }
      });
    });

    const responseRate = invited > 0 ? Math.round((responded / invited) * 100) : 0;
    return {
      ...event,
      responded,
      attending,
      attendingAdults,
      attendingKids,
      notAttending,
      waiting,
      maxOutstanding,
      maxOutstandingAdults,
      maxOutstandingKids,
      responseRate,
    };
  }), [groupRows]);

  const rowMatchesFilter = useCallback((row, filterValue) => {
    if (filterValue === 'all') return true;
    if (filterValue === 'responded') return row.statusKey === 'responded';
    if (filterValue === 'waiting') return row.statusKey === 'waiting';
    if (filterValue === 'sunday-not-invited') return row.sunday === 'Not invited';
    if (filterValue === 'music') return row.songRequest !== '—';
    if (filterValue === 'allergies') return row.allergies !== '—';
    if (filterValue === 'shuttle') return row.shuttleNeed === 'Yes';
    return true;
  }, []);

  const filterCounts = useMemo(() => ({
    all: personRows.length,
    responded: personRows.filter((row) => rowMatchesFilter(row, 'responded')).length,
    waiting: personRows.filter((row) => rowMatchesFilter(row, 'waiting')).length,
    'sunday-not-invited': personRows.filter((row) => rowMatchesFilter(row, 'sunday-not-invited')).length,
    music: personRows.filter((row) => rowMatchesFilter(row, 'music')).length,
    allergies: personRows.filter((row) => rowMatchesFilter(row, 'allergies')).length,
    shuttle: personRows.filter((row) => rowMatchesFilter(row, 'shuttle')).length,
  }), [personRows, rowMatchesFilter]);

  const visiblePersonRows = useMemo(() => {
    const filtered = responseFilter === 'all'
      ? personRows
      : personRows.filter((row) => rowMatchesFilter(row, responseFilter));

    const grouped = new Map();
    filtered.forEach((row) => {
      if (!grouped.has(row.groupId)) {
        grouped.set(row.groupId, []);
      }
      grouped.get(row.groupId).push(row);
    });

    const flattened = [];
    let visibleGroupIndex = 0;
    grouped.forEach((rows) => {
      const displayGroupStripe = visibleGroupIndex % 2 === 1;
      rows.forEach((row, index) => {
        flattened.push({
          ...row,
          displayGroupStripe,
          showGroupMetaCells: index === 0,
          isFirstInGroup: index === 0,
          isLastInGroup: index === rows.length - 1,
          visibleGroupRowSpan: rows.length,
        });
      });
      visibleGroupIndex += 1;
    });

    return flattened;
  }, [personRows, responseFilter, rowMatchesFilter]);

  const unlockAdminView = () => {
    const candidate = password.trim();
    if (!candidate) {
      setPasswordError('Enter the password to view responses.');
      return;
    }
    if (candidate !== RESPONSES_ADMIN_PASSWORD) {
      setPasswordError('Incorrect password.');
      return;
    }
    setPassword('');
    setPasswordError('');
    setIsAuthorized(true);
    try {
      window.sessionStorage.setItem(RESPONSES_ADMIN_ACCESS_KEY, '1');
    } catch {
      // Ignore sessionStorage failures.
    }
  };

  return (
    <Box className="responses-admin-shell">
      <Container maxWidth="xl" sx={{ py: { xs: 2, md: 4 }, position: 'relative', zIndex: 1 }}>
        <Card className="responses-admin-card" elevation={0}>
          <CardContent sx={{ p: { xs: 2.4, md: 3 } }}>
            <Stack spacing={1.4}>
              <Typography variant="h5" className="section-title" sx={{ mb: 0 }}>
                RSVP Responses
              </Typography>
              <Typography className="copy">
                Admin-only view. This page is intentionally hidden from the main site navigation.
              </Typography>

              {!isAuthorized && (
                <Stack spacing={1.1} sx={{ maxWidth: 420 }}>
                  <TextField
                    fullWidth
                    label="Admin password"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(event) => {
                      setPassword(event.target.value);
                      setPasswordError('');
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        unlockAdminView();
                      }
                    }}
                    className="guest-search-input"
                  />
                  {!!passwordError && (
                    <Typography className="guest-match-message">{passwordError}</Typography>
                  )}
                  <Box>
                    <Button
                      variant="contained"
                      className="guest-continue-button"
                      disabled={!password.trim()}
                      onClick={unlockAdminView}
                    >
                      Unlock responses
                    </Button>
                  </Box>
                </Stack>
              )}

              {isAuthorized && (
                <Stack spacing={1.2}>
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={1}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', md: 'center' }}
                  >
                    <Typography className="copy">
                      Invite groups: <strong>{summary.totalGroups}</strong> | Invited people: <strong>{summary.invitedPeople}</strong> | Responded: <strong>{summary.respondedPeople}</strong> | Partial: <strong>{summary.partialPeople}</strong> | Still waiting: <strong>{summary.waitingPeople}</strong> | Not invited: <strong>{summary.notInvitedPeople}</strong> | Kids marked: <strong>{summary.kidPeople}</strong>
                    </Typography>
                    <Button
                      variant="outlined"
                      className="responses-admin-refresh"
                      disabled={isLoading}
                      onClick={() => void loadResponses()}
                    >
                      {isLoading ? 'Refreshing...' : 'Refresh'}
                    </Button>
                  </Stack>

                  <Typography className="copy">
                    Last refreshed: {formatIsoDateTime(lastLoadedAtIso)}
                  </Typography>

                  <Box className="responses-admin-event-stats-grid">
                    {eventStats.map((event) => (
                      <Box key={event.key} className="responses-admin-event-stat-card">
                        <Typography className="responses-admin-event-stat-title">
                          {event.label}
                        </Typography>
                        <Typography className="responses-admin-event-stat-line">
                          Max outstanding: <strong>{event.maxOutstanding}</strong> (Adults: <strong>{event.maxOutstandingAdults}</strong>, Kids: <strong>{event.maxOutstandingKids}</strong>) | Responded: <strong>{event.responded}</strong> ({event.responseRate}%)
                        </Typography>
                        <Typography className="responses-admin-event-stat-line">
                          Attending: <strong>{event.attending}</strong> (Adults: <strong>{event.attendingAdults}</strong>, Kids: <strong>{event.attendingKids}</strong>) | Not attending: <strong>{event.notAttending}</strong> | Waiting: <strong>{event.waiting}</strong>
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
                    {RESPONSES_FILTER_OPTIONS.map((option) => (
                      <Button
                        key={option.value}
                        size="small"
                        variant={responseFilter === option.value ? 'contained' : 'outlined'}
                        className="responses-admin-filter-button"
                        onClick={() => setResponseFilter(option.value)}
                      >
                        {option.label} ({filterCounts[option.value] ?? 0})
                      </Button>
                    ))}
                  </Stack>

                  {!!loadError && (
                    <Typography className="guest-match-message">{loadError}</Typography>
                  )}

                  {!loadError && (
                    <Box className="responses-admin-table-wrap">
                      <table className="responses-admin-table">
                        <thead>
                          <tr>
                            <th>Group ID</th>
                            <th>Person</th>
                            <th>Friday</th>
                            <th>Saturday</th>
                            <th>Sunday</th>
                            <th>Allergies</th>
                            <th>Shuttle</th>
                            <th>Staying / pickup</th>
                            <th>Status</th>
                            <th>Song request</th>
                            <th>Additional notes</th>
                          </tr>
                        </thead>
                        <tbody>
                          {visiblePersonRows.length === 0 && (
                            <tr>
                              <td colSpan={11} className="responses-admin-empty">
                                {isLoading ? 'Loading invited people...' : 'No people match this filter.'}
                              </td>
                            </tr>
                          )}
                          {visiblePersonRows.map((row) => (
                            <tr
                              key={row.rowId}
                              className={`responses-admin-row${row.displayGroupStripe ? ' responses-admin-row--group-alt' : ''}${!row.isFirstInGroup ? ' responses-admin-row--person-divider' : ''}`}
                            >
                              {row.showGroupMetaCells && (
                                <td rowSpan={row.visibleGroupRowSpan} className="responses-admin-group-cell" title={row.groupId}>
                                  {truncateDisplayText(row.groupId, 10)}
                                </td>
                              )}
                              <td>
                                <span>{row.guestName}</span>
                                {row.isKid && <span className="responses-admin-kid-badge">Kid</span>}
                              </td>
                              <td>{row.friday}</td>
                              <td>{row.saturday}</td>
                              <td>{row.sunday}</td>
                              <td>{row.allergies}</td>
                              <td>{row.shuttleNeed}</td>
                              <td>{row.shuttleLocation}</td>
                              <td>
                                <span className={`responses-admin-status responses-admin-status--${row.statusKey}`}>
                                  {row.statusLabel}
                                </span>
                              </td>
                              {row.showGroupMetaCells && (
                                <td rowSpan={row.visibleGroupRowSpan}>{row.songRequest}</td>
                              )}
                              {row.showGroupMetaCells && (
                                <td rowSpan={row.visibleGroupRowSpan}>{row.additionalNotes}</td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </Box>
                  )}
                </Stack>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}

function SiteLayout() {
  const [entryStep, setEntryStep] = useState('locked');
  const [accessPassword, setAccessPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isCheckingPassword, setIsCheckingPassword] = useState(false);
  const [hasPasswordAccess, setHasPasswordAccess] = useState(() => {
    try {
      return window.sessionStorage.getItem('sitePasswordAccess') === '1';
    } catch {
      return false;
    }
  });
  const [guestSearch, setGuestSearch] = useState('');
  const [hasSubmittedGuestSearch, setHasSubmittedGuestSearch] = useState(false);
  const [guestMatches, setGuestMatches] = useState([]);
  const [guestSearchError, setGuestSearchError] = useState('');
  const [isSearchingGuests, setIsSearchingGuests] = useState(false);
  const [selectedGuestGroupId, setSelectedGuestGroupId] = useState(() => {
    try {
      return window.localStorage.getItem('guestGroupId') || '';
    } catch {
      return '';
    }
  });
  const [selectedGuestGroup, setSelectedGuestGroup] = useState(null);
  const [siteVisitorName, setSiteVisitorName] = useState(() => {
    try {
      return window.localStorage.getItem('siteVisitorName') || '';
    } catch {
      return '';
    }
  });
  const [rsvpResponses, setRsvpResponses] = useState({});
  const [partySongRecommendation, setPartySongRecommendation] = useState('');
  const [partyAdditionalNotes, setPartyAdditionalNotes] = useState('');
  const [partyNeedsShuttle, setPartyNeedsShuttle] = useState(null);
  const [partyShuttleLocation, setPartyShuttleLocation] = useState('');
  const [hasLoadedRsvp, setHasLoadedRsvp] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = NAV_ITEMS.find((item) => location.pathname.startsWith(item.path))?.path ?? '/home';
  const weddingDate = new Date('2026-08-08T00:00:00');
  const today = new Date();
  const msPerDay = 1000 * 60 * 60 * 24;
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const daysUntil = Math.max(0, Math.ceil((weddingDate - startOfToday) / msPerDay));
  const guestSearchAlphaCount = useMemo(() => ((guestSearch || '').match(/[a-z]/gi) || []).length, [guestSearch]);
  const canSearchGuests = guestSearchAlphaCount >= 4;
  const searchQuery = useMemo(() => {
    const q = guestSearch.trim().toLowerCase();
    if (!canSearchGuests) return '';
    return q;
  }, [guestSearch, canSearchGuests]);
  const shouldShowGuestMatches = canSearchGuests && hasSubmittedGuestSearch && guestMatches.length <= 2;

  useEffect(() => {
    if (!selectedGuestGroupId) {
      setSelectedGuestGroup(null);
      return;
    }

    try {
      window.localStorage.setItem('guestGroupId', selectedGuestGroupId);
    } catch {
      // Ignore localStorage failures in private browsing / restricted environments.
    }

    const quickMatch = guestMatches.find((group) => group.id === selectedGuestGroupId);
    if (quickMatch) {
      setSelectedGuestGroup(quickMatch);
    }

    let isCanceled = false;
    const loadGroup = async () => {
      try {
        const group = await fetchInviteGroup(selectedGuestGroupId);
        if (!isCanceled) {
          setSelectedGuestGroup(group);
        }
      } catch {
        // keep quick-match fallback
      }
    };
    loadGroup();
    return () => {
      isCanceled = true;
    };
  }, [guestMatches, selectedGuestGroupId]);

  useEffect(() => {
    if (!hasSubmittedGuestSearch || !searchQuery) {
      setGuestMatches([]);
      setGuestSearchError('');
      setIsSearchingGuests(false);
      return;
    }

    let isCanceled = false;
    setIsSearchingGuests(true);
    setGuestSearchError('');

    const runSearch = async () => {
      try {
        const matches = await searchInviteGroups(searchQuery, 12);
        if (!isCanceled) {
          setGuestMatches(matches);
        }
      } catch {
        if (!isCanceled) {
          setGuestSearchError('Invite search is temporarily unavailable. Please try again.');
          setGuestMatches([]);
        }
      } finally {
        if (!isCanceled) {
          setIsSearchingGuests(false);
        }
      }
    };

    runSearch();
    return () => {
      isCanceled = true;
    };
  }, [hasSubmittedGuestSearch, searchQuery]);

  useEffect(() => {
    setHasLoadedRsvp(false);
    if (!selectedGuestGroupId) return;
    let isCanceled = false;
    const loadRsvp = async () => {
      try {
        const payload = await fetchRsvp(selectedGuestGroupId);
        if (!isCanceled) {
          const normalizedResponses = normalizeRsvpResponses(payload?.responses);
          const legacyPartyShuttle = derivePartyShuttleFromResponses(normalizedResponses);
          const storedPartyNeedsShuttle = normalizeOptionalBool(payload?.partyNeedsShuttle);
          const nextPartyNeedsShuttle = storedPartyNeedsShuttle ?? legacyPartyShuttle.needsShuttle;

          setRsvpResponses(normalizedResponses);
          setPartySongRecommendation(payload?.partySongRecommendation || '');
          setPartyAdditionalNotes(payload?.partyAdditionalNotes || '');
          setPartyNeedsShuttle(nextPartyNeedsShuttle);
          setPartyShuttleLocation(
            nextPartyNeedsShuttle === true
              ? ((payload?.partyShuttleLocation || legacyPartyShuttle.shuttleLocation || '').trim())
              : ''
          );
          setHasLoadedRsvp(true);
        }
      } catch {
        if (!isCanceled) {
          setRsvpResponses({});
          setPartySongRecommendation('');
          setPartyAdditionalNotes('');
          setPartyNeedsShuttle(null);
          setPartyShuttleLocation('');
          setHasLoadedRsvp(true);
        }
      }
    };
    loadRsvp();
    return () => {
      isCanceled = true;
    };
  }, [selectedGuestGroupId]);

  useEffect(() => {
    try {
      window.localStorage.setItem('siteVisitorName', siteVisitorName || '');
    } catch {
      // ignore
    }
  }, [siteVisitorName]);

  useEffect(() => {
    if (!selectedGuestGroup) return;
    setRsvpResponses((prev) => {
      const next = { ...(prev || {}) };
      selectedGuestGroup.guests.forEach((guest) => {
        next[guest] = { ...getDefaultGuestResponse(), ...(next[guest] || {}) };
      });
      return next;
    });
  }, [selectedGuestGroup]);

  const resetGuestGateState = () => {
    setGuestSearch('');
    setHasSubmittedGuestSearch(false);
    setGuestMatches([]);
    setGuestSearchError('');
    setIsSearchingGuests(false);
    setSelectedGuestGroupId('');
    setSelectedGuestGroup(null);
    setSiteVisitorName('');
    setPartySongRecommendation('');
    setPartyAdditionalNotes('');
    setPartyNeedsShuttle(null);
    setPartyShuttleLocation('');
    setHasLoadedRsvp(false);
    setRsvpResponses({});
    try {
      window.localStorage.removeItem('guestGroupId');
      window.localStorage.removeItem('siteVisitorName');
    } catch {
      // Ignore localStorage failures.
    }
  };

  const resetPasswordGateState = () => {
    setAccessPassword('');
    setPasswordError('');
    setIsCheckingPassword(false);
  };

  const goToGuestGate = () => {
    resetGuestGateState();
    setEntryStep('guest-gate');
    window.setTimeout(() => {
      scroller.scrollTo('guest-gate-anchor', {
        duration: 1100,
        smooth: 'easeInOutCubic',
        offset: -40,
      });
    }, 220);
  };

  useEffect(() => {
    if (entryStep !== 'locked') return undefined;

    let touchStartY = 0;
    let isTransitioning = false;

    const openAccessGate = () => {
      if (isTransitioning) return;
      isTransitioning = true;
      if (hasPasswordAccess) {
        resetGuestGateState();
        setEntryStep('guest-gate');
        window.setTimeout(() => {
          scroller.scrollTo('guest-gate-anchor', {
            duration: 1100,
            smooth: 'easeInOutCubic',
            offset: -40,
          });
        }, 220);
        return;
      }
      resetPasswordGateState();
      setEntryStep('password-gate');
      window.setTimeout(() => {
        scroller.scrollTo('password-gate-anchor', {
          duration: 1100,
          smooth: 'easeInOutCubic',
          offset: -40,
        });
      }, 220);
    };

    const onWheel = (event) => {
      if (event.deltaY > 8) {
        openAccessGate();
      }
    };

    const onTouchStart = (event) => {
      touchStartY = event.touches?.[0]?.clientY ?? 0;
    };

    const onTouchMove = (event) => {
      const currentY = event.touches?.[0]?.clientY ?? touchStartY;
      if (touchStartY - currentY > 12) {
        openAccessGate();
      }
    };

    window.addEventListener('wheel', onWheel, { passive: true });
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: true });

    return () => {
      window.removeEventListener('wheel', onWheel);
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
    };
  }, [entryStep, hasPasswordAccess]);

  const openGuestGate = () => {
    if (entryStep !== 'locked') return;
    if (hasPasswordAccess) {
      goToGuestGate();
      return;
    }
    resetPasswordGateState();
    setEntryStep('password-gate');
    window.setTimeout(() => {
      scroller.scrollTo('password-gate-anchor', {
        duration: 1100,
        smooth: 'easeInOutCubic',
        offset: -40,
      });
    }, 220);
  };

  const submitPasswordGate = async () => {
    const candidate = accessPassword.trim();
    if (!candidate) {
      setPasswordError('Enter the password to continue.');
      return;
    }
    setIsCheckingPassword(true);
    setPasswordError('');
    try {
      const digest = await sha256Hex(candidate);
      if (digest !== SITE_PASSWORD_HASH) {
        setPasswordError('Incorrect password. Please try again.');
        return;
      }
      setHasPasswordAccess(true);
      try {
        window.sessionStorage.setItem('sitePasswordAccess', '1');
      } catch {
        // Ignore sessionStorage failures.
      }
      goToGuestGate();
    } catch {
      setPasswordError('Password check failed. Please refresh and try again.');
    } finally {
      setIsCheckingPassword(false);
    }
  };

  const continueToSite = () => {
    if (!selectedGuestGroupId) return;
    const group = selectedGuestGroup || guestMatches.find((g) => g.id === selectedGuestGroupId);
    if (group) {
      const best = getBestMatchingGuest(group, guestSearch);
      setSiteVisitorName(best.name || group.guests[0] || 'Guest');
    }
    navigate('/home');
    setEntryStep('site');
  };

  const isPasswordGateVisible = entryStep === 'password-gate';
  const isGuestGateVisible = entryStep === 'guest-gate';
  const isSiteVisible = entryStep === 'site';

  return (
    <Box className="site-shell" sx={{ '--bg-image': `url(${BACKGROUND_IMAGE})` }}>
      <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 }, position: 'relative', zIndex: 1 }}>
        <Element name="hero-anchor">
          <Box className={`hero-shell ${entryStep === 'locked' ? 'hero-shell--locked' : 'hero-shell--entered'}`}>
            <Typography
              variant="h2"
              className={`hero-title ${isSiteVisible ? 'hero-title--link' : ''}`}
              onClick={() => {
                if (!isSiteVisible) return;
                navigate('/home');
              }}
            >
              Jess & Joey
            </Typography>
            {selectedGuestGroup && isSiteVisible && (
              <Typography variant="body1" className="hero-guest-line">
                & {selectedGuestGroup.guests.join(', ')}
              </Typography>
            )}
            <Typography variant="h6" className="hero-date">
              August 8th, 2026
            </Typography>
            <Typography variant="body1" className="hero-subtitle">
              {daysUntil} days to go
            </Typography>
            {entryStep === 'locked' && (
              <IconButton
                aria-label="Enter site"
                className="enter-button"
                onClick={openGuestGate}
              >
                <KeyboardArrowDownRoundedIcon sx={{ fontSize: 30 }} />
              </IconButton>
            )}
          </Box>
        </Element>

        {isPasswordGateVisible && (
        <Element name="password-gate-anchor">
          <Box className="guest-gate-shell reveal-shell reveal-shell--visible guest-gate-shell--enter">
            <InfoCard title="Private Access">
              <Typography className="copy">
                Enter the site password to continue.
              </Typography>

              <TextField
                fullWidth
                label="Password"
                type="password"
                autoComplete="current-password"
                value={accessPassword}
                onChange={(event) => {
                  setAccessPassword(event.target.value);
                  setPasswordError('');
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    void submitPasswordGate();
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="Submit password"
                        onClick={() => void submitPasswordGate()}
                        edge="end"
                        disabled={!accessPassword.trim() || isCheckingPassword}
                        size="small"
                      >
                        <KeyboardReturnRoundedIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                className="guest-search-input"
                sx={{ mt: 1.5, mb: 1 }}
              />

              {!!passwordError && (
                <Typography className="guest-match-message">
                  {passwordError}
                </Typography>
              )}

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.25}
                mt={1.1}
                className="guest-continue-row"
              >
                <Button
                  variant="contained"
                  disabled={!accessPassword.trim() || isCheckingPassword}
                  onClick={() => void submitPasswordGate()}
                  className="guest-continue-button"
                >
                  {isCheckingPassword ? 'Checking...' : 'Continue'}
                </Button>
              </Stack>
            </InfoCard>
          </Box>
        </Element>
        )}

        {isGuestGateVisible && (
        <Element name="guest-gate-anchor">
          <Box className="guest-gate-shell reveal-shell reveal-shell--visible guest-gate-shell--enter">
            <InfoCard title={<WelcomeGateTitle />}>
              <Typography className="copy">
                Find your invite to continue.
              </Typography>

              <TextField
                fullWidth
                label="Type your name"
                placeholder="e.g. Joey, Jess, Jacob, Sam..."
                value={guestSearch}
                onChange={(event) => {
                  const nextValue = event.target.value;
                  const nextAlphaCount = (nextValue.match(/[a-z]/gi) || []).length;
                  setGuestSearch(event.target.value);
                  setHasSubmittedGuestSearch(nextAlphaCount >= 4);
                  setSelectedGuestGroupId('');
                  setSelectedGuestGroup(null);
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    setHasSubmittedGuestSearch(true);
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="Submit invite search (Enter)"
                        onClick={() => setHasSubmittedGuestSearch(true)}
                        edge="end"
                        disabled={!canSearchGuests}
                        size="small"
                      >
                        <KeyboardReturnRoundedIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                className="guest-search-input"
                sx={{ mt: 1.5, mb: 2 }}
              />

              {!!guestSearch.trim() && canSearchGuests && hasSubmittedGuestSearch && !isSearchingGuests && !guestSearchError && guestMatches.length === 0 && (
                <Typography className="guest-match-message">
                  Hmm... we didn't find a match. Try just your first or last name? If not results still please reach out to Jess or Joey!
                </Typography>
              )}
              {isSearchingGuests && (
                <Typography className="guest-match-message">
                  Searching invites...
                </Typography>
              )}
              {!!guestSearchError && (
                <Typography className="guest-match-message">
                  {guestSearchError}
                </Typography>
              )}

                {!!guestSearch.trim() && canSearchGuests && hasSubmittedGuestSearch && guestMatches.length === 1 && (
                <Typography className="guest-match-message">
                  We found one possible group. Please confirm it below.
                </Typography>
              )}

              {!!guestSearch.trim() && canSearchGuests && hasSubmittedGuestSearch && guestMatches.length === 2 && (
                <Typography className="guest-match-message">
                  We found multiple possible groups. Please choose yours.
                </Typography>
              )}

              {!!guestSearch.trim() && canSearchGuests && hasSubmittedGuestSearch && !isSearchingGuests && !guestSearchError && guestMatches.length > 2 && (
                <Typography className="guest-match-message">
                  You're going to have to be a little more specific than that!
                </Typography>
              )}

              <Stack
                spacing={1.25}
                className={`guest-results-stack ${
                  shouldShowGuestMatches && guestMatches.length > 1 ? 'guest-results-stack--multi' : ''
                }`}
              >
                {(shouldShowGuestMatches ? guestMatches : []).map((group) => {
                  const selected = selectedGuestGroupId === group.id;
                  return (
                    <Button
                      key={group.id}
                      variant={selected ? 'contained' : 'outlined'}
                      onClick={() => {
                        setSelectedGuestGroupId(group.id);
                        setSelectedGuestGroup(group);
                      }}
                      className={`guest-option-button ${selected ? 'guest-option-button--selected' : ''}`}
                      sx={{
                        justifyContent: 'flex-start',
                        textAlign: 'left',
                        py: 1.2,
                        px: 1.4,
                      }}
                      >
                        <Box>
                          <Typography className="guest-option-title">
                            {group.guests.join(', ')}
                          </Typography>
                        </Box>
                      </Button>
                    );
                  })}
              </Stack>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.25}
                mt={1.1}
                className="guest-continue-row"
              >
                <Button
                  variant="contained"
                  disabled={!selectedGuestGroupId}
                  onClick={continueToSite}
                  className="guest-continue-button"
                >
                  Continue to site 🎉
                </Button>
              </Stack>
            </InfoCard>
          </Box>
        </Element>
        )}

        {isSiteVisible && (
          <>
            <Element name="nav-anchor">
              <Box className="nav-shell reveal-shell reveal-shell--visible">
                <Tabs
                  value={currentPath}
                  onChange={(_, nextValue) => {
                    if (nextValue === currentPath) return;
                    navigate(nextValue);
                  }}
                  variant="scrollable"
                  scrollButtons="auto"
                  allowScrollButtonsMobile
                  className="site-tabs"
                >
                  {NAV_ITEMS.map((item) => (
                    <Tab key={item.path} label={item.label} value={item.path} />
                  ))}
                </Tabs>
              </Box>
            </Element>

            <Box className="content-shell reveal-shell reveal-shell--visible">
              <Outlet context={{
                selectedGuestGroup,
                siteVisitorName,
                rsvpResponses,
                setRsvpResponses,
                partySongRecommendation,
                setPartySongRecommendation,
                partyAdditionalNotes,
                setPartyAdditionalNotes,
                partyNeedsShuttle,
                setPartyNeedsShuttle,
                partyShuttleLocation,
                setPartyShuttleLocation,
                hasLoadedRsvp,
              }} />
            </Box>
          </>
        )}
      </Container>
    </Box>
  );
}

function App() {
  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: 'light',
          primary: { main: '#6d5a47' },
          secondary: { main: '#c8b9a5' },
          background: { default: '#f6f1eb', paper: 'rgba(255,255,255,0.9)' },
          text: { primary: '#2a2119', secondary: '#5f5246' },
        },
        shape: { borderRadius: 5 },
        typography: {
          fontFamily: '"Cormorant Garamond", Georgia, serif',
          h2: {
            fontFamily: '"Italiana", serif',
            letterSpacing: '0.06em',
            fontWeight: 700,
          },
          h5: {
            fontFamily: '"Cormorant Garamond", Georgia, serif',
            letterSpacing: '0.04em',
            fontWeight: 400,
          },
          button: {
            fontFamily: '"Cormorant Garamond", Georgia, serif',
            textTransform: 'none',
            letterSpacing: '0.06em',
            fontSize: '1rem',
          },
        },
        components: {
          MuiTab: {
            styleOverrides: {
              root: {
                minHeight: 52,
              },
            },
          },
        },
      }),
    []
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <Routes>
          <Route path="/responses" element={<ResponsesAdminPage />} />
          <Route path="/" element={<SiteLayout />}>
            <Route index element={<Navigate to="/home" replace />} />
            <Route path="home" element={<HomePage />} />
            <Route path="story" element={<StoryPage />} />
            <Route path="travel" element={<TravelAndStayPage />} />
            <Route path="accommodation" element={<Navigate to="/travel" replace />} />
            <Route path="what-to-do" element={<WhatToDoPage />} />
            <Route path="gifts-causes" element={<GiftsAndCausesPage />} />
            <Route path="faq" element={<FaqPage />} />
            <Route path="game" element={<GamePage />} />
            <Route path="rsvp" element={<RsvpPage />} />
            <Route path="*" element={<Navigate to="/home" replace />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
